import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE, AuthUser, fetchCurrentUser, logout as authLogout, pulseApi } from "../../lib/authApi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import NightlightRoundRoundedIcon from "@mui/icons-material/NightlightRoundRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import "./pulse.css";
import "./pulse-designer.css";
import { PulsePreferences, PulseRange } from "./PulseReviewControls";
import {
  PulseBookmarkDashboard,
  PulseHomeDashboard,
  PulseInsightsDashboard,
  PulseMediaKitDashboard,
  PulseScoreDashboard,
  PulseSettingsDashboard,
  TEXT_SCALE_MIN,
  TEXT_SCALE_MAX,
} from "./PulseDesignerViews";
import { IrisIcon, IrisWordmark, LifeBuoyIcon } from "./PulseIcons";
import { generateMediaKitPdf } from "./mediaKitPdf";

type Mode = "light" | "dark";
type Tab = "home" | "insights" | "score" | "media-kit" | "saved" | "settings";

const TAB_PATHS: Record<Tab, string> = {
  home: "/dashboard",
  insights: "/insights",
  score: "/pulse-score",
  "media-kit": "/media-kit",
  saved: "/bookmarks",
  settings: "/settings",
};
const PATH_TABS: Record<string, Tab> = Object.fromEntries(
  Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]),
);

type TrendPoint = {
  label: string;
  shortForm: number;
  longForm: number;
};

type TopicPoint = {
  label: string;
  count: number;
};

type InsightsMetrics = {
  header: {
    title: string;
    subtitle: string;
    growth_badge: string;
  };
  pulse_score: {
    score: number;
    delta_text: string;
    performance: string;
    formula?: string;
    score_ready?: boolean;
    raw_score?: number;
    weighted_subscores?: Record<string, number>;
    sample_days?: number;
    min_sample_days?: number;
    video_sample_count?: number;
    video_count?: number;
    min_videos?: number;
    videos_remaining?: number;
    confidence?: string;
    score_state?: string;
    state_message?: string;
    weekly_trend?: Array<{ week_start: string; week_end: string; score: number | null }>;
  };
  shorts_score?: {
    score: number;
    score_ready?: boolean;
    score_state?: string;
    weighted_subscores?: Record<string, number>;
    confidence?: string;
    sample_days?: number;
    shorts_read?: number;
    shorts_video_count?: number;
    video_count?: number;
    min_videos?: number;
    videos_remaining?: number;
    formula?: string;
    state_message?: string;
    delta_text?: string;
    score_delta?: number;
    comparison_available?: boolean;
    weekly_trend?: Array<{ week_start: string; week_end: string; score: number | null }>;
  } | null;
  engagement_rate: {
    value: number;
    delta: number;
    comparison_available?: boolean;
  };
  period_metrics?: { views: number; watch_hours: number; subscribers_net: number; sample_days: number; start_date: string; end_date: string };
  best_format: {
    label: string;
    share: number;
  };
  optimal_time: {
    label: string;
    delta: number;
  };
  performance_trends: TrendPoint[];
  content_mix: {
    short: number;
    long: number;
    carousel: number;
    total_videos?: number;
  };
  topic_window?: string;
  topic_performance: TopicPoint[];
  audience_activity: number[];
  audience_activity_detail?: Array<{
    label: string;
    score: number;
    avg_views?: number;
  }>;
};

type MediaGrowthPoint = {
  label: string;
  reach: number | null;
  engagement: number | null;
};

type MediaDemographicPoint = {
  label: string;
  value: string;
};

type MediaTopContentItem = {
  title: string;
  views: number;
  engagement_rate: number;
  url?: string;
  published_at?: string;
};

type MediaKitMetrics = {
  title: string;
  subtitle: string;
  followers: number;
  avg_monthly_reach: number;
  engagement_rate: number;
  avg_comments: number;
  quarterly_growth: MediaGrowthPoint[];
  audience_demographics: MediaDemographicPoint[];
  audience_age_distribution: MediaDemographicPoint[];
  audience_geo_distribution: MediaDemographicPoint[];
  top_content_window: "last_quarter" | "last_year" | "last_5_years";
  top_content_subtitle: string;
  top_content: MediaTopContentItem[];
  top_content_is_inspiration?: boolean;
};

type WidgetLoadState = {
  loading: boolean;
  error: string | null;
};

type PulseWidgetStatus = {
  ready: boolean;
  synthetic: boolean;
  reason?: string;
  sample_days?: number;
  min_sample_days?: number;
};

type PulseRecommendation = {
  title: string;
  summary: string;
  action: string;
  confidence: string;
  based_on: string[];
  state: string;
};

type PulseScorePlanItem = {
  measurement_phase?: string;
  id?: string;
  title: string;
  detail: string;
  status: "proposed" | "pending" | "verified" | "no_move" | "expired" | "done";
  origin?: "algorithmic" | "manual";
  saved_item_id?: string | null;
  evaluated_at?: string | null;
  score_lift: number;
  pillar?: string;
  pillar_label?: string;
  metric_key?: string;
  metric_label?: string;
  target_delta_pct?: number;
  evaluation_days?: number;
  measured_days?: number;
  baseline_value?: number | null;
  current_value?: number | null;
  result_delta_pct?: number | null;
  evidence?: string;
};

type PulseScorePlan = {
  title: string;
  subtitle: string;
  potential_score_lift: number;
  items: PulseScorePlanItem[];
};

type PulseChartPoint = {
  label: string;
  value: number;
};

type PulseChartSpec = {
  kind: "line" | "pie";
  title: string;
  subtitle?: string;
  x_label?: string;
  y_label?: string;
  source_prompt?: string;
  colors?: string[];
  points?: PulseChartPoint[];
  slices?: PulseChartPoint[];
};

type SavedIrisItem = {
  id: string;
  pulse_user_id?: string;
  channel_id?: string;
  type: "metric" | "action";
  title: string;
  body: string;
  value?: string | null;
  chart?: PulseChartSpec | null;
  source?: string | null;
  category?: string | null;
  pinned: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

type UploadedDocumentContext = {
  name: string;
  size: number;
  type: "csv" | "pdf";
  row_count?: number | null;
  column_count?: number | null;
  page_count?: number | null;
  extracted_chars: number;
  context: string;
};

type AccountState = {
  creatorName: string;
  youtubeChannel: string;
};

// Account-level Pulse identity - independent of any connected YouTube
// channel. Backed by GET/PUT /api/profile, PUT /api/profile/handle and
// POST /api/profile/avatar (pulse-backend-d1's account-profile contract).
type PulseProfile = {
  display_name: string | null;
  handle: string | null;
  bio: string | null;
  avatar_url: string | null;
};

type CreatorPreview = {
  found: boolean;
  query: string;
  channel_id?: string | null;
  platform?: string | null;
  title?: string | null;
  custom_url?: string | null;
  description?: string | null;
  thumbnail_url?: string | null;
  cover_url?: string | null;
  subscriber_count?: number | null;
  view_count?: number | null;
  video_count?: number | null;
  url?: string | null;
  published_at?: string | null;
  country?: string | null;
  oauth_connected?: boolean;
  oauth_status?: string | null;
  oauth_last_refresh_at?: string | null;
};

// Shape of one entry from GET /api/youtube/channels, per pulse-backend-d1
// (flat array, no envelope): [{channel_id, title, thumbnail_url, status,
// is_active, connected_at}]. Mapped into CreatorPreview so it can slot into
// the same `channels` state/rendering every existing view already expects.
type YoutubeChannelSummary = {
  channel_id: string;
  title?: string | null;
  thumbnail_url?: string | null;
  status?: string | null;
  is_active?: boolean;
  connected_at?: string | null;
  analytics_synced_at?: string | null;
  auth_status?: string | null;
};

// Resync polling (handleResyncChannel): POST /api/youtube/channels/:id/resync always enqueues
// a background job and returns immediately (youtube-oauth.service.ts resyncChannel) - it never
// finishes the backfill inline. analyticsSyncedAt is only stamped, and the metrics cache only
// busted, once that job actually completes, so polling for analytics_synced_at to advance past
// the resync response's own last_synced_at is the real completion signal, not the HTTP response.
const RESYNC_POLL_INTERVAL_MS = 3000;
const RESYNC_POLL_TIMEOUT_MS = 60000;

const youtubeChannelSummaryToPreview = (c: YoutubeChannelSummary): CreatorPreview => ({
  found: true,
  query: "",
  platform: "youtube",
  channel_id: c.channel_id,
  title: c.title || null,
  thumbnail_url: c.thumbnail_url || null,
  oauth_connected: c.status === "connected",
  oauth_status: c.status || null,
  published_at: c.connected_at || null,
});

type PulseYoutubeConnection = {
  ok: boolean;
  channel_id: string;
  connected: boolean;
  status: string;
  scope?: string | null;
  updated_at?: string | null;
  last_refresh_at?: string | null;
};

type PulseInstagramConnection = {
  ok: boolean;
  connected: boolean;
  status: string;
  account_id?: string | null;
  username?: string | null;
  name?: string | null;
  account_type?: string | null;
  profile_picture_url?: string | null;
  followers_count?: number | null;
  media_count?: number | null;
  scope?: string | null;
  updated_at?: string | null;
  last_refresh_at?: string | null;
  last_data_pull_at?: string | null;
};

type PulseChatMessage = {
  role: "user" | "assistant";
  text: string;
  chart?: PulseChartSpec | null;
  confidence?: string | null;
  surface?: string | null;
  evidence?: Array<Record<string, any>>;
  unknowns?: string[];
  insight?: PulseTypedInsight | null;
};

type PulseTypedInsight = {
  id: string;
  type: "notable_trend" | "missed_opportunity" | "watch_out" | "new_value";
  type_label: string;
  title: string;
  body: string;
  confidence: string;
  surface: string;
  evidence: string;
  action_prompt: string;
};

type PulseCreatorMemory = {
  channel_summary?: string;
  latest_conversation_summary?: string;
  recommendations_given?: string[];
  open_unknowns?: string[];
  user_preferences?: string[];
  topics?: Array<{ topic: string; summary: string; updated_at?: string }>;
  updated_at?: string;
};

const normaliseMarkdownInput = (input: string): string =>
  input
    .replace(/\r\n?/g, "\n")
    .replace(/\n[ \t]*<!--\s*md-gap\s*-->[ \t]*\n/gi, "\n\n")
    .replace(/\n{3,}/g, "\n\n");

const renderPulseMarkdown = (rawText: string): React.ReactNode => {
  const text = normaliseMarkdownInput(rawText);
  const flattenText = (node: React.ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map((item) => flattenText(item)).join("");
    if (React.isValidElement(node)) return flattenText((node.props as { children?: React.ReactNode })?.children);
    return "";
  };
  const toPlain = (children: React.ReactNode): string => flattenText(children).trim();
  const classifyConfidenceLine = (raw: string): "confidence" | "rubric" | "up" | "down" | null => {
    const lower = raw.toLowerCase();
    if (lower.startsWith("confidence:")) return "confidence";
    if (lower.startsWith("rubric:")) return "rubric";
    if (lower.startsWith("moves confidence up:")) return "up";
    if (lower.startsWith("moves confidence down:")) return "down";
    return null;
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => {
          const raw = toPlain(children);
          const kind = classifyConfidenceLine(raw);
          if (kind === "confidence") {
            return <div className="pulse-md-chip pulse-md-chip-confidence">{children}</div>;
          }
          if (kind === "rubric") {
            return <div className="pulse-md-chip pulse-md-chip-rubric">{children}</div>;
          }
          if (kind === "up") {
            return <div className="pulse-md-chip pulse-md-chip-up">{children}</div>;
          }
          if (kind === "down") {
            return <div className="pulse-md-chip pulse-md-chip-down">{children}</div>;
          }
          return <p style={{ margin: "0.42rem 0" }}>{children}</p>;
        },
        ul: ({ children }) => (
          <ul style={{ margin: "0.35rem 0", paddingLeft: "1.2rem", listStyleType: "disc" }}>{children}</ul>
        ),
        ol: ({ children, start }) => (
          <ol
            start={typeof start === "number" ? start : undefined}
            style={{ margin: "0.35rem 0", paddingLeft: "1.2rem", listStyleType: "decimal" }}
          >
            {children}
          </ol>
        ),
        li: ({ children }) => {
          const kind = classifyConfidenceLine(toPlain(children));
          if (kind === "confidence") {
            return (
              <li className="pulse-md-chip-li">
                <div className="pulse-md-chip pulse-md-chip-confidence">{children}</div>
              </li>
            );
          }
          if (kind === "rubric") {
            return (
              <li className="pulse-md-chip-li">
                <div className="pulse-md-chip pulse-md-chip-rubric">{children}</div>
              </li>
            );
          }
          if (kind === "up") {
            return (
              <li className="pulse-md-chip-li">
                <div className="pulse-md-chip pulse-md-chip-up">{children}</div>
              </li>
            );
          }
          if (kind === "down") {
            return (
              <li className="pulse-md-chip-li">
                <div className="pulse-md-chip pulse-md-chip-down">{children}</div>
              </li>
            );
          }
          return <li style={{ margin: "0.15rem 0" }}>{children}</li>;
        },
        table: ({ children }) => (
          <div
            style={{
              marginTop: "0.5rem",
              overflowX: "auto",
              border: "1px solid rgba(17, 24, 39, 0.1)",
              borderRadius: 10,
              background: "rgba(255,255,255,0.4)",
            }}
          >
            <table style={{ width: "100%", minWidth: 420, borderCollapse: "collapse" }}>{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th
            style={{
              textAlign: "left",
              fontWeight: 700,
              fontSize: 13,
              padding: "8px 10px",
              borderBottom: "1px solid rgba(17, 24, 39, 0.14)",
            }}
          >
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td
            style={{
              fontSize: 13,
              padding: "8px 10px",
              borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
              verticalAlign: "top",
            }}
          >
            {children}
          </td>
        ),
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noreferrer" style={{ color: "#5f3cf7", textDecoration: "underline" }}>
            {children}
          </a>
        ),
        code: ({ children }) => (
          <code
            style={{
              background: "rgba(17,24,39,0.08)",
              padding: "1px 4px",
              borderRadius: 6,
              fontSize: "0.92em",
            }}
          >
            {children}
          </code>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

// API_BASE and pulseApi (JWT-authenticated, auto-refreshing) now live in
// src/lib/authApi.ts, shared with the Login page. pulse_user_id (the old
// anonymous session identity) is gone - the backend now identifies the user
// from the Authorization: Bearer token, so pulseUserId below is populated
// from GET /api/auth/me's `id` field instead and is used purely as a
// localStorage-scoping key (unchanged usage), never sent in request bodies.
let pulseSessionBootstrapPromise: Promise<{ pulseUserId: string; authUser: AuthUser }> | null = null;

const TREND_DATA: TrendPoint[] = [
  { label: "Mar 15", shortForm: 8.2, longForm: 4.1 },
  { label: "Mar 22", shortForm: 9.1, longForm: 3.8 },
  { label: "Mar 29", shortForm: 7.8, longForm: 5.1 },
  { label: "Apr 5", shortForm: 10.3, longForm: 4.9 },
  { label: "Apr 12", shortForm: 9.8, longForm: 5.6 },
  { label: "Apr 19", shortForm: 11.2, longForm: 6.1 },
];

const MEDIA_GROWTH: MediaGrowthPoint[] = [
  { label: "Jan", reach: 45000, engagement: 3200 },
  { label: "Feb", reach: 51000, engagement: 3500 },
  { label: "Mar", reach: 61000, engagement: 4700 },
  { label: "Apr", reach: 82000, engagement: 5600 },
];

const DEMO_INSIGHTS_METRICS: InsightsMetrics = {
  header: {
    title: "Performance Overview",
    subtitle: "Last 30 days - Updated 2 hours ago",
    growth_badge: "+23% vs last month",
  },
  pulse_score: {
    score: 8.2,
    delta_text: "+12% from last period",
    performance: "Excellent",
    formula: "Pulse Score = average of 4 sub-scores with trailing 28-day weighting (40/30/20/10).",
  },
  engagement_rate: {
    value: 7.8,
    delta: 0.8,
  },
  best_format: {
    label: "Short-form",
    share: 58,
  },
  optimal_time: {
    label: "6 PM EST",
    delta: 43,
  },
  performance_trends: TREND_DATA,
  content_mix: {
    short: 58,
    long: 28,
    carousel: 14,
    total_videos: 42,
  },
  topic_window: "Last 30 days",
  topic_performance: [
    { label: "Productivity", count: 12 },
    { label: "Creator AI", count: 9 },
    { label: "Growth Experiments", count: 8 },
  ],
  audience_activity: [42, 58, 73, 65, 90, 88, 76],
  audience_activity_detail: [
    { label: "Mon", score: 42, avg_views: 210 },
    { label: "Tue", score: 58, avg_views: 260 },
    { label: "Wed", score: 73, avg_views: 330 },
    { label: "Thu", score: 65, avg_views: 295 },
    { label: "Fri", score: 90, avg_views: 420 },
    { label: "Sat", score: 88, avg_views: 410 },
    { label: "Sun", score: 76, avg_views: 350 },
  ],
};

const DEMO_MEDIA_KIT_METRICS: MediaKitMetrics = {
  title: "Creator Media Kit",
  subtitle: "Q1 2026 Performance Report",
  followers: 124000,
  avg_monthly_reach: 2300000,
  engagement_rate: 8.4,
  avg_comments: 342,
  quarterly_growth: MEDIA_GROWTH,
  audience_demographics: [
    { label: "18-24 - Tech learners", value: "34%" },
    { label: "25-34 - Creator founders", value: "41%" },
    { label: "35-44 - Operators", value: "25%" },
  ],
  audience_age_distribution: [
    { label: "Age 18-24", value: "34%" },
    { label: "Age 25-34", value: "41%" },
    { label: "Age 35-44", value: "25%" },
  ],
  audience_geo_distribution: [
    { label: "United States", value: "54%" },
    { label: "United Kingdom", value: "18%" },
    { label: "Canada", value: "12%" },
    { label: "Other", value: "16%" },
  ],
  top_content_window: "last_quarter",
  top_content_subtitle: "Example videos for a sponsor-ready media kit",
  top_content_is_inspiration: true,
  top_content: [
    { title: "5 Productivity Apps That Changed My Life", views: 428000, engagement_rate: 9.2 },
    { title: "My Remote Work Setup Tour", views: 386000, engagement_rate: 8.8 },
    { title: "Time Management Tips for Entrepreneurs", views: 352000, engagement_rate: 7.9 },
  ],
};

const DEMO_IRIS_RECOMMENDATION: PulseRecommendation = {
  title: "IRIS Recommendation",
  summary: "Connect a creator account to receive analytics-backed weekly recommendations.",
  action: "Set creator profile and connect YouTube OAuth to unlock recommendation quality.",
  confidence: "medium",
  based_on: ["Demo mode"],
  state: "demo",
};

const DEMO_SCORE_PLAN: PulseScorePlan = {
  title: "How to improve your Pulse score?",
  subtitle: "Complete the following action items to boost your Pulse Score. This refreshes every 6 hours.",
  potential_score_lift: 1.05,
  items: [
    { title: "Publish one long-form video in your best slot", detail: "Target your strongest measured audience window this week.", status: "proposed", score_lift: 0.35 },
    { title: "Strengthen the opening of your next upload", detail: "Test a clearer first-minute promise and watch average view percentage.", status: "proposed", score_lift: 0.25 },
    { title: "Ship one title and thumbnail iteration", detail: "Run one packaging test and compare the measured result.", status: "proposed", score_lift: 0.2 },
    { title: "Maintain weekly posting consistency", detail: "Hit your cadence target to stabilize Publishing Discipline.", status: "proposed", score_lift: 0.25 },
  ],
};

const PULSE_METRIC_WIDGETS = [
  "header",
  "pulse_score",
  "engagement_rate",
  "best_format",
  "optimal_time",
  "performance_trends",
  "content_mix",
  "topic_performance",
  "audience_activity",
  "media_kit",
] as const;

type PulseMetricWidget = (typeof PULSE_METRIC_WIDGETS)[number];

const SUGGESTIONS = [
  "Why is my Execution Craft score low?",
  "When should I post this week?",
  "What's trending in my niche?",
];

const ACCOUNT_STORAGE_KEY = "pulse_account_settings";
const ACCOUNT_PREVIEW_STORAGE_KEY = "pulse_account_preview";
const MODE_STORAGE_KEY = "pulse_mode";
const CHAT_STORAGE_KEY = "pulse_chat_history";
const ONBOARDING_STORAGE_KEY = "pulse_onboarding_step";
const USER_STORAGE_KEY = "pulse_user_id";
const GOALS_STORAGE_KEY = "pulse_setup_goals";
const MESSAGE_PREFS_STORAGE_KEY = "pulse_message_prefs";
const PRIVACY_PREFS_STORAGE_KEY = "pulse_privacy_prefs";
const IRIS_MEMORY_STORAGE_KEY = "pulse_iris_memory_enabled";
const IRIS_PANEL_WIDTH_STORAGE_KEY = "pulse_iris_panel_width";
const IRIS_PANEL_WIDTH_DEFAULT = 432;
const IRIS_PANEL_WIDTH_MIN = 360;
const IRIS_PANEL_WIDTH_MAX = 760;
const DEFAULT_ASSISTANT_TEXT =
  "Hi! I'm IRIS, your content intelligence assistant. Ask me anything about your performance, audience, or content strategy.";

type OnboardingStep = "welcome" | "connect" | "goals" | "reading" | "done";
type PulseGoalId =
  | "subscribers"
  | "watch_time"
  | "brand_partnerships"
  | "community"
  | "content_series"
  | "consistency"
  | "adsense"
  | "short_form";

const scopedPulseKey = (baseKey: string, pulseUserId: string) => {
  const clean = String(pulseUserId || "").trim();
  return clean ? `${baseKey}:${clean}` : baseKey;
};

const readPulseUserId = (): string => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(USER_STORAGE_KEY) || "";
};

const savePulseUserId = (pulseUserId: string) => {
  if (typeof window === "undefined") return;
  if (pulseUserId) {
    window.localStorage.setItem(USER_STORAGE_KEY, pulseUserId);
  } else {
    window.localStorage.removeItem(USER_STORAGE_KEY);
  }
};

const readAccount = (pulseUserId: string = readPulseUserId()): AccountState => {
  if (typeof window === "undefined") return { creatorName: "", youtubeChannel: "" };
  if (!pulseUserId) return { creatorName: "", youtubeChannel: "" };
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(ACCOUNT_STORAGE_KEY, pulseUserId));
    if (!raw) return { creatorName: "", youtubeChannel: "" };
    const parsed = JSON.parse(raw);
    return {
      creatorName: String(parsed?.creatorName || ""),
      youtubeChannel: String(parsed?.youtubeChannel || ""),
    };
  } catch {
    return { creatorName: "", youtubeChannel: "" };
  }
};

const saveAccount = (account: AccountState, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined") return;
  if (!pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(ACCOUNT_STORAGE_KEY, pulseUserId), JSON.stringify(account));
};

const readAccountPreview = (pulseUserId: string = readPulseUserId()): CreatorPreview | null => {
  if (typeof window === "undefined") return null;
  if (!pulseUserId) return null;
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(ACCOUNT_PREVIEW_STORAGE_KEY, pulseUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as CreatorPreview;
  } catch {
    return null;
  }
};

const saveAccountPreview = (preview: CreatorPreview | null, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined") return;
  if (!pulseUserId) return;
  const key = scopedPulseKey(ACCOUNT_PREVIEW_STORAGE_KEY, pulseUserId);
  if (!preview) {
    window.localStorage.removeItem(key);
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(preview));
};

// Multi-channel cache. Falls back to the legacy single-preview key so
// accounts that connected a channel before multi-channel support was added
// don't lose their locally-cached channel on first load after the update
// (the server is still the source of truth - see bootstrapPulseSession).
const CHANNELS_STORAGE_KEY = "pulse_channels";
const ACTIVE_CHANNEL_STORAGE_KEY = "pulse_active_channel";

const readChannels = (pulseUserId: string = readPulseUserId()): CreatorPreview[] => {
  if (typeof window === "undefined" || !pulseUserId) return [];
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(CHANNELS_STORAGE_KEY, pulseUserId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((c) => c && typeof c === "object" && c.channel_id);
    }
  } catch {
    // fall through to legacy single-channel cache
  }
  const legacy = readAccountPreview(pulseUserId);
  return legacy?.channel_id ? [legacy] : [];
};

const saveChannels = (channels: CreatorPreview[], pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(CHANNELS_STORAGE_KEY, pulseUserId), JSON.stringify(channels));
};

const readActiveChannelId = (pulseUserId: string = readPulseUserId()): string => {
  if (typeof window === "undefined" || !pulseUserId) return "";
  const stored = window.localStorage.getItem(scopedPulseKey(ACTIVE_CHANNEL_STORAGE_KEY, pulseUserId));
  if (stored) return stored;
  return readAccountPreview(pulseUserId)?.channel_id || "";
};

const saveActiveChannelId = (channelId: string, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(ACTIVE_CHANNEL_STORAGE_KEY, pulseUserId), channelId);
};

const readMode = (): Mode => {
  if (typeof window === "undefined") return "light";
  const saved = String(window.localStorage.getItem(MODE_STORAGE_KEY) || "").trim().toLowerCase();
  return saved === "dark" ? "dark" : "light";
};

const saveMode = (mode: Mode) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MODE_STORAGE_KEY, mode);
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = "pulse_sidebar_collapsed";

const readSidebarCollapsed = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
};

const saveSidebarCollapsed = (collapsed: boolean) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
};

const readIrisPanelWidth = () => {
  if (typeof window === "undefined") return IRIS_PANEL_WIDTH_DEFAULT;
  const value = Number(window.localStorage.getItem(IRIS_PANEL_WIDTH_STORAGE_KEY));
  if (!Number.isFinite(value)) return IRIS_PANEL_WIDTH_DEFAULT;
  return Math.min(IRIS_PANEL_WIDTH_MAX, Math.max(IRIS_PANEL_WIDTH_MIN, value));
};

const saveIrisPanelWidth = (value: number) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(IRIS_PANEL_WIDTH_STORAGE_KEY, String(Math.round(value)));
};

const readChatHistory = (pulseUserId: string = readPulseUserId()): PulseChatMessage[] => {
  if (typeof window === "undefined") return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
  if (!pulseUserId) return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(CHAT_STORAGE_KEY, pulseUserId));
    if (!raw) return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
    const normalized: PulseChatMessage[] = parsed
      .map((item): PulseChatMessage => ({
        role: item?.role === "user" ? "user" : "assistant",
        text: String(item?.text || "").trim(),
        chart: normalizePulseChartSpec(item?.chart),
        confidence: item?.confidence == null ? null : String(item.confidence),
        surface: item?.surface == null ? null : String(item.surface),
        evidence: Array.isArray(item?.evidence) ? item.evidence.slice(0, 8) : [],
        unknowns: Array.isArray(item?.unknowns) ? item.unknowns.map((value: any) => String(value)).slice(0, 8) : [],
      }))
      .filter((item) => item.text.length > 0)
      .slice(-10);
    while (normalized.length && normalized[normalized.length - 1].role === "user") {
      normalized.pop();
    }
    if (!normalized.length) return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
    return normalized;
  } catch {
    return [{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }];
  }
};

const saveChatHistory = (messages: PulseChatMessage[], pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined") return;
  if (!pulseUserId) return;
  const cleaned = (messages || [])
    .map((m) => ({
      role: m.role,
      text: String(m.text || "").trim(),
      chart: m.chart || null,
      confidence: m.confidence || null,
      surface: m.surface || null,
      evidence: m.evidence || [],
      unknowns: m.unknowns || [],
    }))
    .filter((m) => m.text.length > 0)
    .slice(-10);
  window.localStorage.setItem(scopedPulseKey(CHAT_STORAGE_KEY, pulseUserId), JSON.stringify(cleaned));
};

const readSetupGoals = (pulseUserId: string = readPulseUserId()): PulseGoalId[] => {
  if (typeof window === "undefined" || !pulseUserId) return [];
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(GOALS_STORAGE_KEY, pulseUserId));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => String(item || "").trim())
      .filter((item): item is PulseGoalId =>
        [
          "subscribers",
          "watch_time",
          "brand_partnerships",
          "community",
          "content_series",
          "consistency",
          "adsense",
          "short_form",
        ].includes(item),
      )
      .slice(0, 5);
  } catch {
    return [];
  }
};

const saveSetupGoals = (goals: PulseGoalId[], pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(GOALS_STORAGE_KEY, pulseUserId), JSON.stringify(goals.slice(0, 5)));
};

// Client-only preferences below (message preferences, privacy controls, IRIS
// memory toggle) - no backend fields exist for these yet (flagged to
// pulse-backend-00). They persist per-device like theme/sidebar-collapsed
// rather than syncing across devices until that lands.
export type PulseMessagePrefs = {
  weekly_digest: boolean;
  push_recommendations: boolean;
  sponsor_alerts: boolean;
  product_updates: boolean;
};
const DEFAULT_MESSAGE_PREFS: PulseMessagePrefs = {
  weekly_digest: true,
  push_recommendations: true,
  sponsor_alerts: true,
  product_updates: false,
};

export type PulsePrivacyPrefs = {
  personalize_insights: boolean;
  share_benchmarks: boolean;
  iris_reference_saved: boolean;
};
const DEFAULT_PRIVACY_PREFS: PulsePrivacyPrefs = {
  personalize_insights: true,
  share_benchmarks: false,
  iris_reference_saved: true,
};

const readMessagePrefs = (pulseUserId: string = readPulseUserId()): PulseMessagePrefs => {
  if (typeof window === "undefined" || !pulseUserId) return DEFAULT_MESSAGE_PREFS;
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(MESSAGE_PREFS_STORAGE_KEY, pulseUserId));
    return raw ? { ...DEFAULT_MESSAGE_PREFS, ...JSON.parse(raw) } : DEFAULT_MESSAGE_PREFS;
  } catch {
    return DEFAULT_MESSAGE_PREFS;
  }
};

const saveMessagePrefs = (prefs: PulseMessagePrefs, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(MESSAGE_PREFS_STORAGE_KEY, pulseUserId), JSON.stringify(prefs));
};

const readPrivacyPrefs = (pulseUserId: string = readPulseUserId()): PulsePrivacyPrefs => {
  if (typeof window === "undefined" || !pulseUserId) return DEFAULT_PRIVACY_PREFS;
  try {
    const raw = window.localStorage.getItem(scopedPulseKey(PRIVACY_PREFS_STORAGE_KEY, pulseUserId));
    return raw ? { ...DEFAULT_PRIVACY_PREFS, ...JSON.parse(raw) } : DEFAULT_PRIVACY_PREFS;
  } catch {
    return DEFAULT_PRIVACY_PREFS;
  }
};

const savePrivacyPrefs = (prefs: PulsePrivacyPrefs, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(PRIVACY_PREFS_STORAGE_KEY, pulseUserId), JSON.stringify(prefs));
};

const readIrisMemoryEnabled = (pulseUserId: string = readPulseUserId()): boolean => {
  if (typeof window === "undefined" || !pulseUserId) return true;
  return window.localStorage.getItem(scopedPulseKey(IRIS_MEMORY_STORAGE_KEY, pulseUserId)) !== "false";
};

const saveIrisMemoryEnabled = (enabled: boolean, pulseUserId: string = readPulseUserId()) => {
  if (typeof window === "undefined" || !pulseUserId) return;
  window.localStorage.setItem(scopedPulseKey(IRIS_MEMORY_STORAGE_KEY, pulseUserId), String(enabled));
};

const readOnboardingStep = (): OnboardingStep => {
  if (typeof window === "undefined") return "welcome";
  const saved = String(window.localStorage.getItem(ONBOARDING_STORAGE_KEY) || "").trim();
  return saved === "connect" || saved === "goals" || saved === "done" ? saved : "welcome";
};

const saveOnboardingStep = (step: OnboardingStep) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ONBOARDING_STORAGE_KEY, step);
};

const PulseOnboardingProgress: React.FC<{ step: OnboardingStep }> = ({ step }) => (
  <div
    className="pulse-onboarding-progress"
    aria-label={`Onboarding step ${step === "welcome" ? 1 : step === "connect" ? 2 : 3} of 3`}
  >
    <span className={step === "welcome" ? "active" : ""} />
    <span className={step === "connect" ? "active" : ""} />
    <span className={step === "goals" ? "active" : ""} />
  </div>
);

const PulseWelcomeOnboarding: React.FC<{ onGetStarted: () => void; error?: string | null }> = ({ onGetStarted, error }) => (
  <section className="pulse-onboarding-shell">
    <div className="pulse-onboarding-chrome" />
    <main className="pulse-onboarding-center pulse-onboarding-welcome">
      <div className="pulse-onboarding-wordmark">
        <img src="/pulse-icon.png" alt="" aria-hidden="true" className="pulse-v2-brand-mark" />
        Pulse
      </div>
      <h1>Welcome to Pulse</h1>
      <p>
        Your creator intelligence co-pilot. IRIS
        <br />
        analyzes your content, tracks what works,
        <br />
        and tells you exactly what to do next.
      </p>
      <button type="button" className="pulse-onboarding-primary" onClick={onGetStarted}>
        Get started <span aria-hidden="true">&rarr;</span>
      </button>
      {error ? <p className="pulse-onboarding-error">{error}</p> : null}
    </main>
    <PulseOnboardingProgress step="welcome" />
  </section>
);

const PulseConnectOnboarding: React.FC<{
  oauthConnected: boolean;
  oauthBusy: boolean;
  oauthError: string | null;
  instagramConnected: boolean;
  instagramBusy: boolean;
  instagramError: string | null;
  onConnect: () => void;
  onConnectInstagram: () => void;
  onContinue: () => void;
}> = ({
  oauthConnected,
  oauthBusy,
  oauthError,
  instagramConnected,
  instagramBusy,
  instagramError,
  onConnect,
  onConnectInstagram,
  onContinue,
}) => (
  <section className="pulse-onboarding-shell">
    <div className="pulse-onboarding-chrome" />
    <main className="pulse-onboarding-center pulse-onboarding-connect">
      <h1>Connect your channels</h1>
      <p>
        IRIS needs read-only access to your analytics to
        <br />
        build your Pulse Score and generate personalized
        <br />
        recommendations.
      </p>

      <div className="pulse-channel-list">
        <article className={`pulse-channel-card active ${oauthConnected ? "connected" : ""}`}>
          <div className="pulse-channel-icon youtube" aria-hidden="true">
            <span />
          </div>
          <div className="pulse-channel-copy">
            <strong>YouTube Analytics</strong>
            <span>{oauthConnected ? "Connected with read-only access" : <>Read-only access &middot; No posting permissions</>}</span>
          </div>
          {oauthConnected ? (
            <div className="pulse-channel-connected" aria-label="YouTube Analytics connected">
              <CheckRoundedIcon fontSize="small" />
              <span>Connected</span>
            </div>
          ) : (
            <button type="button" className="pulse-channel-connect" onClick={onConnect} disabled={oauthBusy}>
              {oauthBusy ? "Opening..." : "Connect"}
            </button>
          )}
        </article>

        <article className={`pulse-channel-card active instagram ${instagramConnected ? "connected" : ""}`}>
          <div className="pulse-channel-icon instagram" aria-hidden="true"><InstagramIcon /></div>
          <div className="pulse-channel-copy">
            <strong>Instagram</strong>
            <span>{instagramConnected ? "Connected with read-only access" : "Professional account analytics"}</span>
          </div>
          {instagramConnected ? (
            <div className="pulse-channel-connected" aria-label="Instagram connected">
              <CheckRoundedIcon fontSize="small" />
              <span>Connected</span>
            </div>
          ) : (
            <button type="button" className="pulse-channel-connect" onClick={onConnectInstagram} disabled={instagramBusy}>
              {instagramBusy ? "Opening..." : "Connect"}
            </button>
          )}
        </article>

        <article className="pulse-channel-card disabled">
          <div className="pulse-channel-icon muted" aria-hidden="true">TI</div>
          <div className="pulse-channel-copy">
            <strong>TikTok</strong>
            <span>Video & audience analytics</span>
          </div>
          <span className="pulse-channel-soon">Coming soon</span>
        </article>
      </div>

      {oauthError ? <div className="pulse-onboarding-error">{oauthError}</div> : null}
      {instagramError ? <div className="pulse-onboarding-error">{instagramError}</div> : null}

      <button
        type="button"
        className="pulse-onboarding-primary pulse-onboarding-continue"
        onClick={onContinue}
        disabled={!oauthConnected}
      >
        Continue <span aria-hidden="true">&rarr;</span>
      </button>
    </main>
    <PulseOnboardingProgress step="connect" />
  </section>
);

const PULSE_GOALS: Array<{
  id: PulseGoalId;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: "subscribers", label: "Grow subscribers faster", icon: <PeopleOutlineRoundedIcon fontSize="small" /> },
  { id: "watch_time", label: "Increase watch time", icon: <AccessTimeOutlinedIcon fontSize="small" /> },
  { id: "brand_partnerships", label: "Land brand partnerships", icon: <HandshakeOutlinedIcon fontSize="small" /> },
  { id: "community", label: "Build community engagement", icon: <ForumOutlinedIcon fontSize="small" /> },
  { id: "content_series", label: "Launch a content series", icon: <GridViewOutlinedIcon fontSize="small" /> },
  { id: "consistency", label: "Post more consistently", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { id: "adsense", label: "Monetize with AdSense", icon: <AttachMoneyOutlinedIcon fontSize="small" /> },
  { id: "short_form", label: "Master short-form content", icon: <BoltRoundedIcon fontSize="small" /> },
];

const PulseGoalsOnboarding: React.FC<{
  selectedGoals: PulseGoalId[];
  onToggleGoal: (goalId: PulseGoalId) => void;
  onContinue: () => void;
}> = ({ selectedGoals, onToggleGoal, onContinue }) => {
  const remaining = Math.max(0, 2 - selectedGoals.length);
  const canContinue = remaining === 0;
  return (
    <section className="pulse-onboarding-shell">
      <div className="pulse-onboarding-chrome" />
      <main className="pulse-onboarding-center pulse-onboarding-goals">
        <div className="pulse-goals-kicker">
          <span aria-hidden="true">
            <AutoAwesomeRoundedIcon fontSize="inherit" />
          </span>
          IRIS SETUP
        </div>
        <h1>What are you working toward?</h1>
        <p>
          Choose 2&ndash;5 goals. IRIS will orient your Pulse Score and
          <br />
          recommendations around them from day one.
        </p>

        <div className="pulse-goal-grid">
          {PULSE_GOALS.map((goal) => {
            const selected = selectedGoals.includes(goal.id);
            return (
              <button
                key={goal.id}
                type="button"
                className={`pulse-goal-option ${selected ? "selected" : ""}`}
                onClick={() => onToggleGoal(goal.id)}
              >
                <span className="pulse-goal-icon">{goal.icon}</span>
                <span>{goal.label}</span>
                {selected ? <CheckRoundedIcon className="pulse-goal-check" fontSize="small" /> : null}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="pulse-onboarding-primary pulse-onboarding-continue pulse-goals-continue"
          onClick={onContinue}
          disabled={!canContinue}
        >
          {canContinue ? (
            <>
              Continue <span aria-hidden="true">&rarr;</span>
            </>
          ) : (
            `Select ${remaining} more to continue`
          )}
        </button>
      </main>
      <PulseOnboardingProgress step="goals" />
    </section>
  );
};

const PULSE_READING_STEPS = [
  "Scanning your video library",
  "Analyzing audience patterns",
  "Mapping content performance",
  "Building your Pulse Score",
  "Personalizing IRIS recommendations",
];

const PulseReadingOnboarding: React.FC<{ activeStep: number }> = ({ activeStep }) => (
  <section className="pulse-onboarding-shell">
    <div className="pulse-onboarding-chrome" />
    <main className="pulse-onboarding-center pulse-onboarding-reading">
      <div className="pulse-reading-logo">
        <AutoAwesomeRoundedIcon fontSize="large" />
      </div>
      <h1>IRIS is reading your channel</h1>
      <p>Building your first Pulse Score...</p>

      <div className="pulse-reading-list">
        {PULSE_READING_STEPS.map((label, idx) => {
          const status = idx < activeStep ? "done" : idx === activeStep ? "active" : "pending";
          return (
            <div key={label} className={`pulse-reading-step ${status}`}>
              <span className="pulse-reading-dot">{idx < activeStep ? <CheckRoundedIcon fontSize="inherit" /> : null}</span>
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      <div className="pulse-reading-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, Math.max(8, (activeStep / PULSE_READING_STEPS.length) * 100))}%` }} />
      </div>
    </main>
  </section>
);

const ChartTooltip: React.FC<{
  point: TrendPoint;
  left: number;
}> = ({ point, left }) => (
  <div className="pulse-tooltip" style={{ left }}>
    <div className="pulse-tooltip-date">{point.label.replace("Apr ", "Apr ")}</div>
    <div className="pulse-tooltip-short">shortForm : {point.shortForm.toFixed(1)}</div>
    <div className="pulse-tooltip-long">longForm : {point.longForm.toFixed(1)}</div>
  </div>
);

const TrendChart: React.FC<{ mode: Mode; points: TrendPoint[]; loading?: boolean }> = ({ mode, points, loading = false }) => {
  const dataPoints = points.length >= 2 ? points : TREND_DATA;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const width = 860;
  const height = 300;
  const leftPad = 48;
  const rightPad = 18;
  const topPad = 16;
  const bottomPad = 34;
  const maxY = 12;
  const minY = 0;
  const graphWidth = width - leftPad - rightPad;
  const graphHeight = height - topPad - bottomPad;

  const xAt = (i: number) => leftPad + (i / (dataPoints.length - 1)) * graphWidth;
  const yAt = (value: number) => topPad + ((maxY - value) / (maxY - minY)) * graphHeight;

  const buildSmoothPath = (points: Array<{ x: number; y: number }>) => {
    if (!points.length) return "";
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i += 1) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return d;
  };

  const shortPoints = dataPoints.map((point, i) => ({ x: xAt(i), y: yAt(point.shortForm) }));
  const longPoints = dataPoints.map((point, i) => ({ x: xAt(i), y: yAt(point.longForm) }));
  const shortPath = buildSmoothPath(shortPoints);
  const longPath = buildSmoothPath(longPoints);
  const shortAreaPath =
    `${shortPath} L ${xAt(dataPoints.length - 1)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`;
  const longAreaPath =
    `${longPath} L ${xAt(dataPoints.length - 1)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`;

  const tooltipPoint = hoveredIndex !== null ? dataPoints[hoveredIndex] : null;
  const tooltipLeft = hoveredIndex !== null ? xAt(hoveredIndex) + 12 : 0;

  return (
    <div className={`pulse-chart-wrap ${loading ? "loading" : "loaded"}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="pulse-chart-svg"
        role="img"
        aria-label="Performance trends"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <defs>
          <linearGradient id={`shortArea-${mode}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={mode === "dark" ? "#8E6CFF" : "#7A51FF"} stopOpacity="0.38" />
            <stop offset="100%" stopColor={mode === "dark" ? "#8E6CFF" : "#7A51FF"} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id={`longArea-${mode}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F041A2" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#F041A2" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {[0, 3, 6, 9, 12].map((v) => (
          <g key={v}>
            <line x1={leftPad} y1={yAt(v)} x2={width - rightPad} y2={yAt(v)} className="pulse-grid-line" />
            <text x={leftPad - 18} y={yAt(v) + 4} className="pulse-axis-text">
              {v}
            </text>
          </g>
        ))}

        {dataPoints.map((point, i) => (
          <line
            key={point.label}
            x1={xAt(i)}
            y1={topPad}
            x2={xAt(i)}
            y2={height - bottomPad}
            className="pulse-grid-line-vertical"
          />
        ))}

        <path d={shortAreaPath} className="pulse-area-short" fill={`url(#shortArea-${mode})`} />
        <path d={longAreaPath} className="pulse-area-long" fill={`url(#longArea-${mode})`} />
        <path d={shortPath} className="pulse-line-short" />
        <path d={longPath} className="pulse-line-long" />

        {hoveredIndex !== null && (
          <line x1={xAt(hoveredIndex)} y1={topPad} x2={xAt(hoveredIndex)} y2={height - bottomPad} className="pulse-hover-line" />
        )}

        {dataPoints.map((point, i) => (
          <g key={`${point.label}-hotspot`}>
            <circle
              cx={xAt(i)}
              cy={yAt(point.shortForm)}
              r={5}
              className="pulse-hover-dot-short"
              style={{ opacity: hoveredIndex === i ? 1 : 0 }}
            />
            <circle
              cx={xAt(i)}
              cy={yAt(point.longForm)}
              r={5}
              className="pulse-hover-dot-long"
              style={{ opacity: hoveredIndex === i ? 1 : 0 }}
            />
            <rect
              x={xAt(i) - graphWidth / dataPoints.length / 2}
              y={topPad}
              width={graphWidth / dataPoints.length}
              height={graphHeight}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(i)}
            />
          </g>
        ))}

        {dataPoints.map((point, i) => (
          <text key={`${point.label}-x`} x={xAt(i)} y={height - 14} textAnchor="middle" className="pulse-axis-text">
            {point.label}
          </text>
        ))}
      </svg>
      {tooltipPoint && <ChartTooltip point={tooltipPoint} left={tooltipLeft} />}
    </div>
  );
};

const LegacyInsightsView: React.FC<{
  mode: Mode;
  metrics: InsightsMetrics;
  widgetStatus: Partial<Record<PulseMetricWidget, PulseWidgetStatus>>;
  scorePlan: PulseScorePlan | null;
  scorePlanLoading: boolean;
  scorePlanError?: string | null;
  recommendation: PulseRecommendation | null;
  recommendationLoading: boolean;
  recommendationError?: string | null;
  trendsLoading: boolean;
  mixLoading: boolean;
  noticeMessage?: string | null;
}> = ({
  mode,
  metrics,
  widgetStatus,
  scorePlan,
  scorePlanLoading,
  scorePlanError,
  recommendation,
  recommendationLoading,
  recommendationError,
  trendsLoading,
  mixLoading,
  noticeMessage,
}) => {
  const topicStatus = widgetStatus.topic_performance;
  const audienceStatus = widgetStatus.audience_activity;
  const bestFormatStatus = widgetStatus.best_format;
  const optimalTimeStatus = widgetStatus.optimal_time;
  const mixStatus = widgetStatus.content_mix;

  const hideTopic = topicStatus?.synthetic === true || topicStatus?.ready === false;
  const hideAudience = audienceStatus?.synthetic === true || audienceStatus?.ready === false;
  const hideBestFormat = bestFormatStatus?.synthetic === true || bestFormatStatus?.ready === false;
  const hideOptimalTime = optimalTimeStatus?.synthetic === true || optimalTimeStatus?.ready === false;
  const hideContentMix = mixStatus?.synthetic === true || mixStatus?.ready === false;

  return (
    <>
      {noticeMessage ? <div className="pulse-demo-notice">{noticeMessage}</div> : null}
      <div className="pulse-header-row">
        <div>
          <h2 className="pulse-title">{metrics.header.title}</h2>
          <p className="pulse-subtitle">{metrics.header.subtitle}</p>
        </div>
        <div className="pulse-growth-badge">{metrics.header.growth_badge}</div>
      </div>

      <section className={`pulse-score-card ${mode === "dark" ? "dark" : "light"}`}>
        <div className="pulse-score-label-wrap">
          <span className="pulse-score-icon">
            <AutoAwesomeRoundedIcon fontSize="small" />
          </span>
          <span
            className="pulse-score-label"
            title={metrics.pulse_score.formula || "Pulse Score uses four equal sub-scores with trailing 28-day weighting and weekly cap"}
          >
            Pulse Score
          </span>
        </div>
        <div className="pulse-score-main-row">
          <div>
            <div
              className="pulse-score-value pulse-score-value-hover"
              title={metrics.pulse_score.formula || "Pulse Score uses four equal sub-scores with trailing 28-day weighting and weekly cap"}
            >
              {metrics.pulse_score.score.toFixed(1)}
            </div>
            <div className="pulse-score-delta">
              {metrics.pulse_score.score_ready === false
                ? "Waiting for OAuth analytics snapshots"
                : metrics.pulse_score.delta_text}
            </div>
          </div>
          <div className="pulse-performance">
            <div className="pulse-performance-label">Performance</div>
            <div className="pulse-performance-value">{metrics.pulse_score.performance}</div>
          </div>
        </div>
      </section>

      <section className="pulse-kpi-row">
        <article className="pulse-kpi-card">
          <div>
            <p className="pulse-kpi-title">Engagement Rate</p>
            <h3 className="pulse-kpi-value">{metrics.engagement_rate.value.toFixed(1)}%</h3>
            <p className="pulse-kpi-note">{`${metrics.engagement_rate.delta >= 0 ? "+" : ""}${metrics.engagement_rate.delta.toFixed(1)}% from average`}</p>
          </div>
          <div className="pulse-kpi-icon pink">
            <BoltRoundedIcon fontSize="small" />
          </div>
        </article>
        <article className="pulse-kpi-card">
          <div>
            <p className="pulse-kpi-title">Best Format</p>
            <h3 className="pulse-kpi-value">{hideBestFormat ? "Data pending" : metrics.best_format.label}</h3>
            <p className="pulse-kpi-muted">
              {hideBestFormat ? "Waiting for non-synthetic format data." : `${metrics.best_format.share}% of total content`}
            </p>
          </div>
          <div className="pulse-kpi-icon blue">S</div>
        </article>
        <article className="pulse-kpi-card">
          <div>
            <p className="pulse-kpi-title">Optimal Time</p>
            <h3 className="pulse-kpi-value">{hideOptimalTime ? "Data pending" : metrics.optimal_time.label}</h3>
            <p className="pulse-kpi-note">
              {hideOptimalTime ? "Waiting for reliable audience-time signal." : `~${Math.abs(metrics.optimal_time.delta).toFixed(1)}% engagement`}
            </p>
          </div>
          <div className="pulse-kpi-icon amber">
            <ScheduleRoundedIcon fontSize="small" />
          </div>
        </article>
      </section>

      <section className="pulse-score-plan-card">
        <div className="pulse-score-plan-header">
          <div>
            <h3 className="pulse-score-plan-title">{scorePlan?.title || "How to improve your Pulse score?"}</h3>
            <p className="pulse-score-plan-subtitle">
              {scorePlanLoading
                ? "Building action plan..."
                : scorePlan?.subtitle || scorePlanError || "No action plan available yet."}
            </p>
          </div>
          <div className="pulse-score-plan-potential">{`+${(scorePlan?.potential_score_lift ?? 0).toFixed(2)} potential`}</div>
        </div>
        <div className="pulse-score-plan-list">
          {(scorePlan?.items || []).slice(0, 6).map((item, idx) => (
            <div key={`${item.title}-${idx}`} className="pulse-score-plan-item">
              <div className={`pulse-score-plan-status ${item.status === "verified" ? "done" : "todo"}`}>
                {item.status === "verified" ? "OK" : "-"}
              </div>
              <div className="pulse-score-plan-content">
                <div className="pulse-score-plan-item-title">{item.title}</div>
                <div className="pulse-score-plan-item-detail">{item.detail}</div>
              </div>
              <div className="pulse-score-plan-lift">{`+${item.score_lift.toFixed(2)}`}</div>
            </div>
          ))}
          {!scorePlanLoading && (!scorePlan?.items || scorePlan.items.length === 0) ? (
            <div className="pulse-score-plan-empty">No actionable items yet.</div>
          ) : null}
        </div>
      </section>

      <section className="pulse-data-row">
        <article className="pulse-panel-card">
          <div className="pulse-panel-heading">Performance Trends</div>
          <p className="pulse-panel-sub">Engagement rate by content format</p>
          <TrendChart mode={mode} points={metrics.performance_trends} loading={trendsLoading} />
          <div className="pulse-legend">
            <span><i className="dot short" /> Short-form</span>
            <span><i className="dot long" /> Long-form</span>
          </div>
        </article>
        <article className="pulse-panel-card mix">
          <div className="pulse-panel-heading">Content Mix</div>
          <p className="pulse-panel-sub">Format distribution</p>
          {hideContentMix ? (
            <p className="pulse-panel-sub">Hidden until enough real format-distribution data is available.</p>
          ) : (
            <div className="pulse-donut-wrap">
              <div
                className={`pulse-donut ${mixLoading ? "loading" : "loaded"}`}
                style={
                  {
                    ["--mix-short-deg" as string]: `${Math.max(0, Math.min(100, metrics.content_mix.short)) * 3.6}deg`,
                    ["--mix-long-end-deg" as string]: `${Math.max(0, Math.min(100, metrics.content_mix.short + metrics.content_mix.long)) * 3.6}deg`,
                  } as React.CSSProperties
                }
              />
              <div className="pulse-mix-list">
                <div>
                  <span className="pulse-mix-label"><i className="dot short" /> Short-form</span>
                  <strong>{metrics.content_mix.short}%</strong>
                </div>
                <div>
                  <span className="pulse-mix-label"><i className="dot long" /> Long-form</span>
                  <strong>{metrics.content_mix.long}%</strong>
                </div>
                <div>
                  <span className="pulse-mix-label"><i className="dot carousel" /> Carousel</span>
                  <strong>{metrics.content_mix.carousel}%</strong>
                </div>
              </div>
            </div>
          )}
        </article>
      </section>

      <section className="pulse-bottom-row">
        <article className="pulse-panel-card">
          <div className="pulse-panel-heading">Topic Performance</div>
          <p className="pulse-panel-sub">{`Ranked by engagement score (${metrics.topic_window || "Last 30 days"})`}</p>
          {hideTopic ? (
            <p className="pulse-panel-sub">Hidden until enough non-synthetic topic-level data is available.</p>
          ) : (
            <div className="pulse-topic-list">
              {metrics.topic_performance.slice(0, 3).map((topic) => (
                <div key={topic.label}>
                  <span>{topic.label}</span>
                  <b>{topic.count} posts</b>
                </div>
              ))}
            </div>
          )}
        </article>
        <article className="pulse-panel-card">
          <div className="pulse-panel-heading">Audience Activity</div>
          <p className="pulse-panel-sub">When your audience is most active</p>
          {hideAudience ? (
            <p className="pulse-panel-sub">Hidden until audience activity is measured from real analytics.</p>
          ) : (
            <div className="pulse-activity-bars">
              {metrics.audience_activity.slice(0, 7).map((v, idx) => (
                <div
                  key={idx}
                  className="pulse-activity-bar-item"
                  data-tooltip={
                    metrics.audience_activity_detail?.[idx]
                      ? `${metrics.audience_activity_detail[idx].label}: ${metrics.audience_activity_detail[idx].score}% activity${metrics.audience_activity_detail[idx].avg_views ? ` - ${metrics.audience_activity_detail[idx].avg_views} avg views` : ""}`
                      : `Activity: ${v}%`
                  }
                >
                  <span className="pulse-activity-tooltip">
                    {metrics.audience_activity_detail?.[idx]
                      ? `${metrics.audience_activity_detail[idx].label}: ${metrics.audience_activity_detail[idx].score}%`
                      : `${v}%`}
                  </span>
                  <span className="pulse-activity-fill" style={{ height: `${v}%` }} />
                </div>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="pulse-recommendation-card">
        <div className="pulse-recommendation-title">IRIS Recommendation</div>
        {recommendationLoading ? (
          <p className="pulse-panel-sub">Generating recommendation...</p>
        ) : recommendation ? (
          <>
            <p className="pulse-recommendation-summary">{recommendation.summary}</p>
            <p className="pulse-recommendation-action"><strong>Action:</strong> {recommendation.action}</p>
            <div className="pulse-recommendation-meta">
              <span>Confidence: {recommendation.confidence}</span>
              {Array.isArray(recommendation.based_on) && recommendation.based_on.length > 0 ? (
                <span>Based on: {recommendation.based_on.slice(0, 2).join(" - ")}</span>
              ) : null}
            </div>
          </>
        ) : (
          <p className="pulse-panel-sub">{recommendationError || "No recommendation available yet."}</p>
        )}
      </section>
    </>
  );
};

void LegacyInsightsView;

const clampPulseScore = (value: number) => Math.max(0, Math.min(10, Number.isFinite(value) ? value : 0));

const formatSignedNumber = (value: number, digits = 1) => `${value >= 0 ? "+" : ""}${value.toFixed(digits)}`;

const parseDeltaPercent = (value: string) => {
  const match = String(value || "").match(/[-+]?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 12;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Morning";
  if (hour < 18) return "Afternoon";
  return "Evening";
};

const getCreatorFirstName = (account: AccountState, preview: CreatorPreview | null) => {
  const title = String(preview?.title || account.creatorName || "").trim();
  const cleaned = title.replace(/^building\s+value\s+with\s+/i, "").replace(/^the\s+/i, "").trim();
  const first = cleaned.split(/\s+/).filter(Boolean)[0];
  return first || "Creator";
};

const buildSavedIrisTitle = (text: string, type: SavedIrisItem["type"]) => {
  const firstLine =
    String(text || "")
      .split("\n")
      .map((line) =>
        line
          .replace(/^#+\s*/, "")
          .replace(/^[-*]\s*/, "")
          .replace(/\*\*/g, "")
          .trim(),
      )
      .find(Boolean) || (type === "metric" ? "Saved metric" : "Saved action");
  return firstLine.length > 92 ? `${firstLine.slice(0, 89).trim()}...` : firstLine;
};

const normalizePulseChartSpec = (raw: any): PulseChartSpec | null => {
  if (!raw || typeof raw !== "object") return null;
  const kind = String(raw.kind || raw.type || "").toLowerCase() === "pie" ? "pie" : String(raw.kind || raw.type || "").toLowerCase() === "line" ? "line" : "";
  if (!kind) return null;
  const normalizeRows = (rows: any[]): PulseChartPoint[] =>
    rows
      .map((row) => ({
        label: String(row?.label || "").trim().slice(0, 48),
        value: Number(row?.value ?? 0),
      }))
      .filter((row) => row.label && Number.isFinite(row.value))
      .slice(0, kind === "pie" ? 10 : 16);
  const points = normalizeRows(Array.isArray(raw.points) ? raw.points : []);
  const slices = normalizeRows(Array.isArray(raw.slices) ? raw.slices : []).filter((row) => row.value > 0);
  if (kind === "line" && points.length < 2) return null;
  if (kind === "pie" && slices.length < 2) return null;
  return {
    kind,
    title: String(raw.title || (kind === "line" ? "Line chart" : "Pie chart")).trim().slice(0, 120),
    subtitle: String(raw.subtitle || "").trim().slice(0, 220),
    x_label: String(raw.x_label || "").trim().slice(0, 60),
    y_label: String(raw.y_label || "").trim().slice(0, 60),
    source_prompt: String(raw.source_prompt || "").trim().slice(0, 500),
    colors: Array.isArray(raw.colors) ? raw.colors.map((color: any) => String(color || "").trim()).filter(Boolean).slice(0, 12) : undefined,
    points,
    slices,
  };
};

const normalizeSavedIrisItem = (raw: any): SavedIrisItem | null => {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  const body = String(raw.body || "").trim();
  if (!id || !body) return null;
  return {
    id,
    pulse_user_id: String(raw.pulse_user_id || ""),
    channel_id: String(raw.channel_id || ""),
    type: String(raw.type || "").toLowerCase() === "action" ? "action" : "metric",
    title: String(raw.title || "").trim() || "Saved by IRIS",
    body,
    value: raw.value == null ? null : String(raw.value || ""),
    chart: normalizePulseChartSpec(raw.chart),
    source: raw.source == null ? null : String(raw.source || ""),
    category: raw.category == null ? null : String(raw.category || ""),
    pinned: Boolean(raw.pinned),
    created_at: raw.created_at == null ? null : String(raw.created_at || ""),
    updated_at: raw.updated_at == null ? null : String(raw.updated_at || ""),
  };
};

const normalizePulseScorePlanItem = (raw: any): PulseScorePlanItem | null => {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || "").trim();
  if (!title) return null;
  const rawStatus = String(raw.status || "proposed").toLowerCase();
  // "done" is a real, distinct status (self-reported completion of a manual
  // move - see origin below) - it must never collapse into "verified", which
  // means IRIS algorithmically confirmed the result against real measurement.
  const status: PulseScorePlanItem["status"] = rawStatus === "todo" || !["proposed", "pending", "verified", "no_move", "expired", "done"].includes(rawStatus)
    ? "proposed"
    : rawStatus as PulseScorePlanItem["status"];
  const origin = raw.origin === "manual" ? "manual" : raw.origin === "algorithmic" ? "algorithmic" : undefined;
  return {
    measurement_phase: raw.measurement_phase,
    id: raw.id == null ? undefined : String(raw.id),
    title,
    detail: String(raw.detail || "").trim(),
    status,
    score_lift: Number(raw.score_lift ?? 0),
    pillar: raw.pillar == null ? undefined : String(raw.pillar),
    pillar_label: raw.pillar_label == null ? undefined : String(raw.pillar_label),
    metric_key: raw.metric_key == null ? undefined : String(raw.metric_key),
    metric_label: raw.metric_label == null ? undefined : String(raw.metric_label),
    target_delta_pct: raw.target_delta_pct == null ? undefined : Number(raw.target_delta_pct),
    evaluation_days: raw.evaluation_days == null ? undefined : Number(raw.evaluation_days),
    measured_days: raw.measured_days == null ? undefined : Number(raw.measured_days),
    baseline_value: raw.baseline_value == null ? null : Number(raw.baseline_value),
    current_value: raw.current_value == null ? null : Number(raw.current_value),
    result_delta_pct: raw.result_delta_pct == null ? null : Number(raw.result_delta_pct),
    evidence: raw.evidence == null ? undefined : String(raw.evidence),
    origin,
    saved_item_id: raw.saved_item_id == null ? null : String(raw.saved_item_id),
    evaluated_at: raw.evaluated_at == null ? null : String(raw.evaluated_at),
  };
};

const pulseChartRequested = (text: string) => {
  const lower = String(text || "").toLowerCase();
  return /\b(chart|graph|grpah|fraph|plot|visual|visually|visuali[sz]e|render|mermaid|xychart|pie|line chart|trend line|breakdown|line graph)\b/.test(lower);
};

const pulseChartFollowUpRequested = (text: string) => {
  const lower = String(text || "").toLowerCase();
  return /\b(first|second|option a|option b|use whatever|fake data|mock|example|show me|make it|need it|do it|yes|please|again)\b/.test(lower);
};

const PulseMiniComposer: React.FC<{
  className?: string;
  chatDraft: string;
  chatLoading: boolean;
  chatStatusMessage: string;
  chatError: string | null;
  chatMessages: PulseChatMessage[];
  composerRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  showResults?: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onNewChat: () => void;
}> = ({
  className = "",
  chatDraft,
  chatLoading,
  chatStatusMessage,
  chatError,
  chatMessages,
  composerRef,
  showResults = true,
  onDraftChange,
  onSend,
  onNewChat,
}) => {
  const latestMessages = chatMessages.filter((message) => message.text !== DEFAULT_ASSISTANT_TEXT).slice(-2);
  const hasConversation = latestMessages.length > 0 || chatLoading || chatError;
  const canSend = chatDraft.trim().length > 0 && !chatLoading;

  return (
    <div className={`pulse-new-composer ${className}`}>
      <div className="pulse-new-composer-input-row">
        <textarea
          ref={(node) => {
            composerRef.current = node;
          }}
          rows={1}
          className="pulse-new-composer-input"
          placeholder="Ask IRIS anything about your content..."
          value={chatDraft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <button type="button" className={`pulse-new-send ${canSend ? "ready" : ""}`} onClick={onSend} disabled={!canSend}>
          <ArrowUpwardRoundedIcon fontSize="small" />
        </button>
      </div>
      <div className="pulse-new-suggestions">
        <span>Suggestions:</span>
        {SUGGESTIONS.map((suggestion) => (
          <button key={suggestion} type="button" onClick={() => onDraftChange(suggestion)}>
            {suggestion}
          </button>
        ))}
        {showResults && hasConversation ? (
          <button type="button" className="pulse-new-chat-reset" onClick={onNewChat}>
            New chat
          </button>
        ) : null}
      </div>
      {showResults && hasConversation ? (
        <div className="pulse-new-chat-results">
          {latestMessages.map((message, idx) => (
            <div key={`${message.role}-${idx}`} className={`pulse-new-chat-result ${message.role}`}>
              {message.role === "assistant" ? renderPulseMarkdown(message.text) : message.text}
            </div>
          ))}
          {chatLoading ? <div className="pulse-new-chat-result assistant">{chatStatusMessage || "IRIS is working..."}</div> : null}
          {chatError ? <div className="pulse-new-chat-error">{chatError}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

const PulseScoreGauge: React.FC<{ score: number }> = ({ score }) => {
  const clamped = clampPulseScore(score);
  const ticks = 34;
  const activeTicks = Math.round((clamped / 10) * ticks);
  const tickColor = (idx: number) => {
    if (idx >= activeTicks) return "#eceaf0";
    const ratio = idx / Math.max(1, ticks - 1);
    if (ratio < 0.32) return "#72dff0";
    if (ratio < 0.66) return "#a8a1ff";
    return "#8b46ff";
  };
  return (
    <div
      className="pulse-new-gauge"
      title={`Pulse Score ${clamped.toFixed(1)} out of 10`}
    >
      <svg className="pulse-new-gauge-svg" viewBox="0 0 140 86" aria-hidden="true">
        {Array.from({ length: ticks }).map((_, idx) => {
          const angle = Math.PI + (idx / (ticks - 1)) * Math.PI;
          const outer = 58;
          const inner = 47;
          const cx = 70;
          const cy = 73;
          const x1 = cx + Math.cos(angle) * inner;
          const y1 = cy + Math.sin(angle) * inner;
          const x2 = cx + Math.cos(angle) * outer;
          const y2 = cy + Math.sin(angle) * outer;
          return (
            <line
              key={idx}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={tickColor(idx)}
              strokeWidth="3"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="pulse-new-gauge-value">
        <strong>{clamped.toFixed(1)}</strong>
        <span>out of 10</span>
      </div>
    </div>
  );
};

const InsightsView: React.FC<{
  mode: Mode;
  metrics: InsightsMetrics;
  widgetStatus: Partial<Record<PulseMetricWidget, PulseWidgetStatus>>;
  scorePlan: PulseScorePlan | null;
  scorePlanLoading: boolean;
  scorePlanError?: string | null;
  recommendation: PulseRecommendation | null;
  recommendationLoading: boolean;
  recommendationError?: string | null;
  trendsLoading: boolean;
  mixLoading: boolean;
  account: AccountState;
  accountPreview: CreatorPreview | null;
  chatDraft: string;
  chatMessages: PulseChatMessage[];
  chatLoading: boolean;
  chatStatusMessage: string;
  chatError: string | null;
  heroComposerRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  onChatDraftChange: (value: string) => void;
  onChatSend: () => void;
  onNewChat: () => void;
  onOpenMediaKit: () => void;
}> = ({
  mode,
  metrics,
  widgetStatus,
  scorePlan,
  scorePlanLoading,
  scorePlanError,
  recommendation,
  recommendationLoading,
  recommendationError,
  trendsLoading,
  mixLoading,
  account,
  accountPreview,
  chatDraft,
  chatMessages,
  chatLoading,
  chatStatusMessage,
  chatError,
  heroComposerRef,
  onChatDraftChange,
  onChatSend,
  onNewChat,
  onOpenMediaKit,
}) => {
  const topicStatus = widgetStatus.topic_performance;
  const audienceStatus = widgetStatus.audience_activity;
  const bestFormatStatus = widgetStatus.best_format;
  const optimalTimeStatus = widgetStatus.optimal_time;
  const mixStatus = widgetStatus.content_mix;

  const hideTopic = topicStatus?.synthetic === true || topicStatus?.ready === false;
  const hideAudience = audienceStatus?.synthetic === true || audienceStatus?.ready === false;
  const hideBestFormat = bestFormatStatus?.synthetic === true || bestFormatStatus?.ready === false;
  const hideOptimalTime = optimalTimeStatus?.synthetic === true || optimalTimeStatus?.ready === false;
  const hideContentMix = mixStatus?.synthetic === true || mixStatus?.ready === false;
  const greetingName = getCreatorFirstName(account, accountPreview);
  const score = clampPulseScore(metrics.pulse_score.score);
  const weeklyDelta = parseDeltaPercent(metrics.pulse_score.delta_text);
  const weekLabel = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date());
  const thisWeekItems = scorePlan?.items?.slice(0, 5) || [];
  const scoreDimensions = [
    {
      title: "Content Strategy",
      score: clampPulseScore(Math.max(5.8, score + 0.4)),
      note: hideBestFormat
        ? "Format strategy is waiting for reliable non-synthetic data."
        : `${metrics.best_format.label} is carrying the strongest return on effort.`,
      tone: "purple",
      icon: <AutoAwesomeRoundedIcon fontSize="small" />,
    },
    {
      title: "Execution Craft",
      score: clampPulseScore(Math.max(5.5, score - 0.8)),
      note: recommendation?.action || "IRIS is watching packaging and early engagement quality.",
      tone: "pink",
      icon: <BoltRoundedIcon fontSize="small" />,
    },
    {
      title: "Audience Growth & Retention",
      score: clampPulseScore(Math.max(5.8, score + 0.7)),
      note: `${metrics.engagement_rate.value.toFixed(1)}% engagement rate is the strongest available audience signal.`,
      tone: "cyan",
      icon: <ForumOutlinedIcon fontSize="small" />,
    },
    {
      title: "Publishing Discipline",
      score: clampPulseScore(Math.max(5.6, score - 0.3)),
      note: hideOptimalTime
        ? "Cadence guidance will sharpen as audience timing data fills in."
        : `Aim around ${metrics.optimal_time.label} for your next high-intent upload.`,
      tone: "amber",
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <div className="pulse-new-insights">
      <section className="pulse-new-hero">
        <div className="pulse-new-greeting">
          <AutoAwesomeRoundedIcon className="pulse-new-greeting-icon" />
          <h2>
            {getGreeting()} {greetingName}, IRIS is at your service
            <span className="pulse-new-greeting-smile" aria-hidden="true">:)</span>
          </h2>
        </div>
        <PulseMiniComposer
          chatDraft={chatDraft}
          chatLoading={chatLoading}
          chatStatusMessage={chatStatusMessage}
          chatError={chatError}
          chatMessages={chatMessages}
          composerRef={heroComposerRef}
          showResults={false}
          onDraftChange={onChatDraftChange}
          onSend={onChatSend}
          onNewChat={onNewChat}
        />
      </section>

      <section className="pulse-new-section pulse-new-week-section">
        <div className="pulse-new-section-heading">
          <div>
            <h3>Your Week</h3>
            <p>{scorePlanLoading ? "Updating now" : metrics.header.subtitle || "Updated recently"}</p>
          </div>
          <button type="button" className="pulse-new-outline-btn">
            <BarChartRoundedIcon fontSize="small" /> Add to Dashboard
          </button>
        </div>
        <article className="pulse-new-card pulse-new-actions-card">
          <div className="pulse-new-card-title">
            <h4>This Week's Actions</h4>
            <span>{weekLabel}</span>
          </div>
          {scorePlanLoading ? (
            <p className="pulse-new-muted">IRIS is building this week's action list...</p>
          ) : thisWeekItems.length ? (
            <div className="pulse-new-action-list">
              {thisWeekItems.map((item, idx) => (
                <div key={`${item.title}-${idx}`} className="pulse-new-action-item">
                  <span className={`pulse-new-checkbox ${item.status === "verified" ? "checked" : ""}`}>
                    {item.status === "verified" ? <CheckRoundedIcon fontSize="inherit" /> : null}
                  </span>
                  <div>
                    <strong>{item.title}</strong>
                    {item.detail ? <p>{item.detail}</p> : null}
                  </div>
                  <b>{formatSignedNumber(item.score_lift, 2)}</b>
                </div>
              ))}
            </div>
          ) : (
            <p className="pulse-new-muted">{scorePlanError || "No weekly actions available yet."}</p>
          )}
        </article>
      </section>

      <section className="pulse-new-card pulse-new-score-card">
        <div className="pulse-new-card-eyebrow">Pulse Scores</div>
        <p className="pulse-new-card-subtitle">Here's what's driving it and where to focus next.</p>
        <div className="pulse-new-score-summary">
          <button type="button" className="pulse-new-gauge-card" title={metrics.pulse_score.formula || undefined}>
            <PulseScoreGauge score={score} />
            <span>View details</span>
          </button>
          <div className="pulse-new-score-copy">
            <strong>Your score is up by</strong>
            <span className="pulse-new-score-pill">{formatSignedNumber(weeklyDelta, 0)}%</span>
            <strong>this week.</strong>
          </div>
        </div>
        <div className="pulse-new-score-grid">
          {scoreDimensions.map((dimension) => (
            <article key={dimension.title} className={`pulse-new-dimension-card ${dimension.tone}`}>
              <div className="pulse-new-dimension-top">
                <span>{dimension.icon}</span>
                <p>{dimension.title}</p>
                <strong>{dimension.score.toFixed(1)}</strong>
              </div>
              <div
                className="pulse-new-dimension-bar"
                style={{ ["--dimension-score" as string]: `${dimension.score * 10}%` } as React.CSSProperties}
              >
                <span />
              </div>
              <p>{dimension.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pulse-new-media-strip">
        <div>
          <strong>Your Pulse Score is trending strong this quarter.</strong>
          <p>Ready to turn it into a sponsor pitch?</p>
        </div>
        <button type="button" onClick={onOpenMediaKit}>
          Open Media Kit <ChevronRightRoundedIcon fontSize="small" />
        </button>
      </section>

      <div className="pulse-new-divider"><span>Your Performance</span></div>

      <section className="pulse-new-kpi-grid">
        <article className="pulse-new-card pulse-new-kpi">
          <p>Engagement Rate</p>
          <h4>{metrics.engagement_rate.value.toFixed(1)}%</h4>
          <span>{formatSignedNumber(metrics.engagement_rate.delta, 1)}% above your average</span>
        </article>
        <article className="pulse-new-card pulse-new-kpi">
          <p>Best Format</p>
          <h4>{hideBestFormat ? "Data pending" : metrics.best_format.label}</h4>
          <span>{hideBestFormat ? "Waiting for format signal" : `${metrics.best_format.share}% of output - highest return on effort`}</span>
        </article>
        <article className="pulse-new-card pulse-new-kpi">
          <p>Optimal Post Time</p>
          <h4>{hideOptimalTime ? "Data pending" : metrics.optimal_time.label}</h4>
          <span>{hideOptimalTime ? "Waiting for timing signal" : `${formatSignedNumber(metrics.optimal_time.delta, 0)}% engagement lift`}</span>
        </article>
      </section>

      <section className="pulse-new-chart-grid">
        <article className="pulse-new-card pulse-new-trends-card">
          <div className="pulse-new-panel-title">
            <h4>Performance Trends</h4>
            <p>Engagement rate by content format</p>
          </div>
          <TrendChart mode={mode} points={metrics.performance_trends} loading={trendsLoading} />
          <div className="pulse-legend pulse-new-legend">
            <span><i className="dot short" /> Short-form</span>
            <span><i className="dot long" /> Long-form</span>
            <span><i className="dot carousel" /> Carousel</span>
          </div>
        </article>
        <article className="pulse-new-card pulse-new-mix-card">
          <div className="pulse-new-panel-title">
            <h4>Content Mix</h4>
            <p>Format distribution</p>
          </div>
          {hideContentMix ? (
            <p className="pulse-new-muted">Waiting for enough format-distribution data.</p>
          ) : (
            <div className="pulse-new-donut-wrap">
              <div
                className={`pulse-donut ${mixLoading ? "loading" : "loaded"}`}
                style={
                  {
                    ["--mix-short-deg" as string]: `${Math.max(0, Math.min(100, metrics.content_mix.short)) * 3.6}deg`,
                    ["--mix-long-end-deg" as string]: `${Math.max(0, Math.min(100, metrics.content_mix.short + metrics.content_mix.long)) * 3.6}deg`,
                  } as React.CSSProperties
                }
              />
              <div className="pulse-mix-list pulse-new-mix-list">
                <div>
                  <span className="pulse-mix-label"><i className="dot short" /> Short-form</span>
                  <strong>{metrics.content_mix.short}%</strong>
                </div>
                <div>
                  <span className="pulse-mix-label"><i className="dot long" /> Long-form</span>
                  <strong>{metrics.content_mix.long}%</strong>
                </div>
                <div>
                  <span className="pulse-mix-label"><i className="dot carousel" /> Carousel</span>
                  <strong>{metrics.content_mix.carousel}%</strong>
                </div>
              </div>
            </div>
          )}
        </article>
      </section>

      <section className="pulse-new-bottom-grid">
        <article className="pulse-new-card pulse-new-topic-card">
          <div className="pulse-new-panel-title">
            <h4>Topic Performance</h4>
            <p>{`Ranked by engagement score (${metrics.topic_window || "Last 30 days"})`}</p>
          </div>
          {hideTopic ? (
            <p className="pulse-new-muted">Waiting for enough topic-level data.</p>
          ) : (
            <div className="pulse-new-topic-list">
              {metrics.topic_performance.slice(0, 5).map((topic, idx) => {
                const max = Math.max(1, ...metrics.topic_performance.map((entry) => entry.count));
                return (
                  <div key={topic.label}>
                    <div>
                      <strong>{topic.label}</strong>
                      <span>{topic.count} posts</span>
                      <b>{Math.max(45, Math.round((topic.count / max) * 100) - idx * 3)}</b>
                    </div>
                    <span style={{ width: `${Math.max(18, (topic.count / max) * 100)}%` }} />
                  </div>
                );
              })}
            </div>
          )}
        </article>
        <article className="pulse-new-card pulse-new-audience-card">
          <div className="pulse-new-panel-title">
            <h4>Audience Activity</h4>
            <p>When your audience is most active</p>
          </div>
          {hideAudience ? (
            <p className="pulse-new-muted">Waiting for analytics-backed audience activity.</p>
          ) : (
            <>
              <div className="pulse-activity-bars pulse-new-activity-bars">
                {metrics.audience_activity.slice(0, 7).map((v, idx) => (
                  <div key={idx} className="pulse-activity-bar-item">
                    <span className="pulse-activity-tooltip">
                      {metrics.audience_activity_detail?.[idx]
                        ? `${metrics.audience_activity_detail[idx].label}: ${metrics.audience_activity_detail[idx].score}%`
                        : `${v}%`}
                    </span>
                    <span className="pulse-activity-fill" style={{ height: `${v}%` }} />
                    <em>{metrics.audience_activity_detail?.[idx]?.label || ["6 AM", "9 AM", "12 PM", "3 PM", "6 PM", "9 PM", "12 AM"][idx]}</em>
                  </div>
                ))}
              </div>
              <div className="pulse-new-iris-note">
                <strong>IRIS:</strong> Post near your strongest audience window for maximum reach.
              </div>
            </>
          )}
        </article>
      </section>

      <section className="pulse-new-card pulse-new-recommendation">
        <div className="pulse-new-panel-title">
          <h4>IRIS Recommendation</h4>
          <p>{recommendationLoading ? "Generating recommendation..." : recommendation?.title || "Weekly direction"}</p>
        </div>
        {recommendation ? (
          <>
            <p>{recommendation.summary}</p>
            <strong>Action: {recommendation.action}</strong>
          </>
        ) : (
          <p>{recommendationError || "No recommendation available yet."}</p>
        )}
      </section>
    </div>
  );
};

void InsightsView;

type SavedLibraryTab = "metrics" | "actions";

const PULSE_CHART_COLORS = ["#8b46ff", "#f6339a", "#17c9e8", "#f6c445", "#10b981", "#6366f1", "#fb7185"];

const formatPulseChartValue = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toFixed(1);
};

const PulseIrisChart: React.FC<{ chart: PulseChartSpec; compact?: boolean }> = ({ chart, compact = false }) => {
  const colors = chart.colors?.length ? chart.colors : PULSE_CHART_COLORS;
  if (chart.kind === "pie") {
    const slices = (chart.slices || []).filter((slice) => slice.value > 0);
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    if (!total || slices.length < 2) return null;
    let cursor = 0;
    const stops = slices
      .map((slice, idx) => {
        const start = cursor;
        const end = cursor + (slice.value / total) * 100;
        cursor = end;
        const color = colors[idx % colors.length];
        return `${color} ${start.toFixed(2)}% ${end.toFixed(2)}%`;
      })
      .join(", ");
    return (
      <div className={`pulse-iris-chart pie ${compact ? "compact" : ""}`}>
        <div className="pulse-iris-chart-head">
          <h4>{chart.title}</h4>
          {chart.subtitle ? <p>{chart.subtitle}</p> : null}
        </div>
        <div className="pulse-iris-pie-row">
          <div className="pulse-iris-pie" style={{ "--pie-stops": stops } as React.CSSProperties}>
            <span>{Math.round(total).toLocaleString()}</span>
          </div>
          <div className="pulse-iris-chart-legend">
            {slices.map((slice, idx) => (
              <div key={`${slice.label}-${idx}`}>
                <i style={{ background: colors[idx % colors.length] }} />
                <span>{slice.label}</span>
                <strong>{Math.round((slice.value / total) * 100)}%</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const points = chart.points || [];
  if (points.length < 2) return null;
  const values = points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const width = 340;
  const height = 190;
  const padLeft = 52;
  const padRight = 18;
  const padTop = 18;
  const padBottom = 34;
  const coords = points.map((point, idx) => {
    const x = padLeft + (idx / Math.max(1, points.length - 1)) * (width - padLeft - padRight);
    const y = padTop + (1 - (point.value - min) / range) * (height - padTop - padBottom);
    return { ...point, x, y };
  });
  const path = coords.map((point, idx) => `${idx === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(" ");
  const areaPath = `${path} L ${coords[coords.length - 1].x.toFixed(2)} ${height - padBottom} L ${coords[0].x.toFixed(2)} ${height - padBottom} Z`;
  return (
    <div className={`pulse-iris-chart line ${compact ? "compact" : ""}`}>
      <div className="pulse-iris-chart-head">
        <h4>{chart.title}</h4>
        {chart.subtitle ? <p>{chart.subtitle}</p> : null}
      </div>
      <svg className="pulse-iris-line-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={chart.title}>
        <defs>
          <linearGradient id={`pulse-chart-area-${chart.title.replace(/[^a-z0-9]/gi, "") || "iris"}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b46ff" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#8b46ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3].map((tick) => {
          const ratio = tick / 3;
          const y = padTop + ratio * (height - padTop - padBottom);
          const value = max - ratio * range;
          return (
            <g key={tick}>
              <line x1={padLeft} x2={width - padRight} y1={y} y2={y} className="pulse-iris-grid-line" />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="pulse-iris-axis-label">
                {formatPulseChartValue(value)}
              </text>
            </g>
          );
        })}
        <path className="pulse-iris-area" d={areaPath} />
        <path className="pulse-iris-line-path" d={path} pathLength={1} />
        {coords.map((point, idx) => (
          <g key={`${point.label}-${idx}`} className="pulse-iris-point" style={{ "--point-delay": `${idx * 80}ms` } as React.CSSProperties}>
            <title>{`${point.label}: ${Math.round(point.value).toLocaleString()}`}</title>
            <circle cx={point.x} cy={point.y} r="4.5" />
            {idx === 0 || idx === coords.length - 1 || points.length <= 6 ? (
              <text x={point.x} y={height - 12} textAnchor="middle">{point.label}</text>
            ) : null}
          </g>
        ))}
      </svg>
    </div>
  );
};

const SavedByIrisView: React.FC<{
  activeLibraryTab: SavedLibraryTab;
  items: SavedIrisItem[];
  loading: boolean;
  error: string | null;
  onLibraryTabChange: (tab: SavedLibraryTab) => void;
  onAskSuggestion: (prompt: string) => void;
  onTogglePinned: (item: SavedIrisItem) => void;
  onDelete: (item: SavedIrisItem) => void;
}> = ({ activeLibraryTab, items, loading, error, onLibraryTabChange, onAskSuggestion, onTogglePinned, onDelete }) => {
  const isMetrics = activeLibraryTab === "metrics";
  const currentType = isMetrics ? "metric" : "action";
  const filteredItems = items.filter((item) => item.type === currentType);
  const suggestion = isMetrics
    ? 'Try asking IRIS: "What\'s my average engagement rate?"'
    : 'Try asking IRIS: "What should I do next week?"';
  const prompt = isMetrics ? "What's my average engagement rate?" : "What should I do next week?";
  const formatSavedDate = (value?: string | null) => {
    if (!value) return "Saved just now";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Saved just now";
    return `Saved ${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date)}`;
  };

  return (
    <div className="pulse-saved-view">
      <header className="pulse-saved-head">
        <h2>Saved by IRIS</h2>
        <p>Metrics and actions captured from your IRIS conversations</p>
      </header>

      <div className="pulse-saved-tabs" role="tablist" aria-label="Saved by IRIS library">
        <button
          type="button"
          className={isMetrics ? "active" : ""}
          onClick={() => onLibraryTabChange("metrics")}
          role="tab"
          aria-selected={isMetrics}
        >
          <BarChartRoundedIcon fontSize="small" /> Saved Metrics
        </button>
        <button
          type="button"
          className={!isMetrics ? "active" : ""}
          onClick={() => onLibraryTabChange("actions")}
          role="tab"
          aria-selected={!isMetrics}
        >
          <CheckRoundedIcon fontSize="small" /> Saved Actions
        </button>
      </div>

      {error ? <div className="pulse-saved-error">{error}</div> : null}

      {loading ? (
        <div className="pulse-saved-list">
          <div className="pulse-saved-card loading" />
          <div className="pulse-saved-card loading" />
        </div>
      ) : filteredItems.length ? (
        <section className="pulse-saved-list" aria-live="polite">
          {filteredItems.map((item) => (
            <article key={item.id} className={`pulse-saved-card ${item.pinned ? "pinned" : ""}`}>
              <div className="pulse-saved-card-top">
                <span className="pulse-saved-card-type">
                  {isMetrics ? <BarChartRoundedIcon fontSize="small" /> : <CheckRoundedIcon fontSize="small" />}
                  {isMetrics ? "Metric" : "Action"}
                </span>
                <span>{formatSavedDate(item.created_at)}</span>
              </div>
              <h3>{item.title}</h3>
              {item.value ? <strong className="pulse-saved-value">{item.value}</strong> : null}
              {item.chart ? <PulseIrisChart chart={item.chart} compact /> : null}
              <div className="pulse-saved-body">{renderPulseMarkdown(item.body)}</div>
              <div className="pulse-saved-card-actions">
                <button type="button" onClick={() => onTogglePinned(item)} className={item.pinned ? "active" : ""}>
                  <PushPinOutlinedIcon fontSize="small" />
                  {item.pinned ? "Pinned" : "Pin"}
                </button>
                <button type="button" onClick={() => onDelete(item)}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="pulse-saved-empty" aria-live="polite">
          <div className="pulse-saved-empty-icon">
            {isMetrics ? <BarChartRoundedIcon fontSize="medium" /> : <CheckRoundedIcon fontSize="medium" />}
          </div>
          <h3>{isMetrics ? "No metrics saved yet" : "No actions saved yet"}</h3>
          <p>
            {isMetrics
              ? "When IRIS surfaces a data point you want to track, save it to your library. Pin it to your Insights dashboard to keep it visible."
              : "When IRIS recommends an action you want to take, save it here. Pin it to your weekly plan to keep it visible."}
          </p>
          <button type="button" className="pulse-saved-suggestion" onClick={() => onAskSuggestion(prompt)}>
            <AutoAwesomeRoundedIcon fontSize="small" />
            {suggestion}
          </button>
        </section>
      )}
    </div>
  );
};

void SavedByIrisView;

const MetricsSkeleton: React.FC<{ tab: Tab }> = ({ tab }) => (
  <div className="pulse-skeleton-page" aria-busy="true" aria-live="polite">
    <div className="pulse-skeleton-line lg" />
    <div className="pulse-skeleton-line sm" />
    {tab === "insights" ? (
      <>
        <div className="pulse-skeleton-block pulse-skeleton-score" />
        <div className="pulse-skeleton-grid-3">
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
        </div>
        <div className="pulse-skeleton-grid-2">
          <div className="pulse-skeleton-block pulse-skeleton-chart" />
          <div className="pulse-skeleton-block pulse-skeleton-chart" />
        </div>
        <div className="pulse-skeleton-grid-2">
          <div className="pulse-skeleton-block pulse-skeleton-small" />
          <div className="pulse-skeleton-block pulse-skeleton-small" />
        </div>
      </>
    ) : (
      <>
        <div className="pulse-skeleton-grid-2">
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
          <div className="pulse-skeleton-block pulse-skeleton-kpi" />
        </div>
        <div className="pulse-skeleton-block pulse-skeleton-chart" />
        <div className="pulse-skeleton-block pulse-skeleton-small" />
      </>
    )}
  </div>
);

const PulsePage: React.FC = () => {
  const [mode, setMode] = useState<Mode>(() => readMode());
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => readSidebarCollapsed());
  const [pulseUserId, setPulseUserId] = useState<string>(() => readPulseUserId());
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [passwordChangeBusy, setPasswordChangeBusy] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState<string | null>(null);
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [deleteAccountBusy, setDeleteAccountBusy] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  // Mirrors currentUser?.two_factor_enabled once /api/auth/me sends that
  // field; until then this is set directly from enable/verify/disable's own
  // responses (optimistic), defaulting to off.
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [pulseSessionReady, setPulseSessionReady] = useState(false);
  const [pulseSessionError, setPulseSessionError] = useState<string | null>(null);
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(() => {
    const seedUserId = readPulseUserId();
    if (!seedUserId) return "welcome";
    const savedStep = readOnboardingStep();
    const seedAccount = readAccount(seedUserId);
    const seedPreview = readAccountPreview(seedUserId);
    const hasSeed = Boolean(
      (seedPreview?.channel_id && String(seedPreview.channel_id).trim()) ||
        seedAccount.youtubeChannel.trim() ||
        seedAccount.creatorName.trim(),
    );
    return hasSeed ? (savedStep === "welcome" ? "done" : savedStep) : "welcome";
  });
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab: Tab = PATH_TABS[location.pathname] || "home";
  const [reportingRange, setReportingRange] = useState<PulseRange>({ lookback_days: 30 });
  const [preferences, setPreferences] = useState<PulsePreferences>({});
  // Account-wide (not per-channel) subset of preferences - text size,
  // creator journey, IRIS voice profile - via /api/account-preferences.
  // `preferences` above stays channel-scoped, now used for branding/media
  // kit fields only (display_name, bio, hero_url, headshot_url, logo_url,
  // accent, tracked_metrics).
  const [accountPreferences, setAccountPreferences] = useState<PulsePreferences>({});
  const [profile, setProfile] = useState<PulseProfile>({ display_name: null, handle: null, bio: null, avatar_url: null });
  const [profileReady, setProfileReady] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [instagramAvailable, setInstagramAvailable] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const metricsRequestRef = useRef(0);
  const typedInsightsRequestRef = useRef(0);
  const [savedLibraryTab, setSavedLibraryTab] = useState<SavedLibraryTab>("metrics");
  const [account, setAccount] = useState<AccountState>(() => readAccount(readPulseUserId()));
  const [accountDraft, setAccountDraft] = useState<AccountState>(() => readAccount(readPulseUserId()));
  const [channels, setChannels] = useState<CreatorPreview[]>(() => readChannels(readPulseUserId()));
  const [activeChannelId, setActiveChannelId] = useState<string>(() => readActiveChannelId(readPulseUserId()));
  const activeChannelIdRef = useRef(activeChannelId);
  activeChannelIdRef.current = activeChannelId;
  // accountPreview stays the read-facing name every existing call site already
  // uses - it's now derived from the channel list + active id instead of its
  // own state. setAccountPreview becomes an upsert into that list so none of
  // the existing write call sites need to change.
  const accountPreview = useMemo<CreatorPreview | null>(
    () => channels.find((c) => c.channel_id === activeChannelId) || channels[0] || null,
    [channels, activeChannelId],
  );
  const setAccountPreview = useCallback(
    (next: CreatorPreview | null | ((prev: CreatorPreview | null) => CreatorPreview | null)) => {
      setChannels((prevChannels) => {
        const currentActive = prevChannels.find((c) => c.channel_id === activeChannelIdRef.current) || prevChannels[0] || null;
        const resolved = typeof next === "function" ? (next as (prev: CreatorPreview | null) => CreatorPreview | null)(currentActive) : next;
        if (!resolved || !resolved.channel_id) return prevChannels;
        const idx = prevChannels.findIndex((c) => c.channel_id === resolved.channel_id);
        const nextChannels = idx === -1 ? [...prevChannels, resolved] : prevChannels.map((c, i) => (i === idx ? resolved : c));
        return nextChannels;
      });
    },
    [],
  );
  const [selectedGoals, setSelectedGoals] = useState<PulseGoalId[]>(() => readSetupGoals(readPulseUserId()));
  const [messagePrefs, setMessagePrefs] = useState<PulseMessagePrefs>(() => readMessagePrefs(readPulseUserId()));
  const [privacyPrefs, setPrivacyPrefs] = useState<PulsePrivacyPrefs>(() => readPrivacyPrefs(readPulseUserId()));
  const [irisMemoryEnabled, setIrisMemoryEnabled] = useState<boolean>(() => readIrisMemoryEnabled(readPulseUserId()));
  const [readingStep, setReadingStep] = useState(0);
  const [accountOpen, setAccountOpen] = useState(false);
  const [channelMenuAnchor, setChannelMenuAnchor] = useState<HTMLElement | null>(null);
  const [channelManageOpen, setChannelManageOpen] = useState(false);
  const [confirmDisconnectId, setConfirmDisconnectId] = useState<string | null>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const [accountView, setAccountView] = useState<"form" | "preview">("form");
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [oauthConnection, setOauthConnection] = useState<PulseYoutubeConnection | null>(null);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [reauthBannerDismissed, setReauthBannerDismissed] = useState(false);
  const [resyncState, setResyncState] = useState<"idle" | "syncing" | "synced" | "error">("idle");
  const [resyncError, setResyncError] = useState<string | null>(null);
  const [instagramConnection, setInstagramConnection] = useState<PulseInstagramConnection | null>(null);
  const [instagramOauthBusy, setInstagramOauthBusy] = useState(false);
  const [instagramOauthError, setInstagramOauthError] = useState<string | null>(null);
  const [chatDraft, setChatDraft] = useState("");
  const [chatMessages, setChatMessages] = useState<PulseChatMessage[]>(() => readChatHistory(readPulseUserId()));
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStatusMessage, setChatStatusMessage] = useState("IRIS is preparing context...");
  const [chatError, setChatError] = useState<string | null>(null);
  const [savedItems, setSavedItems] = useState<SavedIrisItem[]>([]);
  const [savedItemsLoading, setSavedItemsLoading] = useState(false);
  const [savedItemsError, setSavedItemsError] = useState<string | null>(null);
  const [savedActionKey, setSavedActionKey] = useState<string | null>(null);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocumentContext | null>(null);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [chartGenerating, setChartGenerating] = useState(false);
  const [irisPanelWidth, setIrisPanelWidth] = useState(() => readIrisPanelWidth());
  const [irisPanelOpen, setIrisPanelOpen] = useState(false);
  const [showStickyIris, setShowStickyIris] = useState(false);
  const [mediaActionMessage, setMediaActionMessage] = useState("");
  const [metricsReady, setMetricsReady] = useState<boolean>(() => {
    const seedUserId = readPulseUserId();
    const seedAccount = readAccount(seedUserId);
    const seedPreview = readAccountPreview(seedUserId);
    const hasSeed = Boolean(
      (seedPreview?.channel_id && String(seedPreview.channel_id).trim()) ||
        seedAccount.youtubeChannel.trim() ||
        seedAccount.creatorName.trim(),
    );
    return !hasSeed;
  });
  // Derived server-side summary of pulse_score/shorts_score.score_state across both
// surfaces ('no_data' = neither surface has hit its video-count gate yet) - drives the
// Pulse Score/Home no-data full-card takeover. Never computed locally from the two
// surfaces' own score_state so it can't drift from the backend's own definition.
const [channelDataState, setChannelDataState] = useState<"ready" | "low_data" | "no_data">("ready");
const [insightsMetrics, setInsightsMetrics] = useState<InsightsMetrics>(DEMO_INSIGHTS_METRICS);
  const [mediaMetrics, setMediaMetrics] = useState<MediaKitMetrics>(DEMO_MEDIA_KIT_METRICS);
  const [scorePlan, setScorePlan] = useState<PulseScorePlan | null>(DEMO_SCORE_PLAN);
  const [scorePlanLoading, setScorePlanLoading] = useState(false);
  const [scorePlanError, setScorePlanError] = useState<string | null>(null);
  const [typedInsights, setTypedInsights] = useState<PulseTypedInsight[]>([]);
  const [typedInsightsLoading, setTypedInsightsLoading] = useState(false);
  const [creatorMemory, setCreatorMemory] = useState<PulseCreatorMemory>({});
  const [creatorMemoryLoading, setCreatorMemoryLoading] = useState(false);
  const [irisRecommendation, setIrisRecommendation] = useState<PulseRecommendation | null>(DEMO_IRIS_RECOMMENDATION);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);
  const [widgetLoading, setWidgetLoading] = useState<Record<PulseMetricWidget, WidgetLoadState>>({
    header: { loading: false, error: null },
    pulse_score: { loading: false, error: null },
    engagement_rate: { loading: false, error: null },
    best_format: { loading: false, error: null },
    optimal_time: { loading: false, error: null },
    performance_trends: { loading: false, error: null },
    content_mix: { loading: false, error: null },
    topic_performance: { loading: false, error: null },
    audience_activity: { loading: false, error: null },
    media_kit: { loading: false, error: null },
  });
  const [widgetStatus, setWidgetStatus] = useState<Partial<Record<PulseMetricWidget, PulseWidgetStatus>>>({});
  const heroComposerRef = useRef<HTMLTextAreaElement>(null);
  const stickyComposerRef = useRef<HTMLTextAreaElement>(null);
  const panelComposerRef = useRef<HTMLTextAreaElement>(null);
  const documentFileInputRef = useRef<HTMLInputElement>(null);
  const workspaceScrollRef = useRef<HTMLElement | null>(null);
  const irisPanelBodyRef = useRef<HTMLDivElement | null>(null);
  const mediaActionTimerRef = useRef<number | null>(null);
  const hasCreatorAccount = Boolean(
    (accountPreview?.channel_id && String(accountPreview.channel_id).trim()) ||
      account.youtubeChannel.trim() ||
      account.creatorName.trim(),
  );
  const oauthConnected = Boolean(
    oauthConnection?.connected ||
      accountPreview?.oauth_connected,
  );
  // Reported by the backend on the channel list (GET /api/youtube/channels ->
  // status) whenever a channel's YouTube token is fully expired/revoked and
  // can't be silently refreshed - distinct from "not connected yet". Only
  // ever true when the backend actually says so; never inferred locally.
  // Account-wide (any connected channel, not just the currently-selected
  // one) so switching away from the broken channel doesn't hide the alert.
  const reauthChannels = useMemo(
    () => channels.filter((c) => c.oauth_status === "needs_reauth"),
    [channels],
  );
  const needsReauth = reauthChannels.length > 0;
  // A fresh reauth event (a different/additional channel losing its connection) should
  // re-surface the banner even if the user dismissed an earlier one for a prior channel.
  useEffect(() => {
    setReauthBannerDismissed(false);
  }, [reauthChannels.map((c) => c.channel_id).join(",")]);
  // Distinct from the account-wide `needsReauth` above - this one only
  // looks at the channel the resync button would actually act on, so a
  // healthy active channel isn't disabled just because a different,
  // inactive channel elsewhere in the account is broken.
  const activeChannelNeedsReauth = accountPreview?.oauth_status === "needs_reauth";
  const resyncLabel = activeChannelNeedsReauth
    ? "Reconnect needed"
    : resyncState === "syncing"
      ? "Syncing..."
      : resyncState === "synced"
        ? "Synced"
        : resyncState === "error"
          ? "Resync failed"
          : "Resync";
  const instagramOauthConnected = Boolean(instagramConnection?.connected);
  const irisSurfaceAvailable = true;
  const uploadedDocumentKnowledgebase = uploadedDocument?.context || "";
  const navigatePulse = (tab: Tab) => {
    setIrisPanelOpen(false);
    if (location.pathname !== TAB_PATHS[tab]) navigate(TAB_PATHS[tab]);
  };
  useEffect(() => {
    if (!irisPanelOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setIrisPanelOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [irisPanelOpen]);

  useEffect(() => {
    if (!pulseSessionReady) return;
    let cancelled = false;
    setPreferences({});
    setInstagramAvailable(false);
    pulseApi.get(`${API_BASE}/api/preferences`, { params: { channel_id: accountPreview?.channel_id || "" } })
      // The response is flat (PreferencesResponseDto directly, no wrapper) -
      // confirmed against the live backend. `data.preferences`/`data.capabilities`
      // never existed, so this was silently always resetting to {} on every
      // load - pre-existing bug, unrelated to multi-channel, found while
      // verifying the account-preferences contract for that feature.
      .then(({ data }) => { if (!cancelled) { setPreferences(data || {}); } })
      .catch(() => { if (!cancelled) setInstagramAvailable(false); });
    return () => { cancelled = true; };
  }, [pulseSessionReady, pulseUserId, accountPreview?.channel_id]);

  useEffect(() => {
    if (!pulseSessionReady) return;
    let cancelled = false;
    pulseApi.get<any>(`${API_BASE}/api/media-kit/channel`).then(({ data }) => {
      if (cancelled || !data) return;
      setAccountPreview((current) => ({
        ...current,
        found: true,
        query: current?.query || "",
        channel_id: data.channel_id || current?.channel_id || null,
        platform: data.platform || current?.platform || null,
        // YouTube's channels list is provider truth for the title; do not let
        // an old account/profile display-name preference replace it.
        title: data.name || current?.title || null,
        custom_url: data.handle || current?.custom_url || null,
        description: data.description ?? current?.description ?? null,
        thumbnail_url: data.avatar_url || current?.thumbnail_url || null,
        cover_url: data.cover_url || current?.cover_url || null,
      }));
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [pulseSessionReady, activeChannelId, setAccountPreview]);

  const savePreferences = async (patch: PulsePreferences) => {
    const { data } = await pulseApi.put(`${API_BASE}/api/preferences`, { channel_id: accountPreview?.channel_id || "", ...patch });
    setPreferences(data || {});
  };

  // Account-wide - not scoped by channel_id at all, so it only needs to
  // reload when the session (not the active channel) changes.
  useEffect(() => {
    if (!pulseSessionReady) return;
    let cancelled = false;
    pulseApi
      .get(`${API_BASE}/api/account-preferences`)
      .then(({ data }) => {
        if (cancelled) return;
        setAccountPreferences(data || {});
        // setup_goals used to live only in localStorage, which silently
        // reset on a cleared cache/new device (pulse-backend-00 root-caused
        // this as the "Choose Goals" screen reappearing after login even
        // though the user had already picked goals). The backend copy is
        // now the source of truth whenever it has one; localStorage stays
        // as a fast local cache, reconciled here rather than trusted blind.
        const serverGoals = Array.isArray(data?.setup_goals) ? (data.setup_goals as PulseGoalId[]) : [];
        if (serverGoals.length) {
          setSelectedGoals(serverGoals);
          saveSetupGoals(serverGoals, pulseUserId);
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [pulseSessionReady, pulseUserId]);

  const saveAccountPreferences = async (patch: PulsePreferences) => {
    const { data } = await pulseApi.put(`${API_BASE}/api/account-preferences`, patch);
    setAccountPreferences(data || {});
  };

  // Settings -> Appearance's +/- control on the same text_scale field the
  // Reading & IRIS dropdown already writes - both just patch account-preferences.
  const handleTextSizeStep = (direction: 1 | -1) => {
    const current = accountPreferences.text_scale || 1;
    const next = Math.min(
      TEXT_SCALE_MAX,
      Math.max(TEXT_SCALE_MIN, Math.round((current + direction * 0.1) * 10) / 10)
    );
    if (next === current) return;
    void saveAccountPreferences({ text_scale: next });
  };

  // Avatar URLs come back as a path relative to the backend's own origin
  // (e.g. "/api/uploads/avatars/<id>.jpg"), not the frontend's - resolving
  // it as-is against window.location (localhost:3000) 404s. Absolute URLs
  // (e.g. a future Google-SSO-seeded photo) pass through untouched.
  const resolveAvatarUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${API_BASE}${url.startsWith("/") ? url : `/${url}`}`;
  };
  const normalizeProfile = (data: PulseProfile | null | undefined): PulseProfile => ({
    display_name: data?.display_name ?? null,
    handle: data?.handle ?? null,
    bio: data?.bio ?? null,
    avatar_url: resolveAvatarUrl(data?.avatar_url),
  });

  // Account-level Pulse profile - independent of channel data, so it only
  // needs to reload when the session (not the active channel) changes.
  useEffect(() => {
    if (!pulseSessionReady) return;
    let cancelled = false;
    setProfileReady(false);
    pulseApi
      .get<PulseProfile>(`${API_BASE}/api/profile`)
      .then(({ data }) => { if (!cancelled) setProfile(normalizeProfile(data)); })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setProfileReady(true); });
    return () => { cancelled = true; };
  }, [pulseSessionReady, pulseUserId]);

  // Auto-fill display_name from the connected channel's title the first
  // time both are available, instead of leaving the user stuck on
  // "Creator" until they open Settings -> Profile and hit Save themselves
  // - the Name field there already prefills with this same channel title
  // as a placeholder, it just never actually got saved. Only fires once
  // per session and only when display_name is still empty, so it never
  // overwrites a name the user (or Google SSO seeding) already set - they
  // can still change it in Settings any time after.
  const autoFilledDisplayNameRef = useRef(false);
  useEffect(() => {
    if (!profileReady || autoFilledDisplayNameRef.current || profile.display_name) return;
    const channelTitle = String(accountPreview?.title || "").trim();
    if (!channelTitle) return;
    autoFilledDisplayNameRef.current = true;
    pulseApi
      .put<PulseProfile>(`${API_BASE}/api/profile`, { display_name: channelTitle })
      .then(({ data }) => setProfile(normalizeProfile(data)))
      .catch(() => { autoFilledDisplayNameRef.current = false; });
  }, [profileReady, profile.display_name, accountPreview?.title]);

  const saveProfile = async (patch: { display_name?: string; bio?: string }) => {
    setProfileSaving(true);
    setProfileError(null);
    try {
      const { data } = await pulseApi.put<PulseProfile>(`${API_BASE}/api/profile`, patch);
      setProfile(normalizeProfile(data));
    } catch {
      setProfileError("Could not save your profile right now.");
      throw new Error("profile-save-failed");
    } finally {
      setProfileSaving(false);
    }
  };

  const checkHandleAvailable = async (value: string): Promise<boolean> => {
    try {
      const { data } = await pulseApi.get<{ available: boolean }>(`${API_BASE}/api/profile/handle/available`, { params: { value } });
      return Boolean(data?.available);
    } catch {
      return true;
    }
  };

  const saveHandle = async (value: string) => {
    setProfileSaving(true);
    setProfileError(null);
    try {
      const { data } = await pulseApi.put<PulseProfile>(`${API_BASE}/api/profile/handle`, { handle: value });
      setProfile(normalizeProfile(data));
    } catch (err: any) {
      const taken = err?.response?.status === 409;
      setProfileError(taken ? "That handle is already taken." : "Could not update your handle right now.");
      throw err;
    } finally {
      setProfileSaving(false);
    }
  };

  const uploadProfileAvatar = async (file: File) => {
    setAvatarError(null);
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError("Image is too large. Use a JPEG, PNG or WebP under 2MB.");
      return;
    }
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await pulseApi.post<{ avatar_url: string }>(`${API_BASE}/api/profile/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Cache-bust: the upload always lands at the same path for this user
      // (<userId>.<ext>), so a plain re-fetch of that URL can show a stale
      // browser-cached image after re-uploading.
      const resolved = resolveAvatarUrl(data.avatar_url);
      setProfile((prev) => ({ ...prev, avatar_url: resolved ? `${resolved}?t=${Date.now()}` : resolved }));
    } catch {
      setAvatarError("Could not upload that image. Use a JPEG, PNG or WebP under 2MB.");
    } finally {
      setAvatarUploading(false);
    }
  };

  const refreshAccountPreviewRef = useRef<(seed: AccountState) => Promise<CreatorPreview | null>>(async () => null);

  const bootstrapPulseSession = useCallback(async (): Promise<string> => {
    setPulseSessionReady(false);
    setPulseSessionError(null);
    let pending = pulseSessionBootstrapPromise;
    if (!pending) {
      pending = (async () => {
        // Identity now comes from the JWT (AuthGate already guarantees a
        // valid access token exists before PulsePage mounts) - fetch the
        // authenticated user and use their id as the localStorage-scoping
        // key everything else in this file already keyed off pulseUserId.
        const user: AuthUser = await fetchCurrentUser();
        const nextUserId = String(user.id || "").trim();
        if (!nextUserId) throw new Error("Signed-in user response was incomplete.");
        savePulseUserId(nextUserId);
        return { pulseUserId: nextUserId, authUser: user };
      })();
      pulseSessionBootstrapPromise = pending;
    }
    try {
      const { pulseUserId: nextUserId, authUser } = await pending;
      setPulseUserId(nextUserId);
      setCurrentUser(authUser);
      if (typeof authUser.two_factor_enabled === "boolean") {
        setTwoFactorEnabled(authUser.two_factor_enabled);
      }
      const nextAccount = readAccount(nextUserId);
      const nextChannels = readChannels(nextUserId);
      const nextActiveId = readActiveChannelId(nextUserId);
      setAccount(nextAccount);
      setAccountDraft(nextAccount);
      setChannels(nextChannels);
      setActiveChannelId(nextActiveId);
      setSelectedGoals(readSetupGoals(nextUserId));
      setMessagePrefs(readMessagePrefs(nextUserId));
      setPrivacyPrefs(readPrivacyPrefs(nextUserId));
      setIrisMemoryEnabled(readIrisMemoryEnabled(nextUserId));
      setChatMessages(readChatHistory(nextUserId));
      let hasCreator = Boolean(
        nextChannels.some((c) => c.channel_id && String(c.channel_id).trim()) ||
          nextAccount.youtubeChannel.trim() ||
          nextAccount.creatorName.trim(),
      );
      // Always ask the server for the real channel list on every bootstrap,
      // not just when the local cache is empty - localStorage is a per-browser
      // instant-paint cache, but channels/active-channel are account-level
      // and change server-side (new channel added, active switched elsewhere,
      // a disconnect) whenever this doesn't happen to be the browser that made
      // the change. Using the cache only as a fallback meant an existing
      // session with any cached channel would never pick up server changes.
      try {
        const listResp = await pulseApi.get<YoutubeChannelSummary[]>(`${API_BASE}/api/youtube/channels`).catch(() => null);
        const rawChannels = Array.isArray(listResp?.data) ? listResp!.data!.filter((c) => c?.channel_id) : [];
        const serverChannels = rawChannels.map(youtubeChannelSummaryToPreview);
        if (serverChannels.length) {
          const activeRaw = rawChannels.find((c) => c.is_active) || rawChannels[0];
          const serverActiveId = String(activeRaw.channel_id || "").trim();
          setChannels(serverChannels);
          setActiveChannelId(serverActiveId);
          saveChannels(serverChannels, nextUserId);
          saveActiveChannelId(serverActiveId, nextUserId);
          hasCreator = true;
          // Kick off the public-stats (view_count/subscriber_count)
          // fetch right here with the server-confirmed active channel,
          // instead of only relying on the separate profile-sync effect
          // to notice accountPreview.channel_id changing later - that
          // depends on a render/commit having happened first. This fires
          // immediately with real server truth, so Total views has a shot
          // at landing on the very first paint instead of waiting for a
          // channel switch to trigger it.
          if (serverActiveId) {
            void refreshAccountPreviewRef.current({
              creatorName: activeRaw.title || "",
              youtubeChannel: serverActiveId,
            });
          }
        } else if (!hasCreator) {
          // New endpoint returned nothing and local cache was also empty -
          // fall back to the single-channel check that worked before
          // multi-channel existed, so this keeps functioning if the new
          // endpoint is ever unavailable.
          const { data: prefsData } = await pulseApi.get(`${API_BASE}/api/preferences`);
          const serverChannelId = String(prefsData?.channel_id || "").trim();
          if (serverChannelId) {
            const statusResp = await pulseApi
              .get<PulseYoutubeConnection>(`${API_BASE}/api/youtube/connection`, { params: { channel_id: serverChannelId } })
              .catch(() => null);
            if (statusResp?.data?.connected) {
              const hydratedPreview: CreatorPreview = { found: true, query: "", channel_id: serverChannelId, oauth_connected: true };
              setChannels([hydratedPreview]);
              setActiveChannelId(serverChannelId);
              saveChannels([hydratedPreview], nextUserId);
              saveActiveChannelId(serverChannelId, nextUserId);
              saveAccountPreview(hydratedPreview, nextUserId);
              hasCreator = true;
            }
          }
        }
      } catch {
        // Server unreachable - keep whatever the local cache already seeded
        // above rather than blocking the whole session on this call.
      }
      // "Onboarding done" used to mean "has a channel", which let a fresh
      // signup skip straight past the goals screen the instant OAuth
      // redirected back here (this bootstrap re-runs on that redirect and
      // saw hasCreator flip true before the user ever picked goals - a race
      // pulse-backend-00 flagged after wiping a stuck test account). Done
      // now also requires goals, checking the server (not just the
      // localStorage cache, which is empty for both "brand new" and
      // "goals already chosen on another device") whenever the local cache
      // doesn't already show them.
      let hasGoals = readSetupGoals(nextUserId).length >= 2;
      if (hasCreator && !hasGoals) {
        try {
          const { data: prefsData } = await pulseApi.get(`${API_BASE}/api/account-preferences`);
          const serverGoals = Array.isArray(prefsData?.setup_goals) ? (prefsData.setup_goals as PulseGoalId[]) : [];
          if (serverGoals.length) {
            hasGoals = true;
            setSelectedGoals(serverGoals);
            saveSetupGoals(serverGoals, nextUserId);
          }
        } catch {
          // Server unreachable - fall through treating goals as not chosen
          // yet, same as the channels lookup above.
        }
      }
      if (!hasCreator) {
        saveOnboardingStep("welcome");
        setOnboardingStep("welcome");
      } else if (!hasGoals) {
        saveOnboardingStep("goals");
        setOnboardingStep((prev) => (prev === "welcome" || prev === "connect" ? "goals" : prev));
      } else {
        saveOnboardingStep("done");
        setOnboardingStep((prev) => (prev === "welcome" ? "done" : prev));
      }
      setPulseSessionReady(true);
      return nextUserId;
    } catch (error) {
      setPulseSessionReady(false);
      setPulseSessionError(error instanceof Error ? error.message : "Pulse could not restore your saved session. Check the API server and retry.");
      throw error;
    } finally {
      if (pulseSessionBootstrapPromise === pending) {
        pulseSessionBootstrapPromise = null;
      }
    }
  }, []);

  useEffect(() => {
    void bootstrapPulseSession().catch(() => undefined);
  }, [bootstrapPulseSession]);

  // Keep the local cache in sync, and auto-pick an active channel (e.g. the
  // one just connected) whenever the list has entries but nothing is active.
  useEffect(() => {
    if (!pulseUserId) return;
    saveChannels(channels, pulseUserId);
    if (!activeChannelId && channels.length) {
      const firstId = String(channels[0].channel_id || "");
      if (firstId) setActiveChannelId(firstId);
      return;
    }
    saveActiveChannelId(activeChannelId, pulseUserId);
  }, [channels, activeChannelId, pulseUserId]);

  const initials = useMemo(() => {
    const raw = account.creatorName.trim();
    if (!raw) return "A";
    const parts = raw.split(/\s+/);
    return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  }, [account.creatorName]);

  const formatJoinedDate = (iso?: string | null) => {
    if (!iso) return "";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const setMediaActionToast = (message: string) => {
    setMediaActionMessage(message);
    if (mediaActionTimerRef.current !== null) {
      window.clearTimeout(mediaActionTimerRef.current);
    }
    mediaActionTimerRef.current = window.setTimeout(() => {
      setMediaActionMessage("");
      mediaActionTimerRef.current = null;
    }, 2800);
  };

  const handleMediaShare = async () => {
    const channelUrl = accountPreview?.url || account.youtubeChannel || "";
    const shareTitle = `${mediaMetrics.title} - Pulse`;
    const shareText = `${mediaMetrics.subtitle}\n${channelUrl ? `Channel: ${channelUrl}` : ""}`.trim();
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({
          title: shareTitle,
          text: shareText,
        });
        setMediaActionToast("Shared.");
        return;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText([shareTitle, shareText].filter(Boolean).join("\n"));
        setMediaActionToast("Summary copied to clipboard.");
        return;
      }
      setMediaActionToast("Share is not supported in this browser.");
    } catch {
      setMediaActionToast("Share was cancelled.");
    }
  };

  const handleMediaExportPdf = async () => {
    if (!accountPreview?.channel_id) {
      setMediaActionToast("Connect a channel before exporting a Media Kit.");
      return;
    }
    setMediaActionToast("Designing your Media Kit PDF...");
    try {
      const { data: access } = await pulseApi.get<{ share_url: string }>(`${API_BASE}/api/media-kit/access`, {
        params: { channel_id: accountPreview.channel_id },
      });
      const slug = access.share_url.split("/").filter(Boolean).pop();
      if (!slug) throw new Error("Share link is unavailable");
      const pulseHandle = profile.handle ? (profile.handle.startsWith("@") ? profile.handle : `@${profile.handle}`) : "@creator";
      const shareUrl = `${window.location.origin}/mk/${pulseHandle}/share/${slug}`;
      const absoluteAsset = (value?: string | null) => {
        if (!value || /^(data:|https?:|blob:)/i.test(value)) return value || "";
        return `${API_BASE.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
      };
      const rawHandle = String(accountPreview.custom_url || "").trim();
      await generateMediaKitPdf({
        name: String(accountPreview.title || account.creatorName || "Creator").trim() || "Creator",
        channelHandle: rawHandle ? (rawHandle.startsWith("@") ? rawHandle : `@${rawHandle}`) : "",
        email: preferences.media_kit_email || currentUser?.email || "",
        bio: preferences.bio || accountPreview.description || "",
        tags: preferences.media_kit_tags || ["Creator Systems", "Productivity", "SaaS Reviews"],
        platform: accountPreview.platform === "instagram" ? "instagram" : "youtube",
        avatarUrl: absoluteAsset(preferences.headshot_url || accountPreview.thumbnail_url),
        coverUrl: absoluteAsset(preferences.hero_url || accountPreview.cover_url),
        shareUrl,
        subscribers: mediaMetrics.followers || accountPreview.subscriber_count || null,
        totalViews: accountPreview.view_count ?? null,
        avgMonthlyReach: mediaMetrics.avg_monthly_reach > 0 ? mediaMetrics.avg_monthly_reach : null,
        engagementRate: mediaMetrics.engagement_rate > 0 ? mediaMetrics.engagement_rate : null,
        ages: mediaMetrics.audience_age_distribution,
        geographies: mediaMetrics.audience_geo_distribution,
        partnershipSummary: irisRecommendation?.summary || "",
        partnershipAction: irisRecommendation?.action || "",
        topContentSubtitle: mediaMetrics.top_content_subtitle,
        topContent: mediaMetrics.top_content,
        visibility: {
          subscribers: preferences.visible_metrics_subscribers !== false,
          totals: preferences.visible_metrics_avg_views !== false,
          engagement: preferences.visible_metrics_engagement !== false,
          demographics: preferences.visible_metrics_demographics !== false,
          partnership: preferences.visible_iris_partnership_fit !== false,
        },
      });
      setMediaActionToast("Media Kit PDF downloaded.");
    } catch (error) {
      console.error("Media Kit PDF export failed", error);
      setMediaActionToast("Could not generate the PDF. Please retry.");
    }
  };

  const handleDocumentUpload = useCallback(async (file: File | null | undefined) => {
    if (!file) return;
    setDocumentUploadError(null);
    const lowerName = file.name.toLowerCase();
    const supported =
      lowerName.endsWith(".csv") ||
      lowerName.endsWith(".pdf") ||
      file.type === "text/csv" ||
      file.type === "application/pdf" ||
      file.type === "application/vnd.ms-excel";
    if (!supported) {
      setDocumentUploadError("Upload a CSV or PDF file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setDocumentUploadError("File is too large. Keep it under 5 MB for this chat.");
      return;
    }
    setDocumentUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await pulseApi.post<UploadedDocumentContext & { ok: boolean; error?: string | null }>(
        `${API_BASE}/api/document_context`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!response.data?.ok || !response.data.context) {
        throw new Error(String(response.data?.error || "Could not extract document context."));
      }
      setUploadedDocument({
        name: String(response.data.name || file.name),
        size: Number(response.data.size || file.size),
        type: response.data.type === "pdf" ? "pdf" : "csv",
        row_count: response.data.row_count ?? null,
        column_count: response.data.column_count ?? null,
        page_count: response.data.page_count ?? null,
        extracted_chars: Number(response.data.extracted_chars || 0),
        context: String(response.data.context || ""),
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setDocumentUploadError(typeof detail === "string" ? detail : "Could not read that file.");
    } finally {
      setDocumentUploading(false);
      if (documentFileInputRef.current) {
        documentFileInputRef.current.value = "";
      }
    }
  }, []);

  const handleIrisResizeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = irisPanelWidth;
      const maxWidth = Math.min(IRIS_PANEL_WIDTH_MAX, Math.max(IRIS_PANEL_WIDTH_MIN, window.innerWidth - 420));
      const onMove = (moveEvent: MouseEvent) => {
        const nextWidth = Math.min(maxWidth, Math.max(IRIS_PANEL_WIDTH_MIN, startWidth + startX - moveEvent.clientX));
        setIrisPanelWidth(nextWidth);
      };
      const onUp = (upEvent: MouseEvent) => {
        const nextWidth = Math.min(maxWidth, Math.max(IRIS_PANEL_WIDTH_MIN, startWidth + startX - upEvent.clientX));
        setIrisPanelWidth(nextWidth);
        saveIrisPanelWidth(nextWidth);
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.classList.remove("pulse-resizing-iris");
      };
      document.body.classList.add("pulse-resizing-iris");
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [irisPanelWidth],
  );

  const refreshSavedItems = useCallback(async () => {
    if (!pulseSessionReady || !pulseUserId) {
      setSavedItems([]);
      return;
    }
    setSavedItemsLoading(true);
    setSavedItemsError(null);
    try {
      const response = await pulseApi.get<{ ok: boolean; items?: any[]; error?: string | null }>(
        `${API_BASE}/api/saved`,
        {
          params: {
            channel_id: accountPreview?.channel_id || undefined,
          },
        },
      );
      const items = Array.isArray(response.data?.items)
        ? (response.data.items.map(normalizeSavedIrisItem).filter(Boolean) as SavedIrisItem[])
        : [];
      setSavedItems(items);
    } catch {
      setSavedItemsError("Could not load saved IRIS items.");
    } finally {
      setSavedItemsLoading(false);
    }
  }, [accountPreview?.channel_id, pulseSessionReady, pulseUserId]);

  const saveIrisMessage = useCallback(
    async (type: SavedIrisItem["type"] | "auto", text: string, key: string) => {
      const body = String(text || "").trim();
      if (!pulseUserId || !body || body === DEFAULT_ASSISTANT_TEXT) return;
      setSavedActionKey(key);
      setSavedItemsError(null);
      try {
        const response = await pulseApi.post<{ ok: boolean; item?: any; error?: string | null }>(
          `${API_BASE}/api/saved`,
          {
            channel_id: accountPreview?.channel_id || undefined,
            // Omit type/category for an "auto" (plain chat Bookmark) save so the
            // backend classifies both from the real text instead of us guessing
            // "metric" and leaving category null - see conv-bookmark-feed.
            type: type === "auto" ? undefined : type,
            title: buildSavedIrisTitle(body, type === "auto" ? "metric" : type),
            body,
            source: "iris",
            pinned: false,
          },
        );
        const item = normalizeSavedIrisItem(response.data?.item);
        if (item) {
          setSavedItems((prev) => [item, ...prev.filter((existing) => existing.id !== item.id)]);
          navigatePulse("saved");
          setSavedLibraryTab(item.type === "metric" ? "metrics" : "actions");
        }
      } catch {
        setSavedItemsError("Could not save this IRIS item.");
      } finally {
        setSavedActionKey(null);
      }
    },
    [accountPreview?.channel_id, pulseUserId],
  );

  const generateAndSaveIrisChart = useCallback(
    async (latestQuestion: string, irisReply: string) => {
      const shouldAttemptChart =
        pulseChartRequested(latestQuestion) ||
        pulseChartRequested(irisReply) ||
        (pulseChartFollowUpRequested(latestQuestion) && pulseChartRequested(irisReply));
      if (!pulseUserId || !shouldAttemptChart) return;
      setChartGenerating(true);
      setChatStatusMessage("IRIS is drawing a chart...");
      try {
        const chartIntentText = `${latestQuestion}\n${irisReply}`.toLowerCase();
        const preferredKind = chartIntentText.includes("pie")
          ? "pie"
          : chartIntentText.includes("line")
            ? "line"
            : "auto";
        const chartResponse = await pulseApi.post<{
          ok: boolean;
          should_save: boolean;
          title?: string | null;
          body?: string | null;
          chart?: any;
          error?: string | null;
        }>(`${API_BASE}/api/chart`, {
          creator_name: account.creatorName || undefined,
          creator_channel_url: account.youtubeChannel || undefined,
          creator_channel_id: accountPreview?.channel_id || undefined,
          latest_question: latestQuestion,
          iris_reply: irisReply,
          document_context: uploadedDocumentKnowledgebase || undefined,
          preferred_kind: preferredKind,
        });
        if (!chartResponse.data?.ok || !chartResponse.data?.should_save) {
          const reason = String(chartResponse.data?.error || "").trim();
          setChatMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              text:
                reason ||
                "I could not generate a reliable chart from the available YouTube data yet. Try asking for a public proxy chart like \"views by upload month,\" or reconnect OAuth and retry.",
            },
          ]);
          return;
        }
        const chart = normalizePulseChartSpec(chartResponse.data.chart);
        if (!chart) {
          setChatMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              text: "I found chart data, but the chart artifact was not valid enough to save. Please retry with a more specific chart type like line or pie.",
            },
          ]);
          return;
        }
        const title = String(chartResponse.data.title || chart.title || "IRIS generated chart").trim();
        const rawBody = String(chartResponse.data.body || "").trim();
        const body = [
          rawBody || "IRIS automatically generated this chart and saved it to Saved Metrics.",
          `Prompt that generated it: "${latestQuestion}"`,
        ].join("\n\n");
        let savedOk = false;
        try {
          const savedResponse = await pulseApi.post<{ ok: boolean; item?: any }>(
            `${API_BASE}/api/saved`,
            {
              channel_id: accountPreview?.channel_id || undefined,
              type: "metric",
              title,
              body,
              source: "iris_chart",
              pinned: false,
              chart,
            },
          );
          const savedItem = normalizeSavedIrisItem(savedResponse.data?.item);
          if (savedItem) {
            savedOk = true;
            setSavedItems((prev) => [savedItem, ...prev.filter((existing) => existing.id !== savedItem.id)]);
            setSavedLibraryTab("metrics");
          }
        } catch {
          setSavedItemsError("Generated the chart preview, but could not save it to Saved Metrics.");
        }
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: savedOk
              ? "Chart generated. I saved it to Saved Metrics."
              : "Chart generated as a preview, but I could not save it to Saved Metrics.",
            chart,
          },
        ]);
      } catch {
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: "I tried to generate a chart, but could not create a reliable chart artifact from the available numbers.",
          },
        ]);
      } finally {
        setChartGenerating(false);
        setChatStatusMessage("IRIS is ready.");
      }
    },
    [
      account.creatorName,
      account.youtubeChannel,
      accountPreview?.channel_id,
      pulseUserId,
      uploadedDocumentKnowledgebase,
    ],
  );

  const toggleSavedPinned = useCallback(
    async (item: SavedIrisItem) => {
      if (!pulseUserId) return;
      const channelId = String(accountPreview?.channel_id || "").trim();
      const nextPinned = !item.pinned;
      setSavedItems((prev) => prev.map((existing) => (existing.id === item.id ? { ...existing, pinned: nextPinned } : existing)));
      try {
        // A Saved Action doesn't just "pin to Insights" like a metric - toggling
        // it on genuinely commits a tracked move (POST /moves), and toggling it
        // off abandons that move (DELETE), same as IRIS's native moves. See the
        // Bookmark-parity backend spec in conv-bookmark-purpose.
        if (item.type === "action" && channelId) {
          if (nextPinned) {
            const response = await pulseApi.post<{ ok: boolean; move?: any; error?: string | null }>(
              `${API_BASE}/api/moves`,
              { channel_id: channelId, saved_item_id: item.id, title: item.title, detail: item.body },
            );
            const move = normalizePulseScorePlanItem(response.data?.move);
            if (!move) throw new Error(response.data?.error || "Could not add this move.");
            setScorePlan((prev) => prev
              ? { ...prev, items: [move, ...prev.items.filter((existing) => existing.id !== move.id)] }
              : { title: "This week's moves", subtitle: "", potential_score_lift: 0, items: [move] });
          } else {
            const linkedMove = scorePlan?.items?.find((existing) => existing.saved_item_id === item.id && existing.origin === "manual");
            if (linkedMove?.id) {
              await pulseApi.delete(`${API_BASE}/api/moves/${encodeURIComponent(linkedMove.id)}`, { params: { channel_id: channelId } });
              setScorePlan((prev) => prev ? { ...prev, items: prev.items.filter((existing) => existing.id !== linkedMove.id) } : prev);
            }
          }
        }
        const response = await pulseApi.patch<{ ok: boolean; item?: any }>(
          `${API_BASE}/api/saved/${encodeURIComponent(item.id)}`,
          {
            channel_id: accountPreview?.channel_id || undefined,
            pinned: nextPinned,
          },
        );
        const updated = normalizeSavedIrisItem(response.data?.item);
        if (updated) {
          setSavedItems((prev) => prev.map((existing) => (existing.id === updated.id ? updated : existing)));
        }
      } catch {
        setSavedItems((prev) => prev.map((existing) => (existing.id === item.id ? item : existing)));
        setSavedItemsError("Could not update that saved item.");
      }
    },
    [accountPreview?.channel_id, pulseUserId, scorePlan],
  );

  const completeManualMove = useCallback(async (item: PulseScorePlanItem) => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!item.id || !pulseUserId || !channelId || item.origin !== "manual" || item.status !== "pending") return;
    setScorePlanError(null);
    try {
      const response = await pulseApi.post<{ ok: boolean; move?: any; error?: string | null }>(
        `${API_BASE}/api/moves/${encodeURIComponent(item.id)}/complete`,
        { channel_id: channelId },
      );
      const updated = normalizePulseScorePlanItem(response.data?.move);
      if (!updated) throw new Error(response.data?.error || "Move could not be marked done.");
      setScorePlan((prev) => prev ? {
        ...prev,
        items: prev.items.map((existing) => existing.id === updated.id ? updated : existing),
      } : prev);
    } catch (error: any) {
      setScorePlanError(String(error?.response?.data?.detail || error?.message || "Could not mark this move done."));
    }
  }, [accountPreview?.channel_id, pulseUserId]);

  const deleteSavedItem = useCallback(
    async (item: SavedIrisItem) => {
      if (!pulseUserId) return;
      const previous = savedItems;
      setSavedItems((prev) => prev.filter((existing) => existing.id !== item.id));
      try {
        await pulseApi.delete(`${API_BASE}/api/saved/${encodeURIComponent(item.id)}`, {
          params: {},
        });
      } catch {
        setSavedItems(previous);
        setSavedItemsError("Could not delete that saved item.");
      }
    },
    [pulseUserId, savedItems],
  );

  const openAccountDialog = () => {
    setAccountDraft(account);
    setAccountError(null);
    setOauthError(null);
    const hasSaved = Boolean(account.creatorName.trim() || account.youtubeChannel.trim());
    setAccountView(hasSaved && accountPreview ? "preview" : "form");
    setAccountOpen(true);
    if (hasSaved && accountPreview && !accountPreview.thumbnail_url) {
      void refreshAccountPreview(account);
    }
  };

  const handleOnboardingGetStarted = async () => {
    try {
      const nextUserId = pulseUserId || await bootstrapPulseSession();
      if (!nextUserId) return;
      setOnboardingStep("connect");
      saveOnboardingStep("connect");
    } catch {
      // The welcome screen displays pulseSessionError and remains safely blocked.
    }
  };

  const handleOnboardingConnectYoutube = () => {
    if (oauthConnected) return;
    // No name/URL search step anymore - straight to Google's account
    // picker, same as adding any other channel from the sidebar.
    void handleAddChannel();
  };

  const handleOnboardingContinue = () => {
    if (!oauthConnected) return;
    setOnboardingStep("goals");
    saveOnboardingStep("goals");
  };

  const handleToggleGoal = (goalId: PulseGoalId) => {
    setSelectedGoals((prev) => {
      const exists = prev.includes(goalId);
      const next = exists ? prev.filter((item) => item !== goalId) : prev.length >= 5 ? prev : [...prev, goalId];
      saveSetupGoals(next, pulseUserId);
      return next;
    });
  };

  const handleGoalsContinue = () => {
    if (selectedGoals.length < 2) return;
    saveSetupGoals(selectedGoals, pulseUserId);
    // Backend contract (pulse-backend-00): PUT /api/account-preferences now
    // accepts setup_goals - localStorage alone used to silently reset the
    // goal picker on a cleared cache/new device, so this call makes the
    // backend copy authoritative rather than a cache-only value.
    void saveAccountPreferences({ setup_goals: selectedGoals }).catch(() => undefined);
    setReadingStep(0);
    setOnboardingStep("reading");
  };

  // Settings -> Manage IRIS's goal picker is dirty-gated (edits a draft,
  // only persists on "Save changes"), unlike onboarding's goal picker which
  // saves on every click - this is the commit step for that draft.
  const handleSaveGoalsFromSettings = (goals: PulseGoalId[]) => {
    saveSetupGoals(goals, pulseUserId);
    setSelectedGoals(goals);
    void saveAccountPreferences({ setup_goals: goals }).catch(() => undefined);
  };

  const handleToggleMessagePref = (key: keyof PulseMessagePrefs) => {
    setMessagePrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveMessagePrefs(next, pulseUserId);
      return next;
    });
  };

  const handleTogglePrivacyPref = (key: keyof PulsePrivacyPrefs) => {
    setPrivacyPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      savePrivacyPrefs(next, pulseUserId);
      return next;
    });
  };

  const handleToggleIrisMemory = () => {
    setIrisMemoryEnabled((prev) => {
      const next = !prev;
      saveIrisMemoryEnabled(next, pulseUserId);
      return next;
    });
  };

  const handlePulseLogout = () => {
    if (typeof window !== "undefined" && pulseUserId) {
      window.localStorage.removeItem(scopedPulseKey(ACCOUNT_STORAGE_KEY, pulseUserId));
      window.localStorage.removeItem(scopedPulseKey(ACCOUNT_PREVIEW_STORAGE_KEY, pulseUserId));
      window.localStorage.removeItem(scopedPulseKey(CHAT_STORAGE_KEY, pulseUserId));
      window.localStorage.removeItem(scopedPulseKey(GOALS_STORAGE_KEY, pulseUserId));
    }
    savePulseUserId("");
    saveOnboardingStep("welcome");
    // Revoke the refresh token and clear access/refresh tokens, then send
    // the browser to /login. Full navigation (not react-router) is
    // deliberate here - a real sign-out should drop all in-memory state.
    void authLogout().finally(() => {
      window.location.assign("/login");
    });
  };

  // Backend contract (pulse-backend-00, feature/account-profile):
  // PUT /api/account/password { current_password, new_password } -> revokes
  // every other session on success. 400 if the account has no password yet
  // (Google-only), 401 if current_password is wrong.
  const handleChangePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (passwordChangeBusy) return false;
    setPasswordChangeBusy(true);
    setPasswordChangeError(null);
    setPasswordChangeMessage(null);
    try {
      await pulseApi.put(`${API_BASE}/api/account/password`, { current_password: currentPassword, new_password: newPassword });
      setPasswordChangeMessage("Password updated. You've been signed out of other sessions.");
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400) {
        setPasswordChangeError("Your account doesn't have a password yet - it's signed in with Google.");
      } else if (status === 401) {
        setPasswordChangeError("Current password is incorrect.");
      } else {
        setPasswordChangeError("Couldn't update your password. Try again in a moment.");
      }
      return false;
    } finally {
      setPasswordChangeBusy(false);
    }
  };

  // Backend contract (pulse-backend-00, feature/account-profile):
  // POST /api/account/delete-request { password? } -> soft delete. Sets
  // pending_deletion_at and revokes all refresh tokens (signs out
  // everywhere within the access token's ~15min TTL). Auto-restored on the
  // next successful login/google - no separate restore step. password is
  // required whenever the account has one (401 wrong password, 403 if
  // omitted), optional for Google-only accounts.
  const handleRequestAccountDeletion = async (password?: string): Promise<boolean> => {
    if (deleteAccountBusy) return false;
    setDeleteAccountBusy(true);
    setDeleteAccountError(null);
    try {
      await pulseApi.post(`${API_BASE}/api/account/delete-request`, password ? { password } : {});
      handlePulseLogout();
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setDeleteAccountError("Incorrect password.");
      } else if (status === 403) {
        setDeleteAccountError("Password is required to delete this account.");
      } else {
        setDeleteAccountError("Couldn't start account deletion. Try again in a moment.");
      }
      setDeleteAccountBusy(false);
      return false;
    }
  };

  // Backend contract (pulse-backend, PB-013): POST /api/account/2fa/enable ->
  // {secret, otpauth_url}. Doesn't flip twoFactorEnabled server-side yet -
  // POST /api/account/2fa/verify does that once the code is proven.
  const handleEnable2fa = async (): Promise<{ secret: string; otpauth_url: string } | null> => {
    if (twoFactorBusy) return null;
    setTwoFactorBusy(true);
    try {
      const { data } = await pulseApi.post<{ secret: string; otpauth_url: string }>(`${API_BASE}/api/account/2fa/enable`);
      return data;
    } catch {
      return null;
    } finally {
      setTwoFactorBusy(false);
    }
  };

  // POST /api/account/2fa/verify {code} -> {ok:true, recovery_codes?:string[]}.
  // recovery_codes is optional in the type until the backend lands it - the
  // UI only shows the "save these" step when it's actually present.
  const handleVerify2fa = async (code: string): Promise<{ ok: true; recovery_codes?: string[] } | null> => {
    if (twoFactorBusy) return null;
    setTwoFactorBusy(true);
    try {
      const { data } = await pulseApi.post<{ ok: true; recovery_codes?: string[] }>(`${API_BASE}/api/account/2fa/verify`, { code });
      setTwoFactorEnabled(true);
      return data;
    } catch {
      return null;
    } finally {
      setTwoFactorBusy(false);
    }
  };

  // POST /api/account/2fa/disable {code} (TOTP or a recovery code).
  const handleDisable2fa = async (code: string): Promise<boolean> => {
    if (twoFactorBusy) return false;
    setTwoFactorBusy(true);
    try {
      await pulseApi.post(`${API_BASE}/api/account/2fa/disable`, { code });
      setTwoFactorEnabled(false);
      return true;
    } catch {
      return false;
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const refreshAccountPreview = async (seed: AccountState): Promise<CreatorPreview | null> => {
    const creatorName = seed.creatorName.trim();
    const creatorChannel = seed.youtubeChannel.trim();
    if (!creatorName && !creatorChannel) return null;
    try {
      const response = await pulseApi.post<CreatorPreview>(`${API_BASE}/api/creator_preview`, {
        creator_name: creatorName || undefined,
        creator_channel_url: creatorChannel || undefined,
      });
        const preview = response.data;
      if (preview?.found) {
        // /api/creator_preview is a public-metadata lookup (avatar, title,
        // subscriber counts). It does echo back an oauth_status/oauth_connected
        // pair, but that pair only reflects "was this ever connected", not
        // live reauth state - /api/youtube/channels (consumed in
        // bootstrapPulseSession) is the authoritative source for that, same
        // as the resync-triggered needs_reauth transitions below. Spreading
        // preview's oauth fields on top here was clobbering a real
        // needs_reauth status with creator_preview's stale "connected",
        // which silently hid the re-auth banner. Merge onto the existing
        // record but keep oauth_connected/oauth_status from whatever is
        // already known (same pattern syncMediaKitChannel below already
        // uses for its own fields) so this public lookup can't regress them.
        // Merge against the CURRENT channels state at update time, not the
        // `accountPreview` closed over when this function was defined - this
        // function can be called (via refreshAccountPreviewRef) immediately
        // after a setChannels() from the server in the same tick, before
        // React has re-rendered and refreshed that closure.
        let merged: CreatorPreview | null = null;
        setAccountPreview((prev) => {
          merged = {
            ...prev,
            ...preview,
            oauth_connected: prev?.oauth_connected ?? preview.oauth_connected,
            oauth_status: prev?.oauth_status ?? preview.oauth_status,
          };
          // Sticky last-known value: once view_count/subscriber_count has a
          // real number, a later fetch that resolves the channel but returns
          // null/missing stats (a momentary upstream hiccup, not a real "no
          // data") must not blank an already-good number back to "--". "--"
          // is only for "never successfully fetched yet."
          if (typeof preview.view_count !== "number" && typeof prev?.view_count === "number") {
            merged.view_count = prev.view_count;
          }
          if (typeof preview.subscriber_count !== "number" && typeof prev?.subscriber_count === "number") {
            merged.subscriber_count = prev.subscriber_count;
          }
          return merged;
        });
        if (merged) saveAccountPreview(merged, pulseUserId);
        if (preview.channel_id) {
          try {
            const statusResp = await pulseApi.get<PulseYoutubeConnection>(
              `${API_BASE}/api/youtube/connection`,
              { params: { channel_id: preview.channel_id } },
            );
            setOauthConnection(statusResp.data);
          } catch {
            setOauthConnection(null);
          }
        }
        return preview;
      }
      return null;
    } catch {
      return null;
    }
  };
  // bootstrapPulseSession is a useCallback([]) that only ever closes over
  // the FIRST render's refreshAccountPreview - calling refreshAccountPreview
  // directly from inside it would use a permanently-stale accountPreview.
  // A ref sidesteps that: always reassigned to the latest closure here,
  // read at call time from inside the stable callback below.
  refreshAccountPreviewRef.current = refreshAccountPreview;

  const syncMediaKitChannel = async (): Promise<CreatorPreview | null> => {
    try {
      const response = await pulseApi.post<any>(`${API_BASE}/api/media-kit/channel/sync`, {
        channel_id: accountPreview?.channel_id || undefined,
      });
      const raw = response.data?.provider || response.data?.channel || response.data;
      if (!raw || !(raw.channel_id || raw.name || raw.title)) throw new Error("Channel sync returned no channel.");
      const synced: CreatorPreview = {
        ...accountPreview,
        found: true,
        query: accountPreview?.query || "",
        channel_id: raw.channel_id || accountPreview?.channel_id || null,
        platform: raw.platform || accountPreview?.platform || "youtube",
        title: raw.name || raw.title || accountPreview?.title || null,
        custom_url: raw.handle || raw.custom_url || accountPreview?.custom_url || null,
        description: raw.description ?? raw.bio ?? accountPreview?.description ?? null,
        thumbnail_url: raw.avatar_url || raw.thumbnail_url || accountPreview?.thumbnail_url || null,
        cover_url: raw.cover_url || accountPreview?.cover_url || null,
      };
      setAccountPreview(synced);
      saveAccountPreview(synced, pulseUserId);
      return synced;
    } catch (error) {
      // The dedicated sync endpoint is authoritative. In particular, a 409
      // means the provider refresh failed and the backend deliberately kept
      // its cached values unchanged; surface that failure without falling
      // back to another endpoint or replacing the current UI state.
      throw error;
    }
  };

  // Keep the cached channel profile (avatar, title, subscriber/view/video
  // counts) in sync with what's actually on YouTube, instead of only ever
  // refreshing it once at connect time and caching it indefinitely. Runs
  // once as soon as a channel is connected, then on a recurring interval
  // for as long as the app stays open - picks up e.g. a changed avatar or
  // channel name without the creator needing to reconnect.
  useEffect(() => {
    if (!pulseSessionReady || !accountPreview?.channel_id) return;
    const PROFILE_SYNC_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
    const INITIAL_RETRY_DELAY_MS = 2000;
    const INITIAL_MAX_ATTEMPTS = 5; // ~2+4+6+8+10s of retrying, well under a minute
    let cancelled = false;
    const syncProfile = async (attempt = 0): Promise<void> => {
      // Root cause of "Total views shows -- until a channel switch": this
      // seed used to try account.youtubeChannel FIRST - a single freeform
      // localStorage string left over from the old single-channel
      // onboarding form, e.g. a stale demo placeholder ("DEMO-pulse-
      // showcase-channel" was observed live in the backend logs). That
      // string matches no channel-id/handle/URL pattern, so resolveChannel
      // fell through to YouTube's free-text search API - which was
      // separately hitting its own exhausted daily quota. accountPreview's
      // own channel_id (a real UC... id, resolved via the cheap direct
      // channels.list lookup, a different quota bucket) is both more
      // correct AND cheaper, so it goes first now. Also refuse to use
      // account.youtubeChannel at all unless it actually looks like a
      // channel reference, so a stale non-channel string can't poison this
      // again even as a last-resort fallback.
      const storedChannelRef = account.youtubeChannel.trim();
      const plausibleStoredChannelRef = /^(UC[\w-]{20,}|@[\w.-]{2,}|https?:\/\/(www\.)?youtube\.com\/)/i.test(storedChannelRef)
        ? storedChannelRef
        : "";
      const result = await refreshAccountPreview({
        creatorName: account.creatorName || accountPreview.title || "",
        youtubeChannel: accountPreview.channel_id || accountPreview.url || plausibleStoredChannelRef || "",
      });
      // This is the only source for view_count/subscriber_count on Media
      // Kit (not part of /api/metrics/all). A single failed or incomplete
      // attempt used to leave those fields undefined until the next 30-min
      // tick or a manual channel switch, which is what looked like "shows
      // -- on first load, only fills in after switching channels." Keep
      // retrying with backoff (not just once) until either a real
      // view_count lands or the attempt budget runs out - the interval
      // still covers picking up later legitimate changes.
      const landed = Boolean(result) && typeof result?.view_count === "number";
      if (!cancelled && !landed && attempt < INITIAL_MAX_ATTEMPTS - 1) {
        window.setTimeout(() => { if (!cancelled) void syncProfile(attempt + 1); }, INITIAL_RETRY_DELAY_MS * (attempt + 1));
      }
    };
    void syncProfile();
    const intervalId = window.setInterval(() => void syncProfile(), PROFILE_SYNC_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(intervalId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseSessionReady, accountPreview?.channel_id]);

  const refreshOauthConnection = useCallback(async (channelId: string) => {
    const clean = String(channelId || "").trim();
    if (!clean) {
      setOauthConnection(null);
      return;
    }
    try {
      const resp = await pulseApi.get<PulseYoutubeConnection>(`${API_BASE}/api/youtube/connection`, {
        params: { channel_id: clean },
      });
      setOauthConnection(resp.data);
      setOauthError(null);
    } catch {
      setOauthConnection(null);
    }
  }, []);

  const refreshInstagramConnection = useCallback(async () => {
    if (!pulseUserId) {
      setInstagramConnection(null);
      return;
    }
    try {
      const response = await pulseApi.get<PulseInstagramConnection>(
        `${API_BASE}/api/instagram/connection`,
        { params: {} },
      );
      setInstagramConnection(response.data);
      setInstagramOauthError(null);
    } catch {
      setInstagramConnection(null);
    }
  }, [pulseUserId]);

  const handleConnectYoutubeOAuth = async (targetChannelId?: string) => {
    const channelId = String(targetChannelId || accountPreview?.channel_id || "").trim();
    if (!channelId || oauthBusy) return;
    setOauthBusy(true);
    setOauthError(null);
    try {
      const response = await pulseApi.get<{
        ok: boolean;
        channel_id?: string | null;
        connect_url?: string | null;
        error?: string | null;
      }>(`${API_BASE}/api/youtube/connect`, {
        params: {
          creator_channel_id: channelId,
          return_url: window.location.href,
        },
      });
      if (!response.data?.ok || !response.data?.connect_url) {
        throw new Error(String(response.data?.error || "OAuth connect URL unavailable."));
      }
      window.location.assign(String(response.data.connect_url));
    } catch (error: any) {
      setOauthError(error?.message || "Could not start YouTube OAuth.");
      setOauthBusy(false);
    }
  };

  const handleDisconnectYoutubeOAuth = async () => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!channelId || oauthBusy) return;
    setOauthBusy(true);
    setOauthError(null);
    try {
      await pulseApi.post(`${API_BASE}/api/youtube/disconnect`, {
        channel_id: channelId,
      });
      setOauthConnection({
        ok: true,
        channel_id: channelId,
        connected: false,
        status: "disconnected",
      });
      setAccountPreview((prev) => (prev ? { ...prev, oauth_connected: false, oauth_status: "disconnected" } : prev));
    } catch {
      setOauthError("Could not disconnect YouTube OAuth.");
    } finally {
      setOauthBusy(false);
    }
  };

  // Disconnects one channel from the account's channel list (as opposed to
  // handleDisconnectYoutubeOAuth, which only ever acted on the single active
  // channel). Backend auto-promotes another connected channel to active if
  // the disconnected one was active - mirror that locally too.
  // POST /api/youtube/disconnect IS the delete - Jim confirmed there's no
  // separate delete route, and this endpoint now fully purges the channel
  // (daily/video/dimension/cache data), not just a token revoke. Reconnect
  // means adding the channel again and reauthenticating from scratch.
  const handleDisconnectChannel = async (channelId: string) => {
    const cleanId = String(channelId || "").trim();
    if (!cleanId || oauthBusy) return;
    setOauthBusy(true);
    setOauthError(null);
    try {
      await pulseApi.post(`${API_BASE}/api/youtube/disconnect`, { channel_id: cleanId });
      setChannels((prev) => {
        const remaining = prev.filter((c) => c.channel_id !== cleanId);
        if (cleanId === activeChannelIdRef.current) {
          setActiveChannelId(remaining[0]?.channel_id ? String(remaining[0].channel_id) : "");
        }
        return remaining;
      });
    } catch {
      setOauthError("Could not delete that channel.");
    } finally {
      setOauthBusy(false);
    }
  };

  // Adds a new channel to the account (as opposed to handleConnectYoutubeOAuth,
  // which re-verifies the single already-known channel). No creator_channel_id
  // is sent - pulse-backend resolves whichever channel the Google grant covers
  // and adds it to the account's channel list, per the multi-channel contract
  // in newPlan.md. Errors here are expected until that endpoint ships.
  const handleAddChannel = async () => {
    if (oauthBusy) return;
    setChannelMenuAnchor(null);
    setOauthBusy(true);
    setOauthError(null);
    try {
      const response = await pulseApi.get<{
        ok: boolean;
        connect_url?: string | null;
        error?: string | null;
      }>(`${API_BASE}/api/youtube/connect`, {
        params: { return_url: window.location.href },
      });
      if (!response.data?.ok || !response.data?.connect_url) {
        throw new Error(String(response.data?.error || "OAuth connect URL unavailable."));
      }
      window.location.assign(String(response.data.connect_url));
    } catch (error: any) {
      setOauthError(error?.message || "Could not start YouTube OAuth.");
      setOauthBusy(false);
    }
  };

  const handleSwitchActiveChannel = (channelId: string) => {
    const cleanId = String(channelId || "").trim();
    if (!cleanId || cleanId === activeChannelId) {
      setChannelMenuAnchor(null);
      return;
    }
    setActiveChannelId(cleanId);
    setChannelMenuAnchor(null);
    // Best-effort - keeps the active channel consistent across
    // devices/browsers once pulse-backend ships this endpoint; falls back
    // to the localStorage-only selection (already applied above) until then.
    void pulseApi.post(`${API_BASE}/api/youtube/channels/active`, { channel_id: cleanId }).catch(() => undefined);
  };

  const handleConnectInstagramOAuth = async () => {
    if (!pulseUserId || instagramOauthBusy) return;
    setInstagramOauthBusy(true);
    setInstagramOauthError(null);
    try {
      const response = await pulseApi.get<{
        ok: boolean;
        connect_url?: string | null;
        error?: string | null;
      }>(`${API_BASE}/api/instagram/connect`, {
        params: {
          return_url: window.location.href,
        },
      });
      if (!response.data?.ok || !response.data?.connect_url) {
        throw new Error(String(response.data?.error || "Instagram OAuth connect URL unavailable."));
      }
      window.location.assign(String(response.data.connect_url));
    } catch (error: any) {
      setInstagramOauthError(error?.message || "Could not start Instagram OAuth.");
      setInstagramOauthBusy(false);
    }
  };

  const handleDisconnectInstagramOAuth = async () => {
    if (!pulseUserId || instagramOauthBusy) return;
    setInstagramOauthBusy(true);
    setInstagramOauthError(null);
    try {
      await pulseApi.post(`${API_BASE}/api/instagram/disconnect`, {
      });
      setInstagramConnection((previous) => ({
        ok: true,
        connected: false,
        status: "disconnected",
        account_id: previous?.account_id,
        username: previous?.username,
      }));
    } catch {
      setInstagramOauthError("Could not disconnect Instagram OAuth.");
    } finally {
      setInstagramOauthBusy(false);
    }
  };

  const handleAccountSave = async () => {
    if (accountSaving) return;
    const creatorName = accountDraft.creatorName.trim();
    const creatorChannel = accountDraft.youtubeChannel.trim();
    if (!creatorName && !creatorChannel) {
      setAccountError("Add creator name or YouTube channel.");
      return;
    }
    setAccountSaving(true);
    setAccountError(null);
    try {
      const preview = await refreshAccountPreview({ creatorName, youtubeChannel: creatorChannel });
      if (!preview?.found) {
        setAccountError("Could not resolve this creator on YouTube. Check the URL or handle.");
        return;
      }
      const nextAccount = { creatorName, youtubeChannel: creatorChannel };
      setAccount(nextAccount);
      saveAccount(nextAccount, pulseUserId);
      const { data } = await pulseApi.put(`${API_BASE}/api/preferences`, { channel_id: preview.channel_id || "", display_name: creatorName });
      setPreferences(data || {});
      setAccountView("preview");
    } catch {
      setAccountError("Could not load creator profile preview right now.");
    } finally {
      setAccountSaving(false);
    }
  };

  const markWidgetLoading = (widget: PulseMetricWidget, loading: boolean, error: string | null = null) => {
    setWidgetLoading((prev) => ({
      ...prev,
      [widget]: { loading, error },
    }));
  };

  const applyWidgetData = (widget: PulseMetricWidget, data: Record<string, any>) => {
    if (widget === "header") {
      setInsightsMetrics((prev) => ({
        ...prev,
        header: {
          title: String(data?.title || prev.header.title),
          subtitle: String(data?.subtitle || prev.header.subtitle),
          growth_badge: String(data?.growth_badge || prev.header.growth_badge),
        },
      }));
      return;
    }
    if (widget === "pulse_score") {
      setInsightsMetrics((prev) => ({
        ...prev,
        pulse_score: {
          score: Number(data?.score ?? prev.pulse_score.score),
          confidence: data?.confidence,
          score_state: data?.score_state,
          state_message: data?.state_message,
          delta_text: String(data?.delta_text || prev.pulse_score.delta_text),
          performance: String(data?.performance || prev.pulse_score.performance),
          formula: String(data?.formula || prev.pulse_score.formula || ""),
          score_ready:
            typeof data?.score_ready === "boolean"
              ? Boolean(data.score_ready)
              : prev.pulse_score.score_ready,
          raw_score:
            data?.raw_score === undefined || data?.raw_score === null
              ? prev.pulse_score.raw_score
              : Number(data.raw_score),
          weighted_subscores:
            data?.weighted_subscores && typeof data.weighted_subscores === "object"
              ? Object.fromEntries(
                  Object.entries(data.weighted_subscores).map(([key, value]) => [key, Number(value)]),
                )
              : prev.pulse_score.weighted_subscores,
          sample_days:
            data?.sample_days === undefined || data?.sample_days === null
              ? prev.pulse_score.sample_days
              : Number(data.sample_days),
          min_sample_days:
            data?.min_sample_days === undefined || data?.min_sample_days === null
              ? prev.pulse_score.min_sample_days
              : Number(data.min_sample_days),
          video_sample_count:
            data?.video_sample_count === undefined || data?.video_sample_count === null
              ? prev.pulse_score.video_sample_count
              : Number(data.video_sample_count),
          video_count: data?.video_count === undefined || data?.video_count === null ? prev.pulse_score.video_count : Number(data.video_count),
          min_videos: data?.min_videos === undefined || data?.min_videos === null ? prev.pulse_score.min_videos : Number(data.min_videos),
          videos_remaining: data?.videos_remaining === undefined || data?.videos_remaining === null ? prev.pulse_score.videos_remaining : Number(data.videos_remaining),
          weekly_trend: Array.isArray(data?.weekly_trend) ? data.weekly_trend : prev.pulse_score.weekly_trend,
        },
      }));
      return;
    }
    if (widget === "engagement_rate") {
      setInsightsMetrics((prev) => ({
        ...prev,
        engagement_rate: {
          value: Number(data?.value ?? prev.engagement_rate.value),
          delta: Number(data?.delta ?? prev.engagement_rate.delta),
          comparison_available: Boolean(data?.comparison_available),
        },
      }));
      return;
    }
    if (widget === "best_format") {
      setInsightsMetrics((prev) => ({
        ...prev,
        best_format: {
          label: String(data?.label || prev.best_format.label),
          share: Number(data?.share ?? prev.best_format.share),
        },
      }));
      return;
    }
    if (widget === "optimal_time") {
      setInsightsMetrics((prev) => ({
        ...prev,
        optimal_time: {
          label: String(data?.label || prev.optimal_time.label),
          delta: Number(data?.delta ?? prev.optimal_time.delta),
        },
      }));
      return;
    }
    if (widget === "performance_trends") {
      const points = Array.isArray(data?.performance_trends) ? data.performance_trends : [];
      if (!points.length) return;
      const normalized = points
        .map((point: any): TrendPoint => ({
          label: String(point?.label || ""),
          shortForm: Number(point?.shortForm ?? 0),
          longForm: Number(point?.longForm ?? 0),
        }))
        .filter((point: TrendPoint) => point.label);
      if (!normalized.length) return;
      setInsightsMetrics((prev) => ({ ...prev, performance_trends: normalized }));
      return;
    }
    if (widget === "content_mix") {
      setInsightsMetrics((prev) => ({
        ...prev,
        content_mix: {
          short: Number(data?.short ?? prev.content_mix.short),
          long: Number(data?.long ?? prev.content_mix.long),
          carousel: Number(data?.carousel ?? prev.content_mix.carousel),
          total_videos: data?.total_videos != null ? Number(data.total_videos) : prev.content_mix.total_videos,
        },
      }));
      return;
    }
    if (widget === "topic_performance") {
      const rows = Array.isArray(data?.topic_performance) ? data.topic_performance : [];
      if (!rows.length) return;
      const normalized = rows
        .map((row: any): TopicPoint => ({
          label: String(row?.label || ""),
          count: Number(row?.count ?? 0),
        }))
        .filter((row: TopicPoint) => row.label);
      if (!normalized.length) return;
      setInsightsMetrics((prev) => ({
        ...prev,
        topic_window: String(data?.topic_window || prev.topic_window || "Last 30 days"),
        topic_performance: normalized,
      }));
      return;
    }
    if (widget === "audience_activity") {
      const bars = Array.isArray(data?.audience_activity) ? data.audience_activity : [];
      if (!bars.length) return;
      const normalized = bars
        .map((value: any) => Number(value))
        .filter((value: number) => Number.isFinite(value))
        .slice(0, 7);
      if (!normalized.length) return;
      const detail = Array.isArray(data?.audience_activity_detail)
        ? data.audience_activity_detail
            .map((entry: any) => ({
              label: String(entry?.label || ""),
              score: Number(entry?.score ?? 0),
              avg_views: Number(entry?.avg_views ?? 0),
            }))
            .filter((entry: any) => entry.label)
            .slice(0, 7)
        : [];
      setInsightsMetrics((prev) => ({
        ...prev,
        audience_activity: normalized,
        audience_activity_detail: detail.length ? detail : prev.audience_activity_detail,
      }));
      return;
    }
    if (widget === "media_kit") {
      const growth = Array.isArray(data?.quarterly_growth)
        ? data.quarterly_growth
            .map((point: any): MediaGrowthPoint => ({
              label: String(point?.label || ""),
              reach: point?.reach === null || point?.reach === undefined ? null : Number(point.reach),
              engagement: point?.engagement === null || point?.engagement === undefined ? null : Number(point.engagement),
            }))
            .filter((point: MediaGrowthPoint) => point.label)
        : [];
      const demographics = Array.isArray(data?.audience_demographics)
        ? data.audience_demographics
            .map((row: any): MediaDemographicPoint => ({
              label: String(row?.label || ""),
              value: String(row?.value || ""),
            }))
            .filter((row: MediaDemographicPoint) => row.label)
        : [];
      const ageDistribution = Array.isArray(data?.audience_age_distribution)
        ? data.audience_age_distribution
            .map((row: any): MediaDemographicPoint => ({
              label: String(row?.label || ""),
              value: String(row?.value || ""),
            }))
            .filter((row: MediaDemographicPoint) => row.label)
        : [];
      const geoDistribution = Array.isArray(data?.audience_geo_distribution)
        ? data.audience_geo_distribution
            .map((row: any): MediaDemographicPoint => ({
              label: String(row?.label || ""),
              value: String(row?.value || ""),
            }))
            .filter((row: MediaDemographicPoint) => row.label)
        : [];
      const topContent = Array.isArray(data?.top_content)
        ? data.top_content
            .map((row: any): MediaTopContentItem => ({
              title: String(row?.title || ""),
              views: Number(row?.views ?? 0),
              engagement_rate: Number(row?.engagement_rate ?? 0),
              url: row?.url ? String(row.url) : undefined,
              published_at: row?.published_at ? String(row.published_at) : undefined,
            }))
            .filter((row: MediaTopContentItem) => row.title)
        : [];
      setMediaMetrics((prev) => ({
        ...prev,
        title: String(data?.title || prev.title),
        subtitle: String(data?.subtitle || prev.subtitle),
        followers: Number(data?.followers ?? prev.followers),
        avg_monthly_reach: Number(data?.avg_monthly_reach ?? prev.avg_monthly_reach),
        engagement_rate: Number(data?.engagement_rate ?? prev.engagement_rate),
        avg_comments: Number(data?.avg_comments ?? prev.avg_comments),
        quarterly_growth: growth,
        audience_demographics: demographics,
        audience_age_distribution: ageDistribution,
        audience_geo_distribution: geoDistribution,
        top_content_window: (() => {
          const rawWindow = String(data?.top_content_window || prev.top_content_window).toLowerCase();
          if (rawWindow === "last_5_years") return "last_5_years";
          return rawWindow === "last_year" ? "last_year" : "last_quarter";
        })(),
        top_content_subtitle: topContent.length
          ? String(data?.top_content_subtitle || prev.top_content_subtitle)
          : "No qualifying YouTube uploads found in the selected lookback.",
        top_content: topContent,
        top_content_is_inspiration: false,
      }));
    }
  };

  const extractWidgetStatus = (data: Record<string, any> | null | undefined): PulseWidgetStatus => {
    const raw = data?._widget_status;
    if (!raw || typeof raw !== "object") {
      return { ready: true, synthetic: false };
    }
    return {
      ready: typeof raw.ready === "boolean" ? raw.ready : true,
      synthetic: typeof raw.synthetic === "boolean" ? raw.synthetic : false,
      reason: raw.reason ? String(raw.reason) : undefined,
      sample_days: raw.sample_days === undefined || raw.sample_days === null ? undefined : Number(raw.sample_days),
      min_sample_days:
        raw.min_sample_days === undefined || raw.min_sample_days === null ? undefined : Number(raw.min_sample_days),
    };
  };

  const fetchPulseSnapshot = useCallback(async () => {
    const requestId = ++metricsRequestRef.current;
    setMetricsError(null);
    PULSE_METRIC_WIDGETS.forEach((widget) => markWidgetLoading(widget, true, null));
    try {
      const response = await pulseApi.post<{
        found: boolean;
        widget: string;
        data?: Record<string, any>;
        error?: string | null;
      }>(`${API_BASE}/api/metrics/all`, {
        ...reportingRange,
        creator_name: account.creatorName || undefined,
        creator_channel_url: account.youtubeChannel || undefined,
        creator_channel_id: accountPreview?.channel_id || undefined,
      });
      const payload = response.data;
      if (requestId !== metricsRequestRef.current) return;
      if (!payload?.found) {
        throw new Error(String(payload?.error || "Creator not found"));
      }
      const snapshot = payload.data || {};
      if (snapshot.meta?.refresh_failed) setMetricsError("YouTube could not refresh. Showing previously collected analytics; retry or check your connection.");
      if (snapshot.meta?.channel_data_state === "low_data" || snapshot.meta?.channel_data_state === "no_data" || snapshot.meta?.channel_data_state === "ready") {
        setChannelDataState(snapshot.meta.channel_data_state);
      }
      // The new backend puts widget data directly under `data.*` (no
      // `.insights` nesting level like the old backend had) - prefer
      // `snapshot.insights` if present for backward compat, otherwise
      // treat `snapshot` itself as the insight data root.
      const insightData = snapshot.insights && typeof snapshot.insights === "object" ? snapshot.insights : snapshot;
      setInsightsMetrics((prev) => ({ ...prev, period_metrics: insightData.period_metrics }));
      // shorts_score is a sibling of pulse_score on this same response, not one of the
      // PULSE_METRIC_WIDGETS loop's known keys - it was never being read at all, so the
      // Shorts card always saw metrics.shorts_score === undefined regardless of what the
      // backend sent (hasRealScore(undefined) is unconditionally false). Wire it directly.
      setInsightsMetrics((prev) => ({
        ...prev,
        shorts_score: insightData.shorts_score
          ? { ...insightData.shorts_score, shorts_video_count: snapshot.meta?.shorts_video_count }
          : null,
      }));
      const statusMap = snapshot.meta?.widget_status && typeof snapshot.meta.widget_status === "object"
        ? snapshot.meta.widget_status
        : {};
      const nextStatus: Partial<Record<PulseMetricWidget, PulseWidgetStatus>> = {};
      PULSE_METRIC_WIDGETS.forEach((widget) => {
        const rawStatus = statusMap[widget];
        nextStatus[widget] = extractWidgetStatus({ _widget_status: rawStatus });
        let widgetData: Record<string, any> = {};
        if (widget === "media_kit") widgetData = snapshot.media_kit || {};
        else if (widget === "topic_performance") widgetData = { topic_performance: insightData.topic_performance, topic_window: insightData.topic_window };
        else if (widget === "audience_activity") widgetData = { audience_activity: insightData.audience_activity, audience_activity_detail: insightData.audience_activity_detail };
        else if (widget === "performance_trends") widgetData = { performance_trends: insightData.performance_trends };
        else widgetData = insightData[widget] || {};
        if (widget === "pulse_score") {
          widgetData = { ...widgetData, video_sample_count: snapshot.meta?.video_sample_count };
        }
        applyWidgetData(widget, widgetData);
        markWidgetLoading(widget, false, null);
      });
      setWidgetStatus(nextStatus);
    } catch (error: any) {
      if (requestId !== metricsRequestRef.current) return;
      setMetricsError("Analytics could not load. Retry, or check your YouTube connection in Settings.");
      PULSE_METRIC_WIDGETS.forEach((widget) => markWidgetLoading(widget, false, "Failed"));
    } finally {
      if (requestId === metricsRequestRef.current) setMetricsReady(true);
    }
  }, [account.creatorName, account.youtubeChannel, accountPreview?.channel_id, reportingRange]);

  // User-triggered "Resync" on their own dashboard - re-runs a full backfill
  // for the active channel, then refetches metrics so cards/graphs update
  // without a manual reload. A 401/needs_reauth response from the resync
  // call updates the same oauth_status field the re-auth banner/modal
  // already watch, so that honest state takes over instead of a generic error.
  const handleResyncChannel = async () => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!channelId || !oauthConnected || needsReauth || resyncState === "syncing") return;
    setResyncState("syncing");
    setResyncError(null);
    try {
      const response = await pulseApi.post<{ ok?: boolean; status?: "syncing" | "needs_reauth"; channel_id?: string; last_synced_at?: string | null }>(
        `${API_BASE}/api/youtube/channels/${encodeURIComponent(channelId)}/resync`,
      );
      if (response.data?.status === "needs_reauth") {
        setAccountPreview((prev) => (prev ? { ...prev, oauth_status: "needs_reauth" } : prev));
        setResyncState("idle");
        return;
      }
      // The resync call only enqueues a background backfill job and returns before it runs
      // (see the RESYNC_POLL_* comment above) - refetching metrics immediately on this
      // response just re-pulls the same pre-sync data from cache. Poll the channel list
      // until analytics_synced_at actually advances past this response's own
      // last_synced_at (the backfill's completion stamp, same moment the metrics cache is
      // busted server-side) before refetching, so the card updates in place instead of
      // needing a manual hard refresh.
      const baseline = response.data?.last_synced_at ?? null;
      const deadline = Date.now() + RESYNC_POLL_TIMEOUT_MS;
      let advanced = false;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, RESYNC_POLL_INTERVAL_MS));
        const listResp = await pulseApi.get<YoutubeChannelSummary[]>(`${API_BASE}/api/youtube/channels`).catch(() => null);
        const row = Array.isArray(listResp?.data) ? listResp!.data!.find((c) => c?.channel_id === channelId) : null;
        if (!row) continue;
        if (row.status === "needs_reauth" || row.auth_status === "needs_reauth") {
          setAccountPreview((prev) => (prev ? { ...prev, oauth_status: "needs_reauth" } : prev));
          setResyncState("idle");
          return;
        }
        if (row.analytics_synced_at && row.analytics_synced_at !== baseline) {
          advanced = true;
          break;
        }
      }
      await fetchPulseSnapshot();
      if (advanced) {
        setResyncState("synced");
        setTimeout(() => setResyncState("idle"), 2500);
      } else {
        // Backfill hadn't completed within the poll window (e.g. a slow pull, or a demo/
        // seed channel with no real token to back off against) - don't claim "Synced" over
        // data we can't confirm is fresh.
        setResyncError("Still syncing in the background - your data will update automatically once it finishes.");
        setResyncState("error");
        setTimeout(() => setResyncState("idle"), 4000);
      }
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.data?.needs_reauth) {
        setAccountPreview((prev) => (prev ? { ...prev, oauth_status: "needs_reauth" } : prev));
        setResyncState("idle");
        return;
      }
      // Surface the real backend status/message instead of a bare "failed" -
      // this is what tells the user (and us) whether it's a missing route
      // (404), a server error (500), or something else, without needing
      // devtools open.
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
      setResyncError(
        status
          ? `Resync failed (${status})${serverMessage ? `: ${serverMessage}` : ""}`
          : "Resync failed: could not reach the server.",
      );
      // eslint-disable-next-line no-console
      console.error("Resync failed", { status, data: error?.response?.data, message: error?.message });
      setResyncState("error");
      setTimeout(() => setResyncState("idle"), 4000);
    }
  };

  const fetchIrisRecommendation = useCallback(async () => {
    setRecommendationLoading(true);
    setRecommendationError(null);
    try {
      const response = await pulseApi.post<{
        found: boolean;
        channel_id?: string | null;
        generated_at?: number | null;
        from_cache?: boolean;
        data?: Record<string, any>;
        error?: string | null;
      }>(`${API_BASE}/api/recommendation`, {
        creator_name: account.creatorName || undefined,
        creator_channel_url: account.youtubeChannel || undefined,
        creator_channel_id: accountPreview?.channel_id || undefined,
      });
      const payload = response.data;
      if (!payload?.found || !payload?.data) {
        throw new Error(String(payload?.error || "No recommendation available"));
      }
      const data = payload.data || {};
      const next: PulseRecommendation = {
        title: String(data?.title || "IRIS Recommendation"),
        summary: String(data?.summary || ""),
        action: String(data?.action || ""),
        confidence: String(data?.confidence || "medium"),
        based_on: Array.isArray(data?.based_on) ? data.based_on.map((v: any) => String(v)) : [],
        state: String(data?.state || "active"),
      };
      setIrisRecommendation(next);
      setRecommendationError(null);
    } catch (_error: any) {
      setRecommendationError("Could not load recommendation.");
      setIrisRecommendation(null);
    } finally {
      setRecommendationLoading(false);
    }
  }, [account.creatorName, account.youtubeChannel, accountPreview?.channel_id]);

  const fetchScorePlan = useCallback(async () => {
    setScorePlanLoading(true);
    setScorePlanError(null);
    try {
      const response = await pulseApi.post<{
        found: boolean;
        channel_id?: string | null;
        generated_at?: number | null;
        from_cache?: boolean;
        data?: Record<string, any>;
        error?: string | null;
      }>(`${API_BASE}/api/score_plan`, {
        creator_name: account.creatorName || undefined,
        creator_channel_url: account.youtubeChannel || undefined,
        creator_channel_id: accountPreview?.channel_id || undefined,
        setup_goals: selectedGoals,
      });
      const payload = response.data;
      if (!payload?.found || !payload?.data) {
        throw new Error(String(payload?.error || "No score plan available"));
      }
      const data = payload.data || {};
      const items = Array.isArray(data?.items)
        ? (data.items.map(normalizePulseScorePlanItem).filter(Boolean) as PulseScorePlanItem[])
        : [];
      const next: PulseScorePlan = {
        title: String(data?.title || "How to improve your Pulse score?"),
        subtitle: String(
          data?.subtitle ||
            "Commit a move and Pulse will verify its target metric after new analytics arrive.",
        ),
        potential_score_lift: Number(data?.potential_score_lift ?? 0),
        items,
      };
      setScorePlan(next);
      setScorePlanError(null);
    } catch (_error: any) {
      setScorePlanError("Could not load score action plan.");
      setScorePlan(null);
    } finally {
      setScorePlanLoading(false);
    }
  }, [account.creatorName, account.youtubeChannel, accountPreview?.channel_id, selectedGoals]);

  const fetchTypedInsights = useCallback(async () => {
    if (!pulseSessionReady) return;
    const requestId = ++typedInsightsRequestRef.current;
    setTypedInsightsLoading(true);
    try {
      const response = await pulseApi.post<{ ok: boolean; insights?: PulseTypedInsight[] }>(
        `${API_BASE}/api/insights`,
        {
          ...reportingRange,
          creator_name: account.creatorName || undefined,
          creator_channel_url: account.youtubeChannel || undefined,
          creator_channel_id: accountPreview?.channel_id || undefined,
        },
      );
      if (requestId === typedInsightsRequestRef.current) setTypedInsights(Array.isArray(response.data?.insights) ? response.data.insights : []);
    } catch {
      if (requestId === typedInsightsRequestRef.current) setTypedInsights([]);
    } finally {
      if (requestId === typedInsightsRequestRef.current) setTypedInsightsLoading(false);
    }
  }, [account.creatorName, account.youtubeChannel, accountPreview?.channel_id, pulseSessionReady, reportingRange]);

  const refreshCreatorMemory = useCallback(async () => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!pulseSessionReady || !pulseUserId || !channelId) {
      setCreatorMemory({});
      return;
    }
    setCreatorMemoryLoading(true);
    try {
      const response = await pulseApi.get<{ ok: boolean; memory?: PulseCreatorMemory }>(
        `${API_BASE}/api/memory`,
        { params: { channel_id: channelId } },
      );
      setCreatorMemory(response.data?.memory || {});
    } catch {
      setCreatorMemory({});
    } finally {
      setCreatorMemoryLoading(false);
    }
  }, [accountPreview?.channel_id, pulseSessionReady, pulseUserId]);

  const commitPrescribedMove = useCallback(async (item: PulseScorePlanItem) => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    // Mirrors the backend's own commit() guard: proposed|no_move|expired can
    // all (re-)commit to pending - a no_move or expired move isn't a dead end.
    if (!item.id || !pulseUserId || !channelId || !["proposed", "no_move", "expired"].includes(item.status)) return;
    setScorePlanError(null);
    try {
      const response = await pulseApi.post<{ ok: boolean; move?: any; error?: string | null }>(
        `${API_BASE}/api/moves/${encodeURIComponent(item.id)}/commit`,
        { channel_id: channelId },
      );
      const updated = normalizePulseScorePlanItem(response.data?.move);
      if (!updated) throw new Error(response.data?.error || "Move could not be committed.");
      setScorePlan((prev) => prev ? {
        ...prev,
        items: prev.items.map((existing) => existing.id === updated.id ? updated : existing),
      } : prev);
    } catch (error: any) {
      setScorePlanError(String(error?.response?.data?.detail || error?.message || "Could not commit this move."));
    }
  }, [accountPreview?.channel_id, pulseUserId]);

  const saveTypedInsight = useCallback(async (insight: PulseTypedInsight) => {
    if (!pulseUserId) return;
    try {
      const response = await pulseApi.post<{ ok: boolean; item?: any }>(
        `${API_BASE}/api/saved`,
        {
          channel_id: accountPreview?.channel_id || undefined,
          type: insight.type === "missed_opportunity" ? "action" : "metric",
          title: insight.title,
          body: `${insight.body}\n\nConfidence: ${insight.confidence}. Surface: ${insight.surface}.`,
          source: "iris_insight",
          category: insight.evidence === "engagement_rate" ? "Audience & ICPs" : insight.evidence === "topic_performance" ? "Format & packaging" : "Data & scope",
          pinned: false,
        },
      );
      const item = normalizeSavedIrisItem(response.data?.item);
      if (item) setSavedItems((prev) => [item, ...prev.filter((existing) => existing.id !== item.id)]);
    } catch {
      setSavedItemsError("Could not save this insight to Bookmark.");
    }
  }, [accountPreview?.channel_id, pulseUserId]);

  const clearCreatorMemory = useCallback(async () => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!pulseUserId || !channelId || !window.confirm("Clear everything IRIS has learned for this channel?")) return;
    try {
      await pulseApi.delete(`${API_BASE}/api/memory`, {
        params: { channel_id: channelId },
      });
      setCreatorMemory({});
    } catch {
      setSavedItemsError("Could not clear IRIS memory.");
    }
  }, [accountPreview?.channel_id, pulseUserId]);

  const clearSavedItems = useCallback(async () => {
    if (!pulseUserId || !window.confirm("Clear all saved metrics and actions for this channel?")) return;
    try {
      await pulseApi.delete(`${API_BASE}/api/saved`, {
        params: { channel_id: accountPreview?.channel_id || undefined },
      });
      setSavedItems([]);
    } catch {
      setSavedItemsError("Could not clear saved items.");
    }
  }, [accountPreview?.channel_id, pulseUserId]);

  const resizeComposer = useCallback((composer: HTMLTextAreaElement | null) => {
    if (!composer) return;
    composer.style.height = "0px";
    const clamped = Math.min(Math.max(composer.scrollHeight, 42), 140);
    composer.style.height = `${clamped}px`;
  }, []);

  const resizeComposers = useCallback(() => {
    resizeComposer(heroComposerRef.current);
    resizeComposer(stickyComposerRef.current);
    resizeComposer(panelComposerRef.current);
  }, [resizeComposer]);

  const handleWorkspaceScroll = useCallback(() => {
    const top = workspaceScrollRef.current?.scrollTop || 0;
    setShowStickyIris(irisSurfaceAvailable && (activeTab === "saved" || top > 260));
  }, [activeTab, irisSurfaceAvailable]);

  const handleSend = () => {
    if (chatLoading) return;
    const content = chatDraft.trim();
    if (!content) return;
    setChatError(null);
    setChatMessages((prev) => [...prev, { role: "user", text: content }]);
    setChatDraft("");
    setIrisPanelOpen(true);
  };

  // "Ask IRIS about this" from a Bookmark card - seeds the NEXT job-creation
  // call (below) with saved_item_id so IRIS grounds its reply in that item.
  // Consumed and cleared the moment it's read, so a follow-up question in the
  // same panel session doesn't keep re-sending it.
  const pendingSavedItemIdRef = useRef<string | null>(null);
  const askIrisAboutSavedItem = useCallback((item: SavedIrisItem) => {
    pendingSavedItemIdRef.current = item.id;
    setChatError(null);
    setChatMessages((prev) => [...prev, { role: "user", text: `Tell me more about this: ${item.title}` }]);
    setIrisPanelOpen(true);
  }, []);

  // Per-surface "jump to IRIS" from a Pulse Score card (hover "+" on Videos/Shorts).
  // Unlike askIrisAboutSavedItem above, this doesn't send a message itself - it scopes the
  // panel (removable chip + surface-specific suggested prompts + placeholder) and lets the
  // user pick or type a real question. No dedicated backend seed field exists for this yet
  // (unlike saved_item_id) - the surface name is real context carried in the prompt TEXT
  // itself once sent, not a fabricated signal.
  const [irisScopedSurface, setIrisScopedSurface] = useState<{ label: string; shortLabel: string; score: number | null } | null>(null);
  const askIrisAboutSurface = useCallback((label: string, shortLabel: string, score: number | null) => {
    setIrisScopedSurface({ label, shortLabel, score });
    setChatError(null);
    setIrisPanelOpen(true);
  }, []);

  const canSendChat = pulseSessionReady && chatDraft.trim().length > 0 && !chatLoading;

  const handleNewChat = () => {
    setChatLoading(false);
    setChatStatusMessage("IRIS is preparing context...");
    setChatError(null);
    setChatDraft("");
    setChatMessages([{ role: "assistant", text: DEFAULT_ASSISTANT_TEXT }]);
    setIrisScopedSurface(null);
    setIrisPanelOpen(true);
  };

  useEffect(() => {
    return () => {
      if (mediaActionTimerRef.current !== null) {
        window.clearTimeout(mediaActionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (onboardingStep !== "reading") return;
    setReadingStep(0);
    const timers = PULSE_READING_STEPS.map((_step, idx) =>
      window.setTimeout(() => {
        setReadingStep(idx + 1);
      }, 420 + idx * 520),
    );
    const doneTimer = window.setTimeout(() => {
      saveOnboardingStep("done");
      setOnboardingStep("done");
    }, 3400);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(doneTimer);
    };
  }, [onboardingStep]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const youtubeOauthState = String(params.get("yt_oauth") || "").trim();
    const instagramOauthState = String(params.get("ig_oauth") || "").trim();
    if (!youtubeOauthState && !instagramOauthState) return;
    const channelId = String(params.get("channel_id") || "").trim();
    if (youtubeOauthState === "connected") {
      setMediaActionToast("YouTube OAuth connected.");
      setOnboardingStep((prev) => {
        if (prev === "done") return prev;
        saveOnboardingStep("connect");
        return "connect";
      });
      if (channelId) {
        void refreshOauthConnection(channelId);
        setAccountPreview((prev) => (prev ? { ...prev, oauth_connected: true, oauth_status: "connected" } : prev));
      }
    } else if (youtubeOauthState) {
      const reason = String(params.get("reason") || "unknown");
      if (reason === "channel_mismatch") {
        const expected = String(params.get("expected_channel_id") || "").trim();
        const connected = String(params.get("connected_channel_id") || "").trim();
        setOauthError(
          `OAuth connected a different YouTube owner channel. Expected ${expected || "selected channel"}, got ${connected || "another channel"}. Select your own channel in Pulse and try again.`,
        );
      } else {
        setOauthError(`OAuth failed (${reason}).`);
      }
    }
    if (instagramOauthState === "connected") {
      setMediaActionToast("Instagram connected.");
      void refreshInstagramConnection();
    } else if (instagramOauthState) {
      const reason = String(params.get("reason") || "unknown");
      setInstagramOauthError(`Instagram OAuth failed (${reason}).`);
    }
    const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [refreshInstagramConnection, refreshOauthConnection]);

  useEffect(() => {
    const last = chatMessages[chatMessages.length - 1];
    if (!pulseSessionReady || !last || last.role !== "user") return;

    let cancelled = false;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const run = async () => {
      setChatLoading(true);
      setChatStatusMessage("IRIS is preparing context...");
      setChatError(null);
      try {
        const createJob = await pulseApi.post<{
          job_id: string;
          trace_id: string;
          status: string;
          status_message: string;
        }>(`${API_BASE}/api/ai_v2/jobs`, {
          creator_name: account.creatorName || undefined,
          creator_channel_url: account.youtubeChannel || undefined,
          creator_channel_id: accountPreview?.channel_id || undefined,
          setup_goals: selectedGoals,
          creator_context: `${oauthConnected
            ? "YouTube OAuth status: connected. Analytics-backed Pulse metrics available."
            : "YouTube OAuth status: not connected. Only public YouTube metadata is available."} ${instagramOauthConnected
            ? "Instagram OAuth status: connected. Treat Instagram as a distinct audience and never add it to YouTube totals."
            : "Instagram OAuth status: not connected."}`,
          ai_knowledgebase: uploadedDocumentKnowledgebase || undefined,
          saved_item_id: pendingSavedItemIdRef.current || undefined,
          panel_language: "en",
          messages: chatMessages.map((m) => ({
            role: m.role,
            text: m.text,
          })),
        });
        pendingSavedItemIdRef.current = null;
        const jobId = String(createJob.data?.job_id || "").trim();
        if (!jobId) {
          throw new Error("IRIS job could not be created.");
        }
        setChatStatusMessage(String(createJob.data?.status_message || "IRIS is preparing context..."));

        let completedReply = "";
        let completedMetadata: Pick<PulseChatMessage, "confidence" | "surface" | "evidence" | "unknowns" | "insight"> = {};
        for (let attempt = 0; attempt < 120; attempt += 1) {
          if (cancelled) return;
          await wait(1500);
          if (cancelled) return;
          const statusResp = await pulseApi.get<{
            job_id: string;
            status: string;
            status_message: string;
            done: boolean;
            trace_id?: string | null;
            reply?: string | null;
            model?: string | null;
            elapsed_seconds?: number | null;
            confidence?: string | null;
            surface?: string | null;
            evidence?: Array<Record<string, any>>;
            unknowns?: string[];
            insight?: PulseTypedInsight | null;
            error?: string | null;
          }>(`${API_BASE}/api/ai_v2/jobs/${encodeURIComponent(jobId)}`);

          const statusData = statusResp.data;
          setChatStatusMessage(String(statusData?.status_message || "IRIS is working..."));
          if (!statusData?.done) continue;
          if (statusData.status === "completed") {
            completedReply = String(statusData.reply || "").trim();
            completedMetadata = {
              confidence: statusData.confidence || null,
              surface: statusData.surface || null,
              evidence: Array.isArray(statusData.evidence) ? statusData.evidence : [],
              unknowns: Array.isArray(statusData.unknowns) ? statusData.unknowns : [],
              insight: statusData.insight || null,
            };
            break;
          }
          if (statusData.status === "failed") {
            throw new Error(String(statusData.error || "IRIS could not complete the response."));
          }
          throw new Error("IRIS could not complete the response.");
        }

        if (!completedReply) {
          throw new Error("IRIS is taking longer than expected. Please retry.");
        }
        if (cancelled) return;
        setChatMessages((prev) => [...prev, { role: "assistant", text: completedReply, ...completedMetadata }]);
        setChatLoading(false);
        await generateAndSaveIrisChart(last.text, completedReply);
      } catch (error) {
        if (cancelled) return;
        setChatError("IRIS couldn't respond right now.");
      } finally {
        if (!cancelled) {
          setChatLoading(false);
          setChatStatusMessage("IRIS is ready.");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [chatMessages, account.creatorName, account.youtubeChannel, accountPreview?.channel_id, oauthConnected, instagramOauthConnected, pulseSessionReady, pulseUserId, selectedGoals, uploadedDocumentKnowledgebase, generateAndSaveIrisChart]);

  useEffect(() => {
    resizeComposers();
  }, [chatDraft, irisPanelOpen, resizeComposers, showStickyIris]);

  useEffect(() => {
    if (!irisSurfaceAvailable) {
      setShowStickyIris(false);
      return;
    }
    handleWorkspaceScroll();
  }, [handleWorkspaceScroll, irisSurfaceAvailable]);

  useEffect(() => {
    if (!irisPanelOpen) return;
    const body = irisPanelBodyRef.current;
    if (!body) return;
    body.scrollTop = body.scrollHeight;
  }, [chatMessages, chatLoading, chatStatusMessage, irisPanelOpen]);

  useEffect(() => {
    saveMode(mode);
  }, [mode]);

  useEffect(() => {
    saveSidebarCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    saveChatHistory(chatMessages, pulseUserId);
  }, [chatMessages, pulseUserId]);

  useEffect(() => {
    if (!pulseSessionReady || !["saved", "insights"].includes(activeTab)) return;
    void Promise.all([refreshSavedItems(), refreshCreatorMemory()]);
  }, [activeTab, pulseSessionReady, refreshCreatorMemory, refreshSavedItems]);

  useEffect(() => {
    const channelId = String(accountPreview?.channel_id || "").trim();
    if (!pulseSessionReady || !channelId) {
      setOauthConnection(null);
      return;
    }
    void refreshOauthConnection(channelId);
  }, [accountPreview?.channel_id, pulseSessionReady, refreshOauthConnection]);

  useEffect(() => {
    if (!pulseSessionReady || !pulseUserId) {
      setInstagramConnection(null);
      return;
    }
    void refreshInstagramConnection();
  }, [pulseSessionReady, pulseUserId, refreshInstagramConnection]);

  useEffect(() => {
    if (!pulseSessionReady) return;
    if (!hasCreatorAccount) {
      setInsightsMetrics(DEMO_INSIGHTS_METRICS);
      setMediaMetrics(DEMO_MEDIA_KIT_METRICS);
      setScorePlan(DEMO_SCORE_PLAN);
      setScorePlanError(null);
      setScorePlanLoading(false);
      setIrisRecommendation(DEMO_IRIS_RECOMMENDATION);
      setRecommendationError(null);
      setRecommendationLoading(false);
      setWidgetStatus({});
      setMetricsReady(true);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setMetricsReady(false);
      setWidgetStatus({});
      setInsightsMetrics({
        header: { title: "Insights", subtitle: "Loading connected analytics", growth_badge: "" },
        pulse_score: { score: 0, delta_text: "Building baseline", performance: "Pending", score_ready: false },
        engagement_rate: { value: 0, delta: 0 },
        best_format: { label: "Data pending", share: 0 },
        optimal_time: { label: "Data pending", delta: 0 },
        performance_trends: [],
        content_mix: { short: 0, long: 0, carousel: 0 },
        topic_window: "Last 28 days",
        topic_performance: [],
        audience_activity: [],
        audience_activity_detail: [],
      });
      setMediaMetrics({
        title: "Creator Media Kit",
        subtitle: "Connected analytics snapshot",
        followers: 0,
        avg_monthly_reach: 0,
        engagement_rate: 0,
        avg_comments: 0,
        quarterly_growth: [],
        audience_demographics: [],
        audience_age_distribution: [],
        audience_geo_distribution: [],
        top_content_window: "last_quarter",
        top_content_subtitle: "Top-performing content is loading.",
        top_content: [],
        top_content_is_inspiration: false,
      });
      setScorePlanError(null);
      setRecommendationError(null);
      if (!cancelled) await fetchPulseSnapshot();
      if (cancelled) return;
      const jobs = [
        (async () => {
          if (cancelled) return;
          await fetchScorePlan();
        })(),
        (async () => {
          if (cancelled) return;
          await fetchIrisRecommendation();
        })(),
        (async () => {
          if (cancelled) return;
          await fetchTypedInsights();
        })(),
      ];
      await Promise.allSettled(jobs);
      if (!cancelled) setMetricsReady(true);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [fetchPulseSnapshot, fetchIrisRecommendation, fetchScorePlan, fetchTypedInsights, hasCreatorAccount, pulseSessionReady]);

  const proxyScoreActive = String(insightsMetrics.pulse_score.formula || "").toLowerCase().includes("proxy_v1")
    || String(insightsMetrics.pulse_score.formula || "").toLowerCase().includes("not yet full section 1.1");

  const pulseNoticeMessage = !hasCreatorAccount
    ? "Demo data shown. Connect an Account for live YouTube metrics."
    : !oauthConnected
      ? "Public YouTube metadata only. Connect YouTube OAuth to unlock analytics-backed scoring."
      : proxyScoreActive
        ? "OAuth connected. Showing beta proxy score while full Section 1.1 model is being finalized."
      : insightsMetrics.pulse_score.score_ready === false
        ? "OAuth connected. Pulse is building daily analytics snapshots for real scoring."
        : null;

  // Retained while the redesigned views roll out; setters still drive API lifecycle state.
  void scorePlanLoading;
  void recommendationLoading;
  void recommendationError;
  void widgetLoading;
  void handleIrisResizeStart;
  void pulseNoticeMessage;

  const channelManageDialog = (
    <Dialog
      open={channelManageOpen}
      onClose={() => setChannelManageOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: `pulse-account-dialog pulse-channel-manage-dialog ${mode === "dark" ? "dark" : "light"}` }}
    >
      <div className="pulse-channel-manager">
        <div className="pulse-channel-manager-header">
          <div className="pulse-channel-manager-badge">
            <IrisIcon fontSize="medium" />
          </div>
          <div>
            <h2>Your channels</h2>
            <p>IRIS needs read-only access to your analytics. No posting permissions are ever requested.</p>
          </div>
          <IconButton size="small" onClick={() => setChannelManageOpen(false)} className="pulse-channel-manager-close" aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="pulse-channel-manager-list">
          {channels.map((channel) => (
            <div key={channel.channel_id} className={`pulse-channel-row ${channel.channel_id === activeChannelId ? "is-active" : ""}`}>
              <div className="pulse-channel-row-avatar">
                <Avatar src={channel.thumbnail_url || undefined} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 48, height: 48, fontSize: 16, bgcolor: "#29bfd4" }}>
                  {(channel.title || channel.custom_url || "?").charAt(0).toUpperCase()}
                </Avatar>
                <span className="pulse-channel-row-platform" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M21.6 7.2s-.2-1.5-.8-2.2c-.8-.9-1.7-.9-2.1-1C15.9 3.8 12 3.8 12 3.8h0s-3.9 0-6.7.2c-.4 0-1.3.1-2.1 1-.6.7-.8 2.2-.8 2.2S2.2 9 2.2 10.7v1.6c0 1.8.2 3.5.2 3.5s.2 1.5.8 2.2c.8.9 1.9.9 2.4 1 1.7.2 7.4.2 7.4.2s3.9 0 6.7-.2c.4 0 1.3-.1 2.1-1 .6-.7.8-2.2.8-2.2s.2-1.8.2-3.5v-1.6c0-1.8-.2-3.5-.2-3.5ZM9.9 14.6V8.7l5.4 2.9-5.4 3Z" /></svg>
                </span>
              </div>
              <div className="pulse-channel-row-copy">
                <strong>{channel.title || channel.custom_url || "YouTube channel"}</strong>
                <span>{channel.custom_url || "Read-only analytics access"}</span>
              </div>
              {channel.channel_id === activeChannelId ? <span className="pulse-channel-row-active-tag">Active</span> : null}
              <span className="pulse-channel-row-connected">
                <VerifiedRoundedIcon fontSize="small" />
                Connected
              </span>
              {confirmDisconnectId === channel.channel_id ? (
                <span className="pulse-channel-row-confirm">
                  <span>Delete this channel?</span>
                  <button type="button" onClick={() => setConfirmDisconnectId(null)} disabled={oauthBusy}>Cancel</button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => { setConfirmDisconnectId(null); void handleDisconnectChannel(String(channel.channel_id)); }}
                    disabled={oauthBusy}
                  >
                    {oauthBusy ? "Deleting..." : "Yes, delete channel"}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="pulse-channel-row-disconnect"
                  onClick={() => setConfirmDisconnectId(String(channel.channel_id))}
                  disabled={oauthBusy}
                  aria-label="Delete this channel"
                  title="Delete this channel"
                >
                  <LinkOffRoundedIcon fontSize="small" />
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="pulse-channel-manager-note">
          Deleting a channel permanently removes it and all its collected analytics. To bring it back, add and reconnect it again.
        </p>

        <button type="button" className="pulse-channel-manager-add" onClick={handleAddChannel} disabled={oauthBusy}>
          <span className="pulse-channel-manager-add-icon">
            <AddRoundedIcon fontSize="small" />
          </span>
          {oauthBusy ? "Opening Google..." : "Connect another channel"}
        </button>

        {oauthError ? <p className="pulse-channel-manager-error">{oauthError}</p> : null}

        <div className="pulse-channel-manager-footer">
          <Button size="small" className="pulse-account-btn primary" onClick={() => setChannelManageOpen(false)}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );

  const accountDialog = (
    <Dialog
      open={accountOpen}
      onClose={() => setAccountOpen(false)}
      maxWidth={accountView === "preview" ? "md" : "sm"}
      fullWidth
      PaperProps={{ className: `pulse-account-dialog ${accountView === "preview" ? "preview" : ""} ${mode === "dark" ? "dark" : "light"}` }}
    >
      {accountView === "form" ? (
        <>
          <DialogTitle className="pulse-account-title">Creator Account</DialogTitle>
          <DialogContent className="pulse-account-content">
            <div className="pulse-account-form-shell">
              <Typography variant="body2" className="pulse-account-intro">
                Connect your creator identity for YouTube-grounded insights and IRIS strategy.
              </Typography>
              <div className="pulse-account-form-grid">
                <TextField
                  label="Creator Name"
                  size="small"
                  fullWidth
                  value={accountDraft.creatorName}
                  onChange={(e) => setAccountDraft((prev) => ({ ...prev, creatorName: e.target.value }))}
                />
                <TextField
                  label="YouTube Channel URL or @handle"
                  size="small"
                  fullWidth
                  value={accountDraft.youtubeChannel}
                  onChange={(e) => setAccountDraft((prev) => ({ ...prev, youtubeChannel: e.target.value }))}
                />
              </div>
            </div>
            {accountError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                {accountError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions className="pulse-account-actions">
            <Button size="small" className="pulse-account-btn ghost" onClick={() => setAccountOpen(false)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              className="pulse-account-btn primary"
              onClick={handleAccountSave}
              disabled={accountSaving}
            >
              {accountSaving ? "Loading..." : "Save"}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle className="pulse-account-title">Creator Profile Preview</DialogTitle>
          <DialogContent className="pulse-account-content">
            <div className="pulse-account-preview-shell">
              <div className="pulse-account-preview">
                <Avatar
                  src={accountPreview?.thumbnail_url || undefined}
                  imgProps={{ referrerPolicy: "no-referrer" }}
                  sx={{ width: 96, height: 96, fontSize: 30, bgcolor: mode === "dark" ? "#2d2d30" : "#ececf1" }}
                >
                  {initials}
                </Avatar>
                <div className="pulse-account-preview-meta">
                  <h4>{accountPreview?.title || account.creatorName || "Unknown creator"}</h4>
                  <p>{accountPreview?.custom_url || account.youtubeChannel || ""}</p>
                  {currentUser?.email ? (
                    <p className="pulse-account-signed-in-as">Signed in as {currentUser.email}</p>
                  ) : null}
                  <div className="pulse-account-preview-tags">
                    <Chip size="small" label={oauthConnected ? "OAuth Connected" : "OAuth Needed"} color={oauthConnected ? "success" : "warning"} />
                    {accountPreview?.country ? <Chip size="small" label={accountPreview.country} /> : null}
                    {accountPreview?.published_at ? (
                      <Chip size="small" label={`Joined ${formatJoinedDate(accountPreview.published_at)}`} />
                    ) : null}
                  </div>
                  {accountPreview?.url && (
                    <a href={accountPreview.url} target="_blank" rel="noreferrer">
                      Open channel
                    </a>
                  )}
                </div>
              </div>
              <div className="pulse-account-stats">
                <div>
                  <span>Subscribers</span>
                  <strong>{(accountPreview?.subscriber_count || 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span>Total Views</span>
                  <strong>{(accountPreview?.view_count || 0).toLocaleString()}</strong>
                </div>
                <div>
                  <span>Videos</span>
                  <strong>{(accountPreview?.video_count || 0).toLocaleString()}</strong>
                </div>
              </div>
              <Typography variant="body2" className="pulse-account-description">
                {accountPreview?.description || "Creator profile is connected. IRIS will use this channel for context."}
              </Typography>
              {oauthError ? (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                  {oauthError}
                </Typography>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions className="pulse-account-actions">
            <Button size="small" className="pulse-account-btn ghost" onClick={handlePulseLogout}>
              Log out
            </Button>
            {oauthConnected ? (
              <Button size="small" className="pulse-account-btn ghost" onClick={handleDisconnectYoutubeOAuth} disabled={oauthBusy}>
                {oauthBusy ? "Working..." : "Disconnect OAuth"}
              </Button>
            ) : (
              <Button size="small" variant="contained" className="pulse-account-btn primary" onClick={() => handleConnectYoutubeOAuth()} disabled={oauthBusy}>
                {oauthBusy ? "Opening..." : "Connect YouTube OAuth"}
              </Button>
            )}
            <Button size="small" className="pulse-account-btn ghost" onClick={() => setAccountView("form")}>
              Change creator
            </Button>
            <Button size="small" variant="contained" className="pulse-account-btn primary" onClick={() => setAccountOpen(false)}>
              Done
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  // Wait for the server-truth bootstrap before deciding onboarding vs.
  // dashboard. onboardingStep's initial value is only a localStorage-cache
  // guess (readAccount/readAccountPreview) used purely to avoid a flash of
  // the wrong screen when the cache IS warm - it must never be trusted on
  // its own. A returning user with a cleared cache (or a first paint on a
  // fresh browser) used to render the "welcome" onboarding screen for one
  // frame, and if the user acted on it before the bootstrap corrected the
  // step, that click could actively derail an otherwise-complete account
  // into onboarding. Show a neutral loading state instead until
  // bootstrapPulseSession has actually resolved (or failed) against the
  // server - onboardingStep is only trustworthy after that.
  if (!pulseSessionReady && !pulseSessionError) {
    return (
      <Box className="pulse-root dark pulse-onboarding-root" sx={{ display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} sx={{ color: "#8428f5" }} />
      </Box>
    );
  }

  if (onboardingStep !== "done") {
    return (
      <Box className="pulse-root dark pulse-onboarding-root">
        {onboardingStep === "welcome" ? (
          <PulseWelcomeOnboarding onGetStarted={handleOnboardingGetStarted} error={pulseSessionError} />
        ) : onboardingStep === "connect" ? (
          <PulseConnectOnboarding
            oauthConnected={oauthConnected}
            oauthBusy={oauthBusy}
            oauthError={oauthError}
            instagramConnected={instagramOauthConnected}
            instagramBusy={instagramOauthBusy}
            instagramError={instagramOauthError}
            onConnect={handleOnboardingConnectYoutube}
            onConnectInstagram={handleConnectInstagramOAuth}
            onContinue={handleOnboardingContinue}
          />
        ) : onboardingStep === "goals" ? (
          <PulseGoalsOnboarding
            selectedGoals={selectedGoals}
            onToggleGoal={handleToggleGoal}
            onContinue={handleGoalsContinue}
          />
        ) : (
          <PulseReadingOnboarding activeStep={readingStep} />
        )}
        {accountDialog}
      </Box>
    );
  }

  return (
    <Box className={`pulse-root ${mode} pulse-app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`} style={{ "--pulse-text-scale": accountPreferences.text_scale || 1 } as React.CSSProperties}>
      <aside className="pulse-sidebar-new">
        <div className="pulse-sidebar-brand">
          <img src="/pulse-icon.png" alt="" aria-hidden="true" className="pulse-v2-brand-mark" />
          <div className="pulse-brand-title">Pulse</div>
          <button
            type="button"
            className="pulse-sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={sidebarCollapsed}
          >
            <KeyboardDoubleArrowLeftRoundedIcon className="pulse-sidebar-collapse" fontSize="small" />
          </button>
        </div>

        <div className="pulse-sidebar-channel">
          <p>Channels</p>
          <button
            type="button"
            onClick={(e) => setChannelMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(channelMenuAnchor)}
          >
            <Avatar
              src={accountPreview?.thumbnail_url || undefined}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 27, height: 27, fontSize: 10, bgcolor: "#29bfd4" }}
            >
              {initials}
            </Avatar>
            <span>{accountPreview?.custom_url || account.creatorName || "Choose creator"}</span>
            <ExpandMoreRoundedIcon fontSize="small" />
          </button>
          <Menu
            anchorEl={channelMenuAnchor}
            open={Boolean(channelMenuAnchor)}
            onClose={() => setChannelMenuAnchor(null)}
            className="pulse-channel-menu"
            // Menu portals to document.body, outside .pulse-app-shell, so a
            // selector like .pulse-app-shell.dark .pulse-channel-menu never
            // matches - pass the mode directly on the Paper instead.
            PaperProps={{ className: `pulse-channel-menu-paper ${mode === "dark" ? "dark" : "light"}` }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {channels.map((channel) => (
              <MenuItem key={channel.channel_id} selected={channel.channel_id === activeChannelId} onClick={() => handleSwitchActiveChannel(String(channel.channel_id))}>
                <ListItemIcon>
                  <Avatar src={channel.thumbnail_url || undefined} imgProps={{ referrerPolicy: "no-referrer" }} sx={{ width: 24, height: 24, fontSize: 10, bgcolor: "#29bfd4" }}>
                    {(channel.title || channel.custom_url || "?").charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemIcon>
                <ListItemText>{channel.custom_url || channel.title || channel.channel_id}</ListItemText>
                {channel.channel_id === activeChannelId ? <CheckRoundedIcon fontSize="small" className="pulse-channel-menu-check" /> : null}
              </MenuItem>
            ))}
            {channels.length ? <Divider /> : null}
            <MenuItem onClick={() => { setChannelMenuAnchor(null); setChannelManageOpen(true); }} className="pulse-channel-menu-add">
              <ListItemIcon>
                <AddRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add channel</ListItemText>
            </MenuItem>
          </Menu>
        </div>

        <div className="pulse-sidebar-resync">
          <button
            type="button"
            className={resyncState}
            onClick={handleResyncChannel}
            disabled={!oauthConnected || activeChannelNeedsReauth || resyncState === "syncing"}
          >
            <AutorenewRoundedIcon fontSize="small" className={resyncState === "syncing" ? "spinning" : ""} />
            <span>{resyncLabel}</span>
          </button>
          {resyncState === "error" && resyncError ? <p className="pulse-sidebar-resync-error">{resyncError}</p> : null}
        </div>

        <nav className="pulse-sidebar-nav" aria-label="Pulse navigation">
          <button
            type="button"
            className={activeTab === "home" && !irisPanelOpen ? "active" : ""}
            onClick={() => navigatePulse("home")}
          >
            <HomeRoundedIcon fontSize="small" /> <span>Home</span>
          </button>
          <button
            type="button"
            className={activeTab === "insights" && !irisPanelOpen ? "active" : ""}
            onClick={() => navigatePulse("insights")}
          >
            <BarChartRoundedIcon fontSize="small" /> <span>Insights</span>
          </button>
          <button
            type="button"
            className={activeTab === "score" && !irisPanelOpen ? "active" : ""}
            onClick={() => navigatePulse("score")}
          >
            <SpeedRoundedIcon fontSize="small" /> <span>Pulse Score</span>
          </button>
          <button
            type="button"
            className={activeTab === "media-kit" && !irisPanelOpen ? "active" : ""}
            onClick={() => navigatePulse("media-kit")}
          >
            <DescriptionOutlinedIcon fontSize="small" /> <span>Media Kit</span>
          </button>
          <button
            type="button"
            className={activeTab === "saved" && !irisPanelOpen ? "active" : ""}
            onClick={() => navigatePulse("saved")}
          >
            <BookmarkBorderOutlinedIcon fontSize="small" /> <span>Bookmark</span>
          </button>
          <button
            type="button"
            className={activeTab === "settings" && !irisPanelOpen ? "active" : ""}
            onClick={() => {
              setAccountDraft(account);
              navigatePulse("settings");
            }}
          >
            <SettingsOutlinedIcon fontSize="small" /> <span>Settings</span>
          </button>
        </nav>

        <div className="pulse-sidebar-bottom">
          <div className="pulse-v2-sidebar-iris-wrap">
            <button type="button" className={`pulse-v2-sidebar-iris ${irisPanelOpen ? "active" : ""}`} onClick={() => setIrisPanelOpen(true)}>
              <IrisIcon /> Ask IRIS
            </button>
          </div>
          <div className="pulse-v2-sidebar-account">
            <button type="button" onClick={() => {
              setAccountDraft(account);
              navigatePulse("settings");
            }}>
              {profileReady ? (
                <>
                  <Avatar
                    src={profile.avatar_url || undefined}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                    sx={{ width: 28, height: 28, fontSize: 10, bgcolor: "#29bfd4" }}
                  >
                    {(() => {
                      const raw = (profile.display_name || account.creatorName || "").trim();
                      if (!raw) return "P";
                      return raw.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
                    })()}
                  </Avatar>
                  <span className="pulse-v2-sidebar-account-copy">
                    {/* Account-level Pulse identity only - never falls back to
                        accountPreview (the active channel's data), so this stays
                        fixed while the user switches channels from the dropdown
                        above. */}
                    <strong>{profile.display_name || account.creatorName || "Creator"}</strong>
                    {profile.handle ? <small>{profile.handle.startsWith("@") ? profile.handle : `@${profile.handle}`}</small> : null}
                  </span>
                </>
              ) : (
                <span className="pulse-v2-sidebar-account-skeleton" aria-hidden="true">
                  <span className="pulse-v2-sidebar-account-skeleton-avatar" />
                  <span className="pulse-v2-sidebar-account-skeleton-lines">
                    <span />
                    <span />
                  </span>
                </span>
              )}
            </button>
            <IconButton size="small" onClick={() => setMode((m) => (m === "light" ? "dark" : "light"))} aria-label="Toggle theme">
              {mode === "light" ? <NightlightRoundRoundedIcon fontSize="small" /> : <WbSunnyRoundedIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={(e) => setAccountMenuAnchor(e.currentTarget)} aria-label="Account menu">
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={() => setAccountMenuAnchor(null)}
              PaperProps={{ className: `pulse-channel-menu-paper ${mode === "dark" ? "dark" : "light"}` }}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <MenuItem
                onClick={() => {
                  setAccountMenuAnchor(null);
                  window.open("https://boltos.ai/book-a-demo/", "_blank", "noopener");
                }}
              >
                <ListItemIcon><LifeBuoyIcon /></ListItemIcon>
                <ListItemText>Help &amp; Support</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAccountMenuAnchor(null);
                  handlePulseLogout();
                }}
                className="pulse-account-menu-logout"
              >
                <ListItemIcon><LogoutRoundedIcon fontSize="small" className="pulse-account-menu-logout-icon" /></ListItemIcon>
                <ListItemText>Log out</ListItemText>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </aside>

      <section
        className="pulse-workspace-new"
        style={{ "--pulse-iris-dock-width": `${irisPanelWidth}px` } as React.CSSProperties}
      >
        <main
          ref={workspaceScrollRef}
          className={`pulse-workspace-scroll-new ${activeTab} ${irisPanelOpen ? "iris-open" : ""}`}
          onScroll={handleWorkspaceScroll}
        >
          {needsReauth && !reauthBannerDismissed ? (
            <div className="pulse-v2-reauth-banner" role="alert">
              <WarningAmberRoundedIcon fontSize="small" />
              <span>Your YouTube connection has expired and may be showing stale data.</span>
              <button
                type="button"
                className="pulse-v2-reauth-reconnect"
                onClick={() => handleConnectYoutubeOAuth(reauthChannels[0]?.channel_id || undefined)}
                disabled={oauthBusy}
              >
                <RefreshRoundedIcon fontSize="small" /> {oauthBusy ? "Opening..." : "Reconnect"}
              </button>
              <button type="button" className="pulse-v2-reauth-dismiss" onClick={() => setReauthBannerDismissed(true)} aria-label="Dismiss">
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
          ) : null}
          {oauthError ? <div className="pulse-v2-card pulse-review-error" role="alert"><p>{oauthError}</p></div> : null}
          {metricsError && <div className="pulse-v2-card pulse-review-error" role="alert"><p>{metricsError}</p><button type="button" onClick={() => void fetchPulseSnapshot()}>Retry analytics</button></div>}
          {irisPanelOpen ? (
            <section className="pulse-v2-iris-view">
              <header>
                <div className="pulse-v2-iris-title"><IrisWordmark height={40} /></div>
                <div>
                  <button type="button" onClick={handleNewChat}><RefreshRoundedIcon fontSize="small" /> New chat</button>
                  <button type="button" onClick={() => setIrisPanelOpen(false)}><CloseRoundedIcon fontSize="small" /> Close</button>
                </div>
              </header>
              <p>Your creator-intelligence co-pilot. Reads across every connected channel, never sums audiences, and always shows its confidence.</p>
              <div className="pulse-v2-iris-canvas">
                <div className="pulse-v2-iris-messages" ref={irisPanelBodyRef}>
                  {chatMessages.filter((message) => message.text !== DEFAULT_ASSISTANT_TEXT).length === 0 ? (
                    <div className="pulse-v2-iris-empty">
                      <span className="pulse-v2-iris-empty-badge"><IrisIcon fontSize="medium" /></span>
                      <h3>Ask IRIS anything about your channel</h3>
                      <p>I've read every connected channel. I never sum audiences into one number, and I always show my confidence.</p>
                    </div>
                  ) : null}
                  {chatMessages.filter((message) => message.text !== DEFAULT_ASSISTANT_TEXT).map((message, idx) => message.role === "assistant" ? (
                    <React.Fragment key={`${message.role}-${idx}`}>
                       <div className="pulse-v2-iris-label"><AutoAwesomeRoundedIcon fontSize="small" /> IRIS</div>
                       <div className="pulse-v2-iris-assistant">{renderPulseMarkdown(message.text)}</div>
                       {message.text !== DEFAULT_ASSISTANT_TEXT && (message.confidence || message.surface) ? (
                         <div className="pulse-v2-iris-meta">
                           {message.confidence ? <span>{message.confidence} confidence</span> : null}
                           {message.surface ? <span>{message.surface}</span> : null}
                           {message.evidence?.length ? <span>{message.evidence.length} evidence source{message.evidence.length === 1 ? "" : "s"}</span> : null}
                         </div>
                       ) : null}
                       {message.unknowns?.length ? <div className="pulse-v2-iris-unknowns"><strong>Still unknown</strong>{message.unknowns.map((item) => <span key={item}>{item}</span>)}</div> : null}
                      {message.chart ? <PulseIrisChart chart={message.chart} /> : null}
                      {message.text !== DEFAULT_ASSISTANT_TEXT && !message.chart ? (
                        message.insight ? (
                          <article className={`pulse-v2-card pulse-v2-typed pulse-v2-iris-insight-card ${message.insight.type}`}>
                            <div><em>{message.insight.type_label}</em><span>{message.insight.confidence} confidence</span></div>
                            <h3>{message.insight.title}</h3>
                            <p>{message.insight.body}</p>
                            <small>Evidence: {message.insight.evidence} - {message.insight.surface}</small>
                            <footer><button type="button" onClick={() => message.insight && saveTypedInsight(message.insight)}><BookmarkBorderOutlinedIcon fontSize="small" /> Save to Bookmark</button></footer>
                          </article>
                        ) : (
                          <div className="pulse-chat-save-actions pulse-v2-save-actions">
                            <button type="button" onClick={() => saveIrisMessage("auto", message.text, `save-${idx}`)} disabled={savedActionKey === `save-${idx}`}><BookmarkBorderOutlinedIcon fontSize="small" /> {savedActionKey === `save-${idx}` ? "Saving..." : "Bookmark"}</button>
                          </div>
                        )
                      ) : null}
                    </React.Fragment>
                  ) : <div key={`${message.role}-${idx}`} className="pulse-v2-iris-user">{message.text}</div>)}
                  {chatLoading ? <><div className="pulse-v2-iris-label"><AutoAwesomeRoundedIcon fontSize="small" /> IRIS</div><div className="pulse-v2-iris-assistant pulse-review-thinking" role="status" aria-live="polite"><span aria-hidden="true"><i /><i /><i /></span>{chatStatusMessage || "IRIS is working..."}</div></> : null}
                  {savedItemsError ? <p role="alert">{savedItemsError}</p> : null}
                  {chartGenerating ? <div className="pulse-chart-save-card"><div className="pulse-chart-save-orbit"><AutoAwesomeRoundedIcon fontSize="small" /></div><div><strong>Building chart</strong><span>Drawing the preview and saving it to Bookmark...</span></div></div> : null}
                  {chatError ? <div className="pulse-chat-error">{chatError}</div> : null}
                </div>
                <div className="pulse-v2-iris-input-zone">
                  {irisScopedSurface ? (
                    <div className="pulse-v2-iris-scope-chip">
                      <span>{irisScopedSurface.label}{irisScopedSurface.score !== null ? ` - ${irisScopedSurface.score.toFixed(1)}/10` : ""}</span>
                      <button type="button" onClick={() => setIrisScopedSurface(null)} aria-label={`Clear ${irisScopedSurface.label} context`}>&times;</button>
                    </div>
                  ) : null}
                  {chatMessages.filter((message) => message.text !== DEFAULT_ASSISTANT_TEXT).length === 0 ? (
                    <div className="pulse-v2-iris-suggestion-row">
                      {(irisScopedSurface
                        ? [
                            `What is driving ${irisScopedSurface.shortLabel} right now?`,
                            `How do I improve ${irisScopedSurface.shortLabel}?`,
                            `Why is my ${irisScopedSurface.shortLabel} where it is?`,
                          ]
                        : SUGGESTIONS
                      ).map((suggestion) => (
                        <button key={suggestion} type="button" onClick={() => setChatDraft(suggestion)}>{suggestion}</button>
                      ))}
                    </div>
                  ) : null}
                  <input ref={documentFileInputRef} type="file" accept=".csv,.pdf,text/csv,application/pdf" className="pulse-doc-file-input" onChange={(event) => void handleDocumentUpload(event.target.files?.[0])} />
                  {uploadedDocument ? <div className="pulse-doc-attachment"><AttachFileRoundedIcon fontSize="small" /><div><strong>{uploadedDocument.name}</strong><span>{uploadedDocument.type === "pdf" ? `${uploadedDocument.page_count || 0} pages` : `${uploadedDocument.row_count || 0} rows`}</span></div><IconButton size="small" onClick={() => setUploadedDocument(null)}><CloseRoundedIcon fontSize="small" /></IconButton></div> : null}
                  {documentUploadError ? <div className="pulse-doc-error">{documentUploadError}</div> : null}
                  <div className="pulse-v2-iris-composer">
                    <button type="button" onClick={() => documentFileInputRef.current?.click()} disabled={documentUploading} aria-label="Attach a CSV or PDF"><AttachFileRoundedIcon fontSize="small" /></button>
                    <textarea ref={panelComposerRef} rows={1} placeholder={irisScopedSurface ? `Ask about ${irisScopedSurface.shortLabel}...` : "Ask IRIS anything about your content..."} value={chatDraft} onChange={(event) => setChatDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); handleSend(); } }} />
                    <button type="button" className="send" onClick={handleSend} disabled={!canSendChat}><ArrowUpwardRoundedIcon fontSize="small" /></button>
                  </div>
                </div>
              </div>
            </section>
          ) : hasCreatorAccount && !metricsReady && !["settings", "saved", "media-kit"].includes(activeTab) ? (
            <div className="pulse-new-content-wrap">
              <MetricsSkeleton tab={activeTab} />
            </div>
          ) : activeTab === "home" ? (
            <PulseHomeDashboard
              metrics={insightsMetrics}
              channelDataState={channelDataState}
              scoreStatus={widgetStatus.pulse_score}
              plan={scorePlan}
              planError={scorePlanError}
              recommendation={irisRecommendation}
              account={{ ...account, creatorName: profile.display_name || preferences.display_name || account.creatorName }}
              preview={accountPreview}
              chatDraft={chatDraft}
              chatLoading={chatLoading}
              onDraftChange={setChatDraft}
              onAskIris={() => {
                if (!chatDraft.trim()) return;
                setIrisPanelOpen(true);
                handleSend();
              }}
              onSuggestion={setChatDraft}
              onOpenInsights={() => navigatePulse("insights")}
              onOpenScore={() => navigatePulse("score")}
              onCommitMove={commitPrescribedMove}
              onAskAboutSurface={askIrisAboutSurface}
            />
          ) : activeTab === "insights" ? (
            <PulseInsightsDashboard
              range={reportingRange}
              onRangeChange={setReportingRange}
              preferences={preferences}
              onPreferencesSave={savePreferences}
              pinnedItems={savedItems.filter((item) => item.pinned)}
              metrics={insightsMetrics}
              status={widgetStatus}
              plan={scorePlan}
              planError={scorePlanError}
              typedInsights={typedInsights}
              typedInsightsLoading={typedInsightsLoading}
              account={{ ...account, creatorName: profile.display_name || preferences.display_name || account.creatorName }}
              preview={accountPreview}
              onCommitMove={commitPrescribedMove}
              onCompleteMove={completeManualMove}
              onAskInsight={(prompt) => { setChatDraft(prompt); setIrisPanelOpen(true); }}
              onSaveInsight={saveTypedInsight}
            />
          ) : activeTab === "score" ? (
            <PulseScoreDashboard
              channelDataState={channelDataState}
              scoreLoading={widgetLoading.pulse_score?.loading}
              metrics={insightsMetrics}
              scoreStatus={widgetStatus.pulse_score}
              oauthConnected={oauthConnected}
              preview={accountPreview}
              preferences={preferences}
              onAskAboutSurface={askIrisAboutSurface}
            />
          ) : activeTab === "media-kit" ? (
            <PulseMediaKitDashboard metrics={mediaMetrics} account={account} accountEmail={currentUser?.email} preview={accountPreview} recommendation={irisRecommendation} onShare={handleMediaShare} onExport={handleMediaExportPdf} actionMessage={mediaActionMessage} preferences={preferences} onPreferencesSave={savePreferences} onChannelSync={syncMediaKitChannel} pulseHandle={profile.handle} />
          ) : activeTab === "saved" ? (
            <PulseBookmarkDashboard activeTab={savedLibraryTab} items={savedItems} memory={creatorMemory} memoryLoading={creatorMemoryLoading} loading={savedItemsLoading} error={savedItemsError} onTabChange={setSavedLibraryTab} onAskSuggestion={(prompt) => { setChatDraft(prompt); setIrisPanelOpen(true); }} onAskAboutItem={askIrisAboutSavedItem} onTogglePinned={toggleSavedPinned} onDelete={deleteSavedItem} onClearMemory={clearCreatorMemory} onClearSaved={clearSavedItems} />
          ) : (
            <PulseSettingsDashboard
              preferences={accountPreferences}
              onPreferencesSave={saveAccountPreferences}
              profile={profile}
              profileSaving={profileSaving}
              profileError={profileError}
              onProfileSave={saveProfile}
              onCheckHandleAvailable={checkHandleAvailable}
              onSaveHandle={saveHandle}
              avatarUploading={avatarUploading}
              avatarError={avatarError}
              onUploadAvatar={uploadProfileAvatar}
              instagramAvailable={instagramAvailable}
              accountEmail={currentUser?.email || null}
              accountAuthProvider={currentUser?.auth_provider || null}
              onLogout={handlePulseLogout}
              passwordChangeBusy={passwordChangeBusy}
              passwordChangeMessage={passwordChangeMessage}
              passwordChangeError={passwordChangeError}
              onChangePassword={handleChangePassword}
              deleteAccountBusy={deleteAccountBusy}
              deleteAccountError={deleteAccountError}
              onRequestAccountDeletion={handleRequestAccountDeletion}
              twoFactorEnabled={twoFactorEnabled}
              twoFactorBusy={twoFactorBusy}
              onEnable2fa={handleEnable2fa}
              onVerify2fa={handleVerify2fa}
              onDisable2fa={handleDisable2fa}
              account={{ ...account, creatorName: profile.display_name || preferences.display_name || account.creatorName }}
              draft={accountDraft}
              preview={accountPreview}
              saving={accountSaving}
              error={accountError}
              youtubeConnected={oauthConnected}
              youtubeBusy={oauthBusy}
              youtubeError={oauthError}
              instagramConnection={instagramConnection}
              instagramBusy={instagramOauthBusy}
              instagramError={instagramOauthError}
              onDraftChange={setAccountDraft}
              onSave={handleAccountSave}
              onConnectYoutube={handleOnboardingConnectYoutube}
              onDisconnectYoutube={handleDisconnectYoutubeOAuth}
              onConnectInstagram={handleConnectInstagramOAuth}
              onDisconnectInstagram={handleDisconnectInstagramOAuth}
              messagePrefs={messagePrefs}
              onToggleMessagePref={handleToggleMessagePref}
              privacyPrefs={privacyPrefs}
              onTogglePrivacyPref={handleTogglePrivacyPref}
              textScale={accountPreferences.text_scale || 1}
              onTextSizeStep={handleTextSizeStep}
              irisMemoryEnabled={irisMemoryEnabled}
              onToggleIrisMemory={handleToggleIrisMemory}
              onClearIrisMemory={clearCreatorMemory}
              goalOptions={PULSE_GOALS}
              selectedGoals={selectedGoals}
              onSaveGoals={(goals) => handleSaveGoalsFromSettings(goals as PulseGoalId[])}
            />
          )}
        </main>
      </section>
      {accountDialog}
      {channelManageDialog}
    </Box>
  );
};

export default PulsePage;
